export type UiLanguage = "en" | "ga";

const english: Record<string, string> = {
  "language.english": "English",
  "language.gaeilge": "Gaeilge",
  "public.aiTools": "AI-powered tools for modern teachers",
  "public.platform": "The All-in-One Teaching Platform",
  "public.makeTeaching": "Make teaching",
  "public.makeTeachingAccent": "smarter, faster, brighter.",
  "public.description": "Elume is an all-in-one teaching platform for secondary school teachers, helping you create quizzes, organise class resources, build exam materials, run live classroom tools and use AI when it helps — all from one teacher-friendly workspace.",
  "public.saveTime": "Save Time", "public.saveTimeDescription": "Create quizzes, class resources and teaching materials in minutes.",
  "public.engageClasses": "Engage Classes", "public.engageClassesDescription": "Run live classroom tools and quizzes that work beautifully on screen.",
  "public.stayOrganised": "Stay Organised", "public.stayOrganisedDescription": "Organise notes, tests, class files and teaching spaces in one place.",
  "public.builtForSchools": "Built for Schools", "public.builtForSchoolsDescription": "Professional, secure and designed for real secondary school classrooms.",
  "public.trustedPilots": "Trusted by pilot teachers", "public.teacherFirst": "Teacher-first design", "public.builtInIreland": "Built in Ireland", "public.seeInAction": "See Elume in action",
  "public.mobileDescription": "Smarter tools for lesson planning, quizzes, reports, whiteboards and class management.",
  "home.welcome": "Welcome to Elume",
  "home.platform": "The All-in-One Teaching Platform",
  "home.languageFeature": "Available in Irish too!",
  "home.languageAction": "Try Elume in Irish",
  "home.languageSelected": "Irish is selected",
  "home.eyebrow": "Built by a teacher, for teachers",
  "home.where": "Where would you like to go?",
  "home.choices": "Choose an Elume entrance",
  "home.studentEyebrow": "Student Hub",
  "home.studentTitle": "Student Hub",
  "home.studentDetail": "Your class. Your resources.",
  "home.studentReassurance": "No student account needed.",
  "home.studentAction": "I'm a student",
  "home.teacherEyebrow": "Teacher",
  "home.teacherTitle": "Teacher",
  "home.teacherDetail": "Your classes and teaching tools.",
  "home.teacherAction": "Teacher login",
  "home.demoEyebrow": "Try the demo",
  "home.demoTitle": "Try the demo",
  "home.demoDetail": "Take a look inside Elume.",
  "home.demoReassurance": "No signup needed.",
  "home.demoAction": "Try the demo",
  "home.benefit": "Plan lessons, teach interactively and follow progress in one teacher-friendly workspace.",
  "home.trial": "Start your free 14-day trial",
  "home.saveTime": "Save Time",
  "home.saveTimeCopy": "Create teaching resources in minutes.",
  "home.engage": "Engage Classes",
  "home.engageCopy": "Bring lessons to life with interactive tools.",
  "home.organised": "Stay Organised",
  "home.organisedCopy": "Keep your classes and resources together.",
  "home.schools": "Built for Schools",
  "home.schoolsCopy": "Designed for secondary school classrooms.",
  "home.trialCopy": "Explore Elume’s teaching tools with your own classes.",
  "home.trialAction": "Start your free 14-day trial",
  "home.footer": "Teacher-first design · Built in Ireland",
  "login.welcomeBack": "Welcome back", "login.signIn": "Sign in", "login.email": "Email", "login.password": "Password", "login.forgotPassword": "Forgot password?", "login.resetPassword": "Reset password", "login.resetGuidance": "Enter your email and we’ll send a reset link if your account is registered.", "login.sendReset": "Send reset link", "login.createAccount": "Create an account", "login.defaultGuidance": "Sign in to access your classes and tools.", "login.schoolGuidance": "Sign in to your school on Elume.", "login.teacherLogin": "Teacher login", "login.schoolLoading": "Loading your school sign-in…", "login.schoolReady": "Sign in to {{school}} on Elume.", "login.schoolUnavailable": "School access is currently unavailable. Please contact your school or return to Elume login.", "login.goToElume": "Go to Elume login", "login.schoolUnknown": "We couldn’t find this Elume school.", "login.schoolBrandingError": "We couldn’t load school branding right now. You can still sign in to Elume.", "login.passwordPlaceholder": "Enter your password", "login.signingIn": "Signing in…", "login.secureBilling": "Secure billing", "login.paymentsPoweredBy": "Payments powered by", "login.secureAccess": "Secure access", "login.teacherFirst": "Teacher-first", "login.schoolReadyBadge": "School-ready", "login.studentHub": "Student hub", "login.studentsStart": "Students start here", "login.studentHubHelp": "Joining a class? Open Student Hub to enter your class code and PIN.", "login.openStudentHub": "Open Student Hub", "login.recovery": "Account recovery", "login.recoveryHelp": "Enter your email and we’ll send you a secure password reset link.", "login.resetSent": "If that email is registered, a password reset link has been sent.", "login.resetFailure": "Could not send reset link", "login.footer": "Designed to help teachers save time, stay organised and make lessons shine.",
  "register.title": "Create teacher account", "register.welcome": "Welcome to Elume", "register.rolePrompt": "Are you a teacher or a student?", "register.teacherChoice": "I am a teacher", "register.teacherChoiceHelp": "Continue to teacher registration.", "register.studentChoice": "I am a student", "register.studentChoiceHelp": "Open Student Hub to join your class.", "register.changeChoice": "Change role", "register.firstName": "First name", "register.lastName": "Last name", "register.schoolName": "School name", "register.confirmPassword": "Confirm password", "register.passwordHelp": "Use at least 8 characters, including an uppercase letter, a lowercase letter, and a number.", "register.submit": "Create account", "register.loading": "Creating account…", "register.completeFields": "Please complete all fields.", "register.validEmail": "Please enter a valid email address.", "register.passwordMismatch": "Passwords do not match.", "register.success": "Account created. Please verify your email before signing in.", "register.failure": "Could not create account.", "register.passwordMinimum": "Password must be at least 8 characters.", "register.passwordUppercase": "Password must include at least one uppercase letter.", "register.passwordLowercase": "Password must include at least one lowercase letter.", "register.passwordNumber": "Password must include at least one number.", "register.freeTrialHero": "Start with a 14-day free trial", "register.heroTitle": "Build your teacher workspace", "register.heroAccent": "and get started fast.", "register.heroDescription": "Create your Elume account, verify your email, then sign in to choose your plan. No plan is selected on this page. Your 14-day free trial begins once you complete billing setup after verification.", "register.trialTitle": "14-Day Trial", "register.trialDescription": "Register first, verify your email, then choose your plan and begin your free trial.", "register.earlyPricingTitle": "Early Adopter Pricing", "register.earlyPricingDescription": "Join now at €6 monthly or €60 yearly during our early launch phase.", "register.annualValueTitle": "Best Annual Value", "register.annualValueDescription": "Annual accounts purchased now stay active until the end of September 2027.", "register.builtForSchoolsDescription": "Teacher-first, practical and designed for real secondary school classrooms.", "register.earlyRates": "Early adopter rates", "register.mobileTrialDescription": "Create your account now, then verify your email and choose your plan to start your 14-day free trial.", "register.formDescription": "Register now, verify your email, then sign in to choose your plan.", "register.teacherSignup": "Teacher sign up", "register.trialBadge": "14-day free trial", "register.pricingBadge": "Early adopter pricing", "register.afterVerify": "After you verify your email and sign in, you’ll choose your Elume plan and begin your free trial.", "register.pricingLead": "Early adopter prices are", "register.monthlyPrice": "€6 monthly", "register.or": "or", "register.yearlyPrice": "€60 yearly", "register.launchOffer": "Special launch offer:", "register.annualActiveLead": "annual accounts purchased now will remain active until the", "register.annualActiveDate": "end of September 2027", "register.stepRegister": "Register first", "register.stepVerify": "Verify email", "register.stepPlan": "Choose plan", "register.firstNamePlaceholder": "First name", "register.lastNamePlaceholder": "Last name", "register.schoolPlaceholder": "Your school", "register.passwordPlaceholder": "Create password", "register.confirmPasswordPlaceholder": "Confirm password", "register.whatNext": "What happens next", "register.nextHelp": "Verify your email, sign in, choose your plan, and start using Elume.", "register.secureSignup": "Secure signup", "register.alreadyAccount": "Already have an account?", "register.footer": "Built to help teachers save time, stay organised and make lessons shine.",
  "verifyEmail.title": "Verify your email", "verifyEmail.help": "We’re activating your Elume teacher account.", "verifyEmail.back": "Back to login", "verifyEmail.verifying": "Verifying your email…", "verifyEmail.missingToken": "Missing verification token.", "verifyEmail.setup": "Email verified. Taking you into Elume setup…", "verifyEmail.success": "Email verified. You can now sign in to Elume.", "verifyEmail.failure": "Could not verify email.",
  "resetPassword.recovery": "Account recovery", "resetPassword.title": "Reset your password", "resetPassword.help": "Choose a new password for your Elume account. This page does not require login.", "resetPassword.new": "New password", "resetPassword.confirm": "Confirm password", "resetPassword.invalid": "This reset link is invalid or incomplete.", "resetPassword.success": "Password reset successful.", "resetPassword.failure": "Could not reset password.", "resetPassword.submit": "Set new password", "resetPassword.loading": "Updating password…", "resetPassword.back": "Back to login",
  "schoolInvite.label": "School invitation", "schoolInvite.title": "Join Elume", "schoolInvite.checking": "Checking invitation…", "schoolInvite.invalid": "This invitation link is invalid or incomplete.", "schoolInvite.unavailable": "This invitation is no longer available.", "schoolInvite.existingPassword": "Existing Elume password", "schoolInvite.firstName": "First name", "schoolInvite.lastName": "Last name", "schoolInvite.confirmPassword": "Confirm password", "schoolInvite.accept": "Accept invitation", "schoolInvite.accepting": "Accepting invitation…", "schoolInvite.back": "Back to login", "schoolInvite.failure": "Could not accept this invitation.", "schoolInvite.defaultInviter": "Your school administrator", "schoolInvite.invitedTeacher": "{{inviter}} has invited you to join {{school}} on Elume.", "schoolInvite.invitedAdmin": "{{inviter}} has invited you to manage {{school}} on Elume.", "schoolInvite.sent": "Invitation sent through Elume", "schoolInvite.existingAccountHelp": "An Elume account already exists for this email. Enter its password to confirm that you own the account before {{action}}.", "schoolInvite.joinSchool": "joining this school", "schoolInvite.joinSchoolAdmin": "joining this school as a School Admin", "schoolInvite.existingPasswordRequired": "Enter your existing Elume password to confirm account ownership.", "schoolInvite.setupTeacher": "Set up your teacher account for {{school}}.", "schoolInvite.setupAdmin": "Set up your School Admin account for {{school}}.", "schoolInvite.password": "Password", "schoolInvite.successTeacher": "You’ve joined {{school}}. Sign in to continue.", "schoolInvite.successAdmin": "You’ve joined {{school}} as a School Admin. Sign in to continue.", "schoolInvite.acceptAdmin": "Join as School Admin",
  "common.back": "Back",
  "common.close": "Close",
  "common.cancel": "Cancel",
  "common.remove": "Remove",
  "common.save": "Save",
  "common.saving": "Saving…",
  "common.saveChanges": "Save changes",
  "common.edit": "Edit",
  "common.delete": "Delete",
  "common.loading": "Loading…",
  "common.open": "Open",
  "studentFiles.view": "View",
  "studentFiles.opening": "Opening…",
  "studentFiles.viewNewTab": "Opens in a new tab. Viewing avoids saving to Downloads, but uses network traffic and browser cache.",
  "studentFiles.viewAria": "View {{filename}} in a new tab",
  "studentFiles.download": "Download",
  "studentFiles.downloadAria": "Download {{filename}}",
  "studentFiles.attachment": "Shared attachment",
  "studentFiles.openAttachment": "Open attachment",
  "studentFiles.viewingNote": "Viewing avoids saving files to Downloads, but still uses network traffic and browser cache. View opens a new tab.",
  "studentFiles.previewUnavailable": "Preview is unavailable for this file type. Use Download to save a copy.",
  "studentFiles.openFailed": "This file could not be opened. Check your connection or access and try again.",
  "common.refresh": "Refresh",
  "common.upload": "Upload",
  "demo.restriction.changeHelp": "In your own Elume account, you can make and save changes.",
  "demo.participantLimit.title": "You\u2019re trying an Elume demo",
  "demo.participantLimit.inUse": "This demo allows one participant at a time, and that place is currently in use.",
  "demo.participantLimit.collaborationDetail": "With a full teacher account, you can create up to 12 breakout rooms for students to work individually or in teams, review their boards and save their work to your class Notes.",
  "demo.participantLimit.liveQuizDetail": "With a full teacher account, you can run live quizzes with your whole class, check understanding as answers arrive and review their results.",
  "demo.participantLimit.backToHub": "Back to Student Hub",
  "demo.participantLimit.startTrial": "Start a free trial",
  "demo.participantInfo.heading": "You\u2019re exploring an Elume demo.",
  "demo.participantInfo.collaboration": "This session allows one participant. With a full teacher account, your class can collaborate across up to 12 breakout rooms.",
  "demo.participantInfo.liveQuiz": "This session allows one participant. With a full teacher account, you can run live quizzes with your whole class.",
  "demo.participantInfo.teacherAction": "Teacher? Start your free trial",
  "demo.participantInfo.studentAction": "Student? Visit the Student Hub",
  "error.sessionChanged": "Your session has expired or changed. Please log in again to continue. This can happen for security reasons or if you signed in on another device.",
  "error.chooseCategory": "Choose a category first",
  "error.enterCategoryName": "Enter a category name",
  "error.requestFallback": "We couldn’t complete that request. Please try again.",
  "nav.dashboard": "Dashboard",
  "nav.admin": "Admin",
  "nav.calendar": "Calendar",
  "teacherAdmin.billing.label": "Subscription & billing",
  "teacherAdmin.billing.individual": "Manage your plan, payments or cancel your subscription.",
  "teacherAdmin.billing.schoolManaged": "Your Elume access is managed by your school. Personal billing is not required while your school membership is active.",
  "teacherAdmin.billing.schoolAndPersonal": "Your Elume access is managed by your school. You can still manage your personal subscription separately.",
  "teacherAdmin.billing.manage": "Manage subscription & billing",
  "teacherAdmin.billing.managePersonal": "Manage personal billing",
  "teacherAdmin.billing.viewDetails": "View billing details",
  "nav.schoolAdmin": "School Admin",
  "nav.platformAdmin": "Platform Admin",
  "nav.schoolResources": "School Resources",
  "nav.logout": "Logout",
  "dashboard.createClass": "Create Class",
  "dashboard.createResources": "Create Resources",
  "dashboard.openClass": "Open class",
  "dashboard.creatingClass": "Creating…",
  "dashboard.dragToArrange": "Drag tiles to arrange.",
  "dashboard.viewTimetable": "View Timetable",
  "dashboard.timetable": "Timetable",
  "dashboard.planner": "Elume Planner",
  "dashboard.tasksWeekView": "Tasks • Week view",
  "dashboard.welcome": "Welcome",
  "dashboard.loadingClasses": "Loading classes…",
  "dashboard.noClasses": "No classes yet — click Create Class to start.",
  "dashboard.archivedClasses": "Archived Classes",
  "dashboard.manageClass": "Manage class",
  "dashboard.editClass": "Edit class",
  "dashboard.archiveClass": "Archive class",
  "dashboard.install": "Install Elume",
  "dashboard.maybeLater": "Maybe later",
  "planner.welcome": "Welcome",
  "planner.tasksWeekView": "Tasks • Week view",
  "createClass.title": "Create Class",
  "createClass.help": "Choose stream/year/level and a tile colour.",
  "createClass.stream": "Stream",
  "createClass.year": "Year",
  "createClass.level": "Level",
  "createClass.subject": "Subject",
  "createClass.className": "Class name",
  "createClass.juniorCycle": "Junior Cycle",
  "createClass.seniorCycle": "Senior Cycle",
  "createClass.transitionYear": "Transition Year (TY)",
  "createClass.sen": "SEN",
  "createClass.tileColour": "Tile colour",
  "createClass.customClass": "Custom Class",
  "createClass.standardClass": "Use standard class details",
  "createClass.customLineOne": "First line",
  "createClass.customLineTwo": "Second line",
  "createClass.customLineOnePlaceholder": "e.g. Animal Care",
  "createClass.customLineTwoPlaceholder": "e.g. Level 5",
  "class.backToDashboard": "Back to Dashboard",
  "class.teacher": "Teacher:",
  "class.group": "Group:",
  "class.room": "Room:",
  "class.classSettings": "Class settings",
  "class.liveTeachingTools": "Live Teaching Tools",
  "class.liveTeachingHelp": "Jump into your main lesson modes quickly.",
  "class.whiteboard": "Whiteboard",
  "class.whiteboardSubtitle": "Open full teaching board",
  "class.collaboration": "Collaboration",
  "class.collaborationSubtitle": "Live student breakout board",
  "class.liveQuiz": "Live Quiz",
  "class.liveQuizSubtitle": "Run saved quiz or poll",
  "class.collabBoard": "Collab Board",
  "class.whiteboardTeachAnnotate": "Teach and annotate",
  "class.collabDrawTogether": "Draw together",
  "class.liveQuizPoll": "Run a quiz or poll",
  "collab.startGuidance": "Create a session, then invite your students.",
  "collab.prepareBoard": "Prepare a board",
  "collab.exitPreparation": "Exit preparation",
  "collab.savePreparedBoard": "Save prepared board",
  "collab.saveBoardChanges": "Save changes",
  "collab.editBoardCopy": "Edit a copy",
  "collab.preparationUnsaved": "Leave board preparation? Unsaved changes will be lost.",
  "collab.preparationLabel": "Temporary preparation",
  "collab.preparedBoardHeading": "Save this prepared board",
  "collab.preparedBoardHelp": "Keep this board in My Saved Boards so you can reopen it or use it in a future collaboration session.",
  "collab.snipSelectingArea": "Selecting area…",
  "collab.takeAnotherSnip": "Take another snip",
  "collab.clickHereToStart": "Click Here to Start",
  "collab.creatingSession": "Creating session…",
  "collab.decreaseRoomCount": "Decrease room count",
  "collab.increaseRoomCount": "Increase room count",
  "collab.roomCountRange": "Choose a whole number from 1 to 12.",
  "collab.roomCountInvalid": "Enter a whole number from 1 to 12 before starting breakout rooms.",
  "collab.leaveSession": "Leave session",
  "collab.leaving": "Leaving…",
  "collab.leaveFailed": "We couldn’t leave the session. Please try again.",
  "collab.leaveUnavailable": "This session can no longer be left from this device. Rejoin with the session code if needed.",
  "teacherInvite.studentJoin": "Student join",
  "teacherInvite.collabTitle": "Collab Board",
  "teacherInvite.quizTitle": "Live Quiz",
  "teacherInvite.boardCode": "Board code",
  "teacherInvite.quizCode": "Quiz code",
  "teacherInvite.joinLink": "Join link",
  "teacherInvite.copyQuizCode": "Copy quiz code",
  "teacherInvite.collabInstruction": "Scan to join, or open Student Hub, choose Collab Board and enter this board code.",
  "teacherInvite.quizInstruction": "Scan to join, or open Student Hub, choose Live Quiz and enter this quiz code.",
  "class.classAdmin": "Class Admin",
  "class.resources": "Resources",
  "class.notes": "Notes",
  "class.tests": "Tests",
  "class.quizzes": "Quizzes",
  "class.examPapers": "Exam Papers",
  "class.videos": "Videos",
  "whiteboard.savedVideos": "Saved class videos",
  "whiteboard.handMove": "Hand / Move",
  "whiteboard.penShortcut": "Pen",
  "whiteboard.savedVideosHelp": "Videos saved in this browser for the current class.",
  "whiteboard.videoPickerHelp": "Choose a video saved for this class or paste a YouTube link.",
  "whiteboard.noSavedVideos": "No videos have been saved for this class in this browser yet.",
  "whiteboard.noClassVideoContext": "Saved class videos are available when the Whiteboard is opened from a class. You can still paste a YouTube link below.",
  "whiteboard.checkingClassAccess": "Checking access to this class before showing saved videos…",
  "whiteboard.classVideoAccessRequired": "Saved class videos cannot be shown because this account does not have access to this class. You can still paste a YouTube link below.",
  "whiteboard.savedVideosLoadError": "We couldn’t load saved class videos from this browser. Try again.",
  "whiteboard.unsupportedSavedVideo": "This saved link is not a supported YouTube video. Update it in Videos before opening it here.",
  "whiteboard.selectVideo": "Select",
  "whiteboard.audioTooLarge": "Audio files can be up to 100 MB. Choose a smaller file and try again.",
  "whiteboard.audioUploadLimit": "This audio upload was rejected before Elume could process it. Audio files can be up to 100 MB; please try again with a smaller file.",
  "whiteboard.audioStorageLimit": "This audio file could not be uploaded because your Elume storage limit has been reached. Delete some files before trying again.",
  "whiteboard.audioUploadNetwork": "The audio upload did not complete. Check your connection and try again; larger files can take longer to upload.",
  "whiteboard.audioFileLimit": "Audio files can be up to 100 MB. Your account allowance is separate.",
  "whiteboard.audioStorageSummary": "Storage: {used} used of {limit}; {available} available.",
  "whiteboard.uploadingAudio": "Uploading audio…",
  "class.links": "Links",
  "class.quickTools": "Quick Classroom Tools",
  "class.quickToolsSubtitle": "Fast in-lesson helpers",
  "class.randomName": "Random Name",
  "class.seatingPlan": "Seating Plan",
  "class.timer": "Timer",
  "class.teamGenerator": "Team Generator",
  "class.externalResources": "External Resources",
  "class.studentAccess": "Student access",
  "class.studentAccessHelp": "Scan to open read-only view.",
  "class.copyLink": "Copy link",
  "class.classCode": "Class code",
  "class.newAnnouncement": "New Announcement",
  "class.calendarAlerts": "Calendar alerts",
  "notes.title": "Notes",
  "notes.description": "Organise PDFs, slides, worksheets, audio and classroom files.",
  "notes.allCategories": "All Categories",
  "notes.audioLibrary": "Audio Library",
  "notes.upload": "Upload",
  "notes.search": "Search categories or files...",
  "notes.categories": "Categories",
  "notes.categoryHelp": "Dashboard-style topic tiles for fast classroom access",
  "notes.addCategoryUpload": "+ Add Category / Upload",
  "notes.loadingWorkspace": "Loading notes workspace...",
  "notes.noCategories": "No categories yet",
  "notes.startLibrary": "Start Notes Library",
  "notes.category": "Category",
  "notes.addFiles": "Add files",
  "notes.deleteCategory": "Delete category",
  "notes.loadingFiles": "Loading files...",
  "notes.noFiles": "No files in this category yet",
  "notes.uploadAudio": "Upload audio",
  "notes.uploadFiles": "Upload files",
  "notes.uploaded": "Uploaded:",
  "notes.deleteFile": "Delete file",
  "notes.reopenWhiteboard": "Reopen in Whiteboard",
  "notes.uploadNotes": "Upload notes",
  "notes.uploadHelp": "Choose an existing category or create a new one before uploading.",
  "notes.existingCategory": "Existing category",
  "notes.createNewCategory": "Create new category",
  "notes.newCategoryName": "New category name",
  "notes.chooseCategory": "Choose a category...",
  "notes.audioFiles": "Audio files",
  "notes.files": "Files",
  "notes.chooseAudioFiles": "Choose audio files",
  "notes.chooseFiles": "Choose files",
  "notes.uploading": "Uploading...",
  "notes.resourcePreparation": "Resource preparation",
  "notes.prepareResource": "Make this resource ready for Elume?",
  "notes.uploadOriginal": "Upload Original",
  "notes.convertAndUpload": "Convert & Upload",
  "calendar.title": "Calendar",
  "calendar.classTitle": "Class Calendar",
  "calendar.newEvent": "+ New Event",
  "calendar.filterEvents": "Filter events",
  "calendar.allEvents": "All events",
  "calendar.globalOnly": "Global only",
  "calendar.selectedClass": "Selected class",
  "calendar.monthView": "Month View",
  "calendar.previous": "Previous",
  "calendar.today": "Today",
  "calendar.next": "Next",
  "calendar.loading": "Loading...",
  "calendar.eventTitle": "Event title",
  "calendar.description": "Description",
  "calendar.date": "Date",
  "calendar.time": "Time",
  "calendar.endTime": "End time",
  "calendar.allDay": "All day",
  "calendar.eventType": "Event type",
  "calendar.createEvent": "Create event",
  "calendar.editEvent": "Edit event",
  "calendar.saveEvent": "Save event",
  "calendar.deleteEvent": "Delete event",
  "liveQuiz.title": "Live Quiz",
  "liveQuiz.backToClass": "Back to class",
  "liveQuiz.questions": "Questions",
  "liveQuiz.session": "Session",
  "liveQuiz.joined": "Joined",
  "liveQuiz.state": "State",
  "liveQuiz.setup": "Quiz setup",
  "liveQuiz.chooseSavedQuiz": "Choose a saved quiz",
  "liveQuiz.classQuizzes": "Class Quizzes",
  "liveQuiz.mainCollection": "Main Quiz Collection",
  "liveQuiz.quickCustom": "Quick Custom",
  "liveQuiz.sessionTitle": "Session title",
  "liveQuiz.sessionOptions": "Session options",
  "liveQuiz.autoPlay": "Auto-play",
  "liveQuiz.shuffleQuestions": "Shuffle questions",
  "liveQuiz.useTimer": "Use timer",
  "liveQuiz.autoEnd": "Auto-end when all answered",
  "liveQuiz.secondsPerQuestion": "Seconds per question",
  "liveQuiz.questionsLoaded": "Questions loaded",
  "liveQuiz.createSession": "Create live session",
  "liveQuiz.creatingSession": "Creating session...",
  "liveQuiz.customQuestions": "Custom questions",
  "liveQuiz.addQuestion": "+ Add question",
  "liveQuiz.question": "Question",
  "liveQuiz.remove": "Remove",
  "liveQuiz.prompt": "Prompt",
  "liveQuiz.correctAnswerOptional": "Optional: mark a correct answer. Leave blank for poll mode.",
  "liveQuiz.start": "Start",
  "liveQuiz.nextQuestion": "Next",
  "liveQuiz.endSession": "End session",
  "liveQuiz.refreshResults": "Refresh results",
  "liveQuiz.yourQuizzes": "Your Quizzes",
  "liveQuiz.elumeQuizLibrary": "Elume Quiz Library",
  "liveQuiz.elumeLibraryHelp": "Browse curated Elume quizzes by level, subject and topic. Starting one keeps the original quiz unchanged.",
  "liveQuiz.allLevels": "All levels",
  "liveQuiz.level": "Level",
  "liveQuiz.subject": "Subject",
  "liveQuiz.subjects": "subjects",
  "liveQuiz.topic": "Topic",
  "liveQuiz.topics": "topics",
  "liveQuiz.quizzes": "quizzes",
  "liveQuiz.loadingLibrary": "Loading Elume Quiz Library...",
  "liveQuiz.preview": "Preview",
  "liveQuiz.startLiveQuiz": "Start Live Quiz",
  "liveQuiz.correctAnswer": "Correct answer",
  "liveQuiz.explanation": "Explanation",
  "liveQuiz.close": "Close",
  "quizzes.title": "Quizzes",
  "quizzes.aiBuilder": "AI Quiz Builder",
  "quizzes.generateFromPdf": "Generate from PDF",
  "quizzes.newManual": "+ New Manual Quiz",
  "quizzes.openLiveQuiz": "Open Live Quiz",
  "quizzes.questions": "Questions",
  "quizzes.empty": "No quizzes yet",
  "quizzes.createManually": "Create Manually",
  "quizzes.library": "Quiz library",
  "quizzes.preview": "Preview",
  "quizzes.editQuiz": "Edit quiz",
  "quizzes.newQuiz": "New quiz",
  "quizzes.quizTitle": "Quiz title",
  "quizzes.category": "Category",
  "quizzes.descriptionOptional": "Short description (optional)",
  "quizzes.editQuestion": "Edit question",
  "quizzes.addQuestion": "Add question",
  "quizzes.questionText": "Question text",
  "quizzes.correctAnswer": "Correct answer",
  "quizzes.backToClass": "Back to Class",
  "quizzes.heroHelp": "Build a quiz in minutes from a class PDF, tidy it up if needed, then deliver it through Live Quiz with clear teacher control.",
  "quizzes.step": "Step",
  "quizzes.pickPdf": "Pick a class PDF",
  "quizzes.pickPdfHelp": "Use a PDF that already exists in Notes or Exam Papers.",
  "quizzes.generateWithAi": "Generate with AI",
  "quizzes.generateHelp": "Choose how many questions you want, then let Elume create a quiz automatically.",
  "quizzes.deliverLive": "Deliver it live",
  "quizzes.deliverHelp": "Once a quiz is ready, run it from the class Live Quiz page.",
  "quizzes.thisClass": "This class",
  "quizzes.fastestStart": "Fastest way to get started",
  "quizzes.fastestStartHelp": "Upload or reuse a class PDF, generate a quiz here, then launch the session from Live Quiz.",
  "quizzes.teacherNote": "Teacher note",
  "quizzes.aiPdfNote": "AI generation uses PDFs already stored for this class.",
  "quizzes.aiPdfHelp": "That keeps quiz creation tidy and predictable. No extra upload step is needed inside the quiz tool.",
  "quizzes.openNotes": "Open Notes",
  "quizzes.openExamPapers": "Open Exam Papers",
  "quizzes.readyToCreate": "Ready to create",
  "quizzes.emptyHelp": "Start with Generate from PDF for the quickest route. Elume will use a PDF already stored in this class and build a quiz you can refine before delivery.",
  "quizzes.goodSources": "Good sources",
  "quizzes.goodSourcesList": "Notes, revision sheets, worked examples, exam papers",
  "quizzes.goodSourcesHelp": "The clearer the PDF, the better the question generation.",
  "quizzes.afterGeneration": "After generation",
  "quizzes.afterGenerationTitle": "Edit if needed, then move to Live Quiz",
  "quizzes.afterGenerationHelp": "This page is for building the quiz. Live delivery happens through the class Live Quiz tool.",
  "quizzes.builtForClass": "Built quizzes for this class",
  "quizzes.categoryLabel": "Category",
  "quizzes.noDescription": "No description yet.",
  "quizzes.created": "Created",
  "quizzes.nextStep": "Next step",
  "quizzes.deliverViaLiveQuiz": "Deliver via Live Quiz",
  "quizzes.deliverLiveAction": "Deliver live",
  "quizzes.editQuestions": "Edit questions",
  "quizzes.mainCollection": "Main Quiz Collection",
  "quizzes.saveToMainCollection": "Save to Main Quiz Collection",
  "quizzes.inMainCollection": "In Main Quiz Collection",
  "quizzes.editor": "Quiz editor",
  "quizzes.buildFromPdf": "Build a quiz from an existing class PDF",
  "quizzes.generating": "Generating...",
  "calendar.back": "Back",
  "calendar.routeHelp": "Keep class events tidy, visible, and easy to update.",
  "calendar.allHelp": "See everything at a glance across classes and school-wide events.",
  "calendar.globalHelp": "Showing school-wide events only.",
  "calendar.eventsThisMonth": "events this month",
  "calendar.aiPlanning": "AI-assisted planning",
  "calendar.view": "View",
  "calendar.quickSwitch": "Quick switch",
  "calendar.class": "Class",
  "calendar.planner": "Planner",
  "calendar.focusedView": "Focused class view",
  "calendar.focusedViewHelp": "Events here are linked directly to this class.",
  "calendar.aiAssistant": "AI Calendar Assistant",
  "calendar.aiAssistantHelp": "Turn a quick thought into a proper event",
  "calendar.generate": "Generate",
  "calendar.aiSuggestionReady": "AI suggestion ready",
  "calendar.aiSuggestionHelp": "We've opened it in the event modal so you can review and adjust it.",
  "calendar.reopenReview": "Reopen review",
  "calendar.tapDay": "Tap a day to add something quickly.",
  "calendar.more": "more",
  "calendar.reviewAiEvent": "Review AI event",
  "calendar.general": "General",
  "calendar.test": "Test",
  "calendar.homework": "Homework",
  "calendar.trip": "Trip",
  "calendar.visibility": "Visibility",
  "calendar.global": "Global",
  "liveQuiz.savedQuizzes": "Saved Quizzes",
  "liveQuiz.teacherControls": "Teacher controls",
  "liveQuiz.runSession": "Run the session live",
  "liveQuiz.answered": "Answered",
  "liveQuiz.timeLeft": "Time left",
  "liveQuiz.joinLink": "Join link",
  "liveQuiz.sessionCode": "Session code",
  "liveQuiz.copyCode": "Copy code",
  "liveQuiz.copyLink": "Copy link",
  "liveQuiz.resultsHistory": "Results & History",
  "liveQuiz.reportsLocal": "Reports are saved locally on this device.",
  "liveQuiz.topThree": "Top 3",
  "liveQuiz.leaderboard": "Leaderboard",
  "liveQuiz.noReport": "No report selected",
  "liveQuiz.noReportHelp": "End a session to generate a report automatically.",
  "liveQuiz.history": "History",
  "liveQuiz.anonymous": "Anonymous",
  "liveQuiz.named": "Named",
  "liveQuiz.average": "Average",
  "liveQuiz.noHistory": "No history yet.",
  "liveQuiz.classInsights": "Class insights",
  "liveQuiz.insightsHelp": "Recent live quiz attempts for this class, grouped by student.",
  "liveQuiz.all": "All",
  "liveQuiz.counted": "Counted",
  "liveQuiz.excluded": "Excluded",
  "liveQuiz.incomplete": "Incomplete",
  "liveQuiz.attempts": "Attempts",
  "liveQuiz.loadingInsights": "Loading class insights...",
  "class.timetable": "Timetable",
  "class.timetableLoading": "Loading timetable...",
  "class.noTimetable": "No timetable found yet. Set it up in Admin first.",
  "class.studentHubHelp": "Students use this with the class PIN in Student Hub.",
  "class.noAnnouncements": "No announcements yet for this class.",
  "class.loadingAnnouncements": "Loading announcements...",
  "class.postBold": "Bold",
  "class.postItalic": "Italic",
  "class.postColour": "Text colour",
  "class.postDefaultColour": "Black (default)",
  "class.postEmoji": "Insert emoji",
  "class.postEmojiCheck": "Check mark",
  "class.postEmojiStar": "Star",
  "class.postEmojiQuestion": "Question mark",
  "class.postEmojiThumbsUp": "Thumbs up",
  "class.postEmojiClap": "Clapping hands",
  "class.postEmojiCelebrate": "Celebration",
  "class.postEmojiIdea": "Idea",
  "class.postEmojiPin": "Pin",
  "class.postEmojiSmile": "Smiling face",
  "class.postEmojiHeart": "Heart",
  "class.postColourRed": "Red",
  "class.postColourBlue": "Blue",
  "class.postColourGreen": "Green",
  "class.postColourPurple": "Purple",
  "class.postPlaceholder": "Write an announcement...",
  "class.noUpcomingEvents": "No events coming up.",
  "tools.startTimer": "Start timer",
  "tools.randomNameHelp": "Picks from active students in Class Admin.",
  "tools.selectedStudent": "Selected Student",
  "tools.loadingStudents": "Loading students...",
  "tools.noActiveStudents": "No active students found. Add students in Class Admin.",
  "tools.picking": "Picking...",
  "tools.teamGeneratorHelp": "Builds quick random groups from active students in Class Admin.",
  "tools.groupsMayVary": "Final groups may be slightly uneven when numbers do not divide exactly.",
  "dashboard.manageClassHelp": "Archive this class when it is no longer active, or permanently delete it.",
  "dashboard.deletePermanently": "Delete Permanently",
  "login.studentHubEnterCode": "Enter your class code and PIN to join your class.",
  "collab.joinBoard": "Join Collab Board",
  "collab.loading": "Loading…",
  "collab.sessionEnded": "Session ended",
  "collab.sessionEndedHelp": "Your teacher has ended the collaboration session. Returning you to Student Hub…",
  "collab.returnNow": "Return now",
  "collab.waiting": "Waiting…",
  "collab.roomNotAssigned": "Your teacher has not assigned your breakout room yet.",
  "collab.sessionNotStarted": "Your teacher has not started the breakout session yet.",
  "collab.leave": "Leave",
  "collab.room": "Room {{number}}",
  "collab.breakoutRoom": "Breakout Room {{number}}",
  "collab.pen": "Pen",
  "collab.highlighter": "Highlighter",
  "collab.line": "Line",
  "collab.eraser": "Eraser",
  "collab.stickyNote": "Sticky note",
  "collab.undo": "Undo",
  "collab.moveBoard": "Move board",
  "collab.moveBoardOn": "Move board on",
  "collab.moveBoardGuidance": "Drag to move around. Switch off to draw.",
  "collab.colour": "Colour",
  "collab.highlight": "Highlight",
  "collab.stroke": "Stroke",
  "collab.roomMembers": "Room members",
  "collab.board": "Collab Board",
  "collab.joinHeading": "JOIN A COLLAB BOARD",
  "collab.joinGuidance": "Use the Collaboration code shown on the board, then enter your name to join.",
  "collab.code": "COLLAB CODE",
  "collab.yourName": "Your name",
  "collab.namePlaceholder": "e.g. Aoife",
  "videos.title": "Videos",
  "videos.intro": "Paste a YouTube link to add a thumbnail. Click to play.",
  "videos.add": "Add Video",
  "videos.empty": "No videos yet. Click",
  "videos.emptySuffix": "and paste a YouTube link.",
  "videos.addYoutube": "Add a YouTube Video",
  "videos.addGuidance": "Paste a link (youtube.com, youtu.be, shorts).",
  "videos.youtubeLink": "YouTube link (e.g. https://www.youtube.com/watch?v=...)",
  "videos.videoTitle": "Title (e.g. Standing Waves)",
  "videos.category": "Category (e.g. Waves, Mechanics)",
  "videos.edit": "Edit Video",
  "videos.editGuidance": "Update the title, category, or link.",
  "links.emptyCategory": "No resources in this category yet. Click",
  "links.addToCategory": "Add resource to this",
  "links.editCategory": "Edit category",
  "links.newCategory": "New category",
  "links.categoryGuidance": "Add a short description so teachers know what this section is for.",
  "links.categoryName": "Category name (e.g. Simulations, Revision, Homework)",
  "links.shortDescription": "Short description (optional)",
  "links.editLink": "Edit link",
  "links.addLink": "Add link",
  "links.linkGuidance": "Choose a category, paste the link, and add a short note for teachers.",
  "links.shortTitle": "Short title (optional)",
  "links.pasteLink": "Paste link (e.g. phet.colorado.edu / youtube.com / desmos.com)",
  "links.linkDescription": "Short description (why it's here / what to do) (optional)",
  "links.add": "Add",
  "whiteboard.audioLibrary": "Audio Library",
  "whiteboard.audioLibraryHelp": "Choose class audio to play alongside your Whiteboard.",
  "whiteboard.uploadAudio": "Upload Audio",
  "whiteboard.audioFormats": "MP3, WAV, M4A, AAC, or OGG. It stays in this class's Notes Audio category.",
  "whiteboard.changeFile": "Change file",
  "whiteboard.chooseAudio": "Choose audio",
  "whiteboard.saveBoard": "Save whiteboard",
  "whiteboard.saveBoardHelp": "This will save an image and create a post in the class feed.",
  "whiteboard.name": "Whiteboard name",
  "whiteboard.filenameDateHelp": "The filename will include today’s date.",
  "whiteboard.restoreDraft": "Restore whiteboard draft?",
  "whiteboard.importPdf": "Import a PDF",
  "whiteboard.importPdfHelp": "Choose a class PDF or a shared exam paper, then use the existing PDF viewer and snip tools.",
  "whiteboard.classPdfs": "Class PDFs",
  "whiteboard.examLibrary": "Exam Library",
  "whiteboard.startSnip": "Start snip",
  "whiteboard.snipToBoard": "Snip to board",
  "whiteboard.dragToSelect": "Drag to select… (click to cancel)",
  "whiteboard.snipHelp": "Drag a rectangle to select, then click",
  "whiteboard.youtubeVideo": "YouTube video",
  "whiteboard.searchAudio": "Search audio files…",
  "whiteboard.loadingAudio": "Loading Audio Library…",
  "whiteboard.noAudio": "No audio files yet",
  "whiteboard.playYoutube": "Play a YouTube video",
  "whiteboard.openBoard": "Open whiteboard",
  "whiteboard.startNew": "Start new whiteboard",
  "whiteboard.openRecent": "Open recent whiteboard",
  "whiteboard.renameSaved": "Rename",
  "whiteboard.renameSavedHelp": "This changes the board title and its linked class Notes label. It does not resave the board.",
  "whiteboard.renameRequired": "Enter a whiteboard title.",
  "whiteboard.renameTooLong": "Whiteboard titles can be up to {{max}} characters.",
  "whiteboard.title": "Whiteboard title",
  "whiteboard.loadingRecent": "Loading recent whiteboards…",
  "whiteboard.noPdfs": "No PDFs found yet. Upload some Notes/Exam PDFs first.",
  "whiteboard.cycle": "Cycle",
  "whiteboard.subject": "Subject",
  "whiteboard.level": "Level",
  "whiteboard.allLevels": "All levels",
  "whiteboard.noExamPapers": "No shared exam papers match these filters yet.",
  "whiteboard.insertGrid": "Insert Grid",
  "whiteboard.insertXy": "Insert XY Plane",
  "whiteboard.fullScreen": "Full screen",
  "whiteboard.halfScreen": "Half screen",
  "whiteboard.leaveBoard": "Leave whiteboard?",
  "whiteboard.pdfLoadFailure": "We couldn’t load this PDF just now. Please try again.",
  "planner.title": "Teacher Planner",
  "planner.intro": "Weekly diary + tasks for your teaching week. Plan by timetable slot so you can see each class at a glance.",
  "planner.changeSettings": "Change Settings",
  "planner.tasks": "Tasks",
  "planner.deleteTaskConfirm": "Delete this task? This cannot be undone.",
  "planner.settings": "Planner settings",
  "planner.visibleSlots": "Change visible slots",
  "planner.slotsPerDay": "Slots per day",
  "planner.saveSettings": "Save settings",
  "planner.editNote": "Edit note",
  "planner.shortTitle": "Short title",
  "planner.fullNote": "Full note",
  "planner.relatesTo": "Relates to",
  "planner.general": "General",
  "planner.personal": "Personal",
  "planner.classClub": "Class / Club",
  "planner.active": "Active",
  "planner.archived": "Archived",
  "planner.expand": "Expand",
  "planner.collapse": "Collapse",
  "planner.noTasks": "No tasks yet.",
  "planner.archive": "Archive",
  "planner.noArchivedTasks": "No archived tasks.",
  "planner.revive": "Revive",
  "planner.openCalendar": "Open calendar",
  "planner.noEvents": "No events.",
  "planner.openCalendarFor": "Open calendar for {{date}}",
  "planner.noEventsFor": "No events for {{date}}",
  "planner.noteTitlePlaceholder": "e.g. Print worksheets / Prep quiz / Call home…",
  "planner.noteBodyPlaceholder": "Write the full detail here…",
  "planner.due": "Due: {{date}}",
  "planner.noDueDate": "No due date",
  "planner.archivedOn": "• Archived: {{date}}",
  "classAdmin.notesOptional": "Notes (optional)",
  "aac.savedPlanComparison": "Saved plan comparison",
  "tests.newCategory": "New Test Category",
  "tests.categoryTitle": "Category title",
  "tests.descriptionOptional": "Description (optional)",
  "tests.create": "Create",
  "tests.uploadTest": "Upload Test",
  "tests.existingCategory": "Existing category",
  "tests.createNewCategory": "Create new category",
  "tests.testTitle": "Test title",
  "tests.category": "Category",
  "tests.noCategory": "No category",
  "tests.newCategoryTitle": "New category title",
  "tests.newCategoryDescription": "New category description (optional)",
  "tests.file": "File",
  "tests.fileHelp": "PDF is ideal. Word/PPT accepted too.",
  "schoolResources.title": "School Resources",
  "schoolResources.sharedTeachingResources": "Shared teaching resources",
  "schoolResources.description": "Resources shared through your departments. Using one always creates your own fresh copy.",
  "schoolResources.allDepartments": "All my departments",
  "schoolResources.allTypes": "All resource types",
  "schoolResources.collaborationBoards": "Collaboration Boards",
  "schoolResources.quiz": "Quiz",
  "schoolResources.collaborationBoard": "Collaboration Board",
  "schoolResources.sharedBy": "Shared by",
  "schoolResources.sharedByYou": "Shared by you",
  "schoolResources.addToMyQuizzes": "Add to My Quizzes",
  "schoolResources.useForCollaboration": "Use for Collaboration",
  "schoolResources.empty": "No shared resources in your departments yet.",
  "schoolResources.emptyTitle": "A little sharing goes a long way",
  "schoolResources.emptyBody": "Share a useful resource with your colleagues to get your department’s collection started.",
  "schoolResources.addQuiz": "Add quiz to a class",
  "schoolResources.chooseClass": "Choose your class",
  "schoolResources.allGroups": "All my groups",
  "schoolResources.moreGroups": "More groups",
  "schoolResources.shareResource": "Share a resource",
  "schoolResources.findResources": "Find, share and reuse resources from your colleagues.",
  "schoolResources.allResources": "All resources",
  "schoolResources.notes": "Notes",
  "schoolResources.worksheets": "Worksheets",
  "schoolResources.tests": "Tests",
  "schoolResources.quizzes": "Quizzes",
  "schoolResources.searchResources": "Search resources",
  "schoolResources.searchInGroup": "Search {{group}} resources…",
  "schoolResources.resources": "resources",
  "schoolResources.yearGroup": "Year group",
  "schoolResources.allYears": "All years",
  "schoolResources.noMembership": "Your shared resources will appear here once your school admin adds you to a department.",
  "schoolResources.noMatches": "No resources match these filters.",
  "schoolResources.preview": "Preview",
  "schoolResources.addToClass": "Add to my class",
  "schoolResources.shareWithColleagues": "Share with colleagues",
  "schoolResources.unshare": "Unshare",
  "schoolResources.resourceType": "Resource type",
  "schoolResources.shareGroup": "Group",
  "schoolResources.chooseGroup": "Choose an authorised group",
  "schoolResources.resourceDescription": "Description (optional)",
  "schoolResources.resourceTitle": "Title",
  "schoolResources.onlyThisFile": "Only this resource file is shared. Student work and class folders are not shared.",
  "schoolResources.studentVisibility": "This copy will be available in the selected class. Check that it contains no teacher-only answers or marking scheme before adding it.",
  "schoolResources.close": "Close",
  "schoolResources.cancel": "Cancel",
  "schoolResources.adding": "Adding…",
  "schoolResources.sharing": "Sharing…",
  "schoolResources.existingResource": "Existing Note, Worksheet or Test",
  "schoolResources.chooseOwnedResource": "Choose a resource you own",
  "schoolResources.class": "Class",
  "schoolResources.test": "Test",
  "schoolResources.notesWorksheet": "Notes / Worksheet",
  "schoolResources.sharedSuccess": "Your resource is now shared with this group.",
  "schoolResources.unsharedSuccess": "Your resource is no longer shared.",
  "schoolResources.quizCopied": "“{{title}}” was added to your class quizzes.",
  "schoolResources.resourceCopied": "“{{title}}” was added to {{className}}.",
  "schoolResources.breadcrumbHome": "Home",
  "schoolResources.breadcrumbResources": "Shared resources",
  "schoolResources.previewTitle": "Preview: {{title}}",
  "schoolResources.downloadFile": "Download {{filename}}",
  "schoolResources.collabPreview": "Collaboration board preview",
  "schoolResources.boardItems": "{{count}} board items",
  "schoolResources.demoLabel": "Elume Whole School Package",
  "schoolResources.packageTitle": "Bring shared teaching resources to your school",
  "schoolResources.packageDescription": "With an Elume Whole School Package, teachers can share tests, worksheets and other teaching resources across their school departments.",
  "schoolResources.trialEyebrow": "FREE 6-WEEK SCHOOL TRIAL",
  "schoolResources.trialTitle": "FREE 6-WEEK SCHOOL TRIAL",
  "schoolResources.trialDescription": "Give your teaching team time to explore Elume together. Tell us about your school and we’ll follow up to arrange the next steps.",
  "schoolResources.trialBenefitOne": "Share useful teaching resources across school groups.",
  "schoolResources.trialBenefitTwo": "Explore collaborative tools for lessons and classes.",
  "schoolResources.trialBenefitThree": "Get a school-focused introduction from the Elume team.",
  "schoolResources.requestTrial": "Request your free school trial",
  "schoolResources.contactName": "Contact name",
  "schoolResources.optional": "optional",
  "schoolResources.publicNavigation": "School trial navigation",
  "schoolResources.signIn": "Teacher sign in",
  "schoolResources.showcaseAlt": "Illustration of teaching resources shared across a school",
  "schoolResources.illustrativeOnly": "Illustrative examples — not downloadable teacher contributions.",
  "schoolResources.enquiryTitle": "Tell us about your school",
  "schoolResources.enquiryDescription": "Share your contact details and we’ll reply about arranging your school’s trial.",
  "schoolResources.name": "Your name",
  "schoolResources.workEmail": "Work email",
  "schoolResources.schoolName": "School name",
  "schoolResources.role": "Your role",
  "schoolResources.teacherCount": "Approximate number of teachers",
  "schoolResources.message": "Message",
  "schoolResources.website": "Website",
  "schoolResources.sendEnquiry": "Request your free school trial",
  "schoolResources.sending": "Sending…",
  "schoolResources.privacyNote": "We’ll use these details only to respond to your school-trial request. They won’t be added to a marketing list.",
  "schoolResources.privacyLink": "Privacy notice",
  "schoolResources.enquirySuccess": "Thanks! Your request has been received. Peter will be in touch to arrange your school’s trial access.",
  "schoolResources.enquiryFailure": "We couldn’t send your request just now. Your details are still here. Please try again or email",
  "schoolResources.enquiryDeliveryFailure": "The email service could not accept your request. Your details are still here. Please try again or email",
  "schoolResources.enquiryNotAccepted": "We could not accept that request. No email was sent. Please check the form and try again, or email",
  "schoolResources.enquiryDuplicateConflict": "This request key was already used for different details. Reload the page to start a new request; your current entries will remain visible until you leave.",
  "schoolResources.enquiryValidationFailure": "Please check the highlighted fields and try again.",
  "schoolResources.fieldInvalid": "Please check this value.",
  "schoolResources.enquiryRateLimited": "Please wait about {{minutes}} minute(s) before sending another request. You can also email",
  "classAdmin.title": "Class Admin",
  "classAdmin.students": "Students",
  "classAdmin.assessments": "Assessments",
  "classAdmin.results": "Results",
  "classAdmin.insights": "Insights",
  "classAdmin.addStudent": "Add Student",
  "classAdmin.rosterHelp": "Add first names only. Use notes for quick teacher reminders.",
  "classAdmin.demoRosterLocked": "In your own Elume account, you can add and manage your students. This demo uses a prepared class so you can explore results and progress.",
  "classAdmin.demoClassReportRestriction": "This demo lets you explore prepared Class Insights and individual student reports. In your own Elume account, you can also open a report for your whole class.",
  "classAdmin.refreshStudentLink": "Refresh Student Link",
  "classAdmin.copyStudentLink": "Copy Student Link",
  "classAdmin.studentLinkReady": "Student access link ready for this class.",
  "classAdmin.firstNamePlaceholder": "First name (e.g. Aoife)",
  "classAdmin.addMultiple": "Add multiple students",
  "classAdmin.bulkHelp": "Paste first names (one per line or comma separated).",
  "classAdmin.addStudents": "Add Students",
  "classAdmin.testTitle": "Test title",
  "classAdmin.dateOptional": "Date (optional)",
  "classAdmin.enteredAbsent": "Entered / absent",
  "classAdmin.currentAverage": "Current average",
  "classAdmin.absent": "Absent",
  "classAdmin.actions": "Actions",
  "classAdmin.scorePlaceholder": "0-100",
  "classAdmin.yes": "Yes",
  "classAdmin.no": "No",
  "classAdmin.clearResult": "Clear Result",
  "classAdmin.deleteStudent": "Delete student?",
  "classAdmin.deleting": "Deleting…",
  "classAdmin.deleteStudentAction": "Delete student",
  "classAdmin.performanceOverTime": "Performance over time",
  "classAdmin.performanceHelp": "Scores are shown in chronological assessment order.",
  "classAdmin.chartLegend": "Chart legend",
  "classAdmin.student": "Student",
  "classAdmin.chartDescription": "Student and class average performance over time",
  "classAdmin.atRisk": "At-risk students",
  "classAdmin.noAtRisk": "No at-risk students flagged.",
  "classAdmin.rankings": "Rankings",
  "classAdmin.noResults": "No results yet.",
  "classAdmin.aiComments": "AI Report Comments",
  "classAdmin.aiCommentsHelp": "Generate individual student report comments using results + teacher-selected indicators.",
  "classAdmin.generating": "Generating…",
  "classAdmin.generateComment": "Generate Comment",
  "classAdmin.commentPlaceholder": "Generated comment will appear here…",
  "classAdmin.noHistory": "No history available yet.",
  "classAdmin.atRiskRule": "Rule: avg < 50 or missed ≥ 2",
  "classAdmin.rankingHelp": "Latest vs overall avg (±3% = arrow)",
  "classAdmin.name": "Name",
  "classAdmin.average": "Average",
  "classAdmin.latest": "Latest",
  "classAdmin.taken": "Taken",
  "classAdmin.missed": "Missed",
  "classAdmin.report": "Report",
  "classAdmin.openStudentReport": "Open Student Report",
  "classAdmin.short": "Short",
  "classAdmin.medium": "Medium",
  "classAdmin.long": "Long",
  "classAdmin.indicators": "Indicators",
  "classAdmin.finalSignOff": "Final sign-off",
  "classAdmin.signOffPlaceholder": "e.g. Enjoy the summer break.",
  "classAdmin.comment": "Comment",
  "classAdmin.copyComment": "Copy comment",
  "ai.inputLabel": "AI-assisted",
  "ai.outputLabel": "AI-generated draft",
  "aac.step3": "Step 3 of 4",
  "aac.step4": "Step 4 of 4",
  "aac.teachingTime": "When will your class work on this?",
  "aac.setupNew": "Set up your new AAC tracker",
  "aac.briefTitle": "Brief title",
  "aac.chooseSpecification": "Choose your specification",
  "aac.step2": "Step 2 of 4",
  "aac.navStart": "1. Get started",
  "aac.navFound": "2. What we found",
  "aac.navTime": "3. Teaching time",
  "aac.navPlan": "4. Suggested plan",
  "aac.sourceFindingsHelp": "These are source findings for you to review, not official decisions and not an AI-generated plan.",
  "aac.keyDates": "Key dates",
  "aac.uploadFormats": "PDF or DOCX only, maximum 15 MB. Scanned/unreadable PDFs need a text-readable version or manual entry.",
  "aac.chooseFile": "Choose file",
  "aac.sourceFile": "AAC source file",
  "aac.noSources": "No sources yet. Manual drafting is still available.",
  "aac.setupManually": "Set up manually",
  "aac.manualReady": "Manual planning is ready. Add key dates, teaching time and stages whenever you are ready.",
  "aac.briefUploaded": "Brief uploaded ✓",
  "aac.reviewExtracted": "Review extracted source sections",
  "aac.removeSourceQuestion": "Remove this source?",
  "aac.createBeforeSources": "Finish creating the new AAC tracker before adding sources.",
  "aac.removeSourceFallback": "The source was kept. Please try again.",
  "aac.documentType": "Document type",
  "aac.earlierDocuments": "Earlier documents are kept with your saved trackers, but none are selected or used here.",
  "aac.specificationBrief": "Specification / brief",
  "aac.purpose.fifth_year_calendar": "Fifth Year school calendar",
  "aac.purpose.sixth_year_calendar": "Sixth Year school calendar",
  "aac.chooseUploadSource": "Choose a source to upload",
  "aac.removeSource": "Remove source",
  "aac.briefUploadedReview": "Brief uploaded ✓. Review what we found when you are ready.",
  "aac.extractionFailed": "The document was uploaded but could not be extracted. You can enter details manually.",
  "aac.uploadFallback": "Use a text-readable PDF or DOCX under 15 MB, or enter information manually.",
  "aac.getStarted": "Let's get started…",
  "aac.examinationYear": "New examination year",
  "aac.examinationYearOptional": "Examination year (optional)",
  "aac.createTracker": "Create AAC tracker",
  "aac.currentProjectStage": "New current project stage",
  "aac.fifthYear": "Fifth Year",
  "aac.sixthYear": "Sixth Year",
  "aac.alreadyUnderway": "Already underway",
  "aac.defaultMinutesHelp": "30 minutes per week is the editable default. Upload your brief to review its stages and key dates.",
  "aac.working": "Working…",
  "aac.generateDraft": "Generate draft suggestions",
  "aac.step1Help": "Upload the specification or brief for your subject. We’ll help you find the important dates and build a plan you can adjust.",
  "aac.addCalendarsOptional": "Add school calendars — optional",
  "aac.calendarHelp": "Calendars help plan around teaching dates and breaks. You can enter those details yourself.",
  "aac.continueToReview": "Continue → Review what we found",
  "aac.removeSourceHelp": "will no longer support new suggestions. If an approved plan uses it, only the active source is removed and the historical copy is retained.",
  "aac.removing": "Removing…",
  "aac.extraction.label": "Extraction",
  "aac.extraction.pending": "Pending extraction",
  "aac.extraction.extracted": "Extracted",
  "aac.extraction.unreadable": "Could not be read",
  "aac.extraction.replaced": "Replaced",
  "aac.extraction.unknown": "Status unavailable",
  "aac.trackerCreatedAddBrief": "AAC tracker created. Add your new brief when you are ready.",
  "aac.trackerCreatedManual": "AAC tracker created. Add a brief or set it up manually.",
  "aac.trackerCreateFallback": "We couldn’t create the AAC tracker.",
  "aac.newBriefReady": "Ready for your new brief.",
  "aac.newDraftFallback": "Your current tracker was kept because a new tracker could not be started.",
  "aac.sourceUploadedStatus": "Source uploaded. Its status is shown below.",
  "aac.startNewBriefPrompt": "Start again with a new brief?",
  "aac.startNewBriefHelp": "Your saved tracker will be kept, but any unsaved changes will be lost.",
  "aac.startNewTracker": "Start new AAC tracker",
  "aac.newBriefHistory": "Start again with a new brief while keeping your saved tracker history.",
  "aac.weeklyMinutes": "Weekly AAC minutes",
  "aac.setupSteps": "AAC setup steps",
  "aac.sourceReviewHeading": "Here’s what we found",
  "aac.noReadableSpecification": "Add a readable specification or brief in Step 1, or continue with manual setup.",
  "aac.reviewSourceEvidence": "Review source evidence",
  "aac.multipleDeadlinesStep3": "More than one possible deadline was found. Choose the controlling student completion date in Step 3.",
  "aac.secCompletionHandIn": "SEC completion / hand-in",
  "aac.useConfirmDate": "Use and confirm this date",
  "aac.explicitSourceStages": "Explicit stages from the brief",
  "aac.reviewApplySourceStages": "Review and apply source stages",
  "aac.continueTeachingTime": "Continue to teaching time",
  "aac.deadlineCheckFallback": "We couldn't check the extracted specification for a deadline.",
  "aac.deadlineRequired": "Enter an SEC completion / hand-in deadline before confirming it.",
  "aac.deadlineConfirmed": "Deadline confirmed in this editable tracker. Save it before approval.",
  "aac.documentDeadlineReview": "Document deadline review",
  "aac.checkDocumentDeadline": "Check document for deadline",
  "aac.deadlineReviewLoading": "Checking extracted specifications. This does not change stages or generate suggestions.",
  "aac.deadlineStatus.specificationNeeded": "A readable AAC specification or brief is needed before Elume can check an official deadline. School calendars do not supply this date.",
  "aac.deadlineStatus.extractionPending": "Your specification is still being extracted. Check again when it is marked extracted.",
  "aac.deadlineStatus.extractionFailed": "The specification could not be read. Upload a text-readable copy or enter the SEC date manually.",
  "aac.deadlineStatus.noClearDeadline": "No clear student completion / hand-in deadline was found in the extracted specification. Enter the date manually and confirm it after checking the brief.",
  "aac.deadlineStatus.conflictingCandidates": "The specification contains conflicting possible deadlines. Review the source excerpts below and select or enter the correct SEC date.",
  "aac.deadlineSuggestionConfirm": "Suggested from your specification — please confirm.",
  "aac.source": "source",
  "aac.suggestedSecDate": "Suggested SEC completion / hand-in date",
  "aac.suggestedFrom": "Suggested from",
  "aac.confirmedDateRetainedPrefix": "Your confirmed date",
  "aac.confirmedDateRetainedSuffix": "stays unchanged unless you explicitly use this suggestion.",
  "aac.useSuggestedDate": "Use suggested date",
  "aac.confirmDate": "Confirm date",
  "aac.changeDate": "Change date",
  "aac.changeDateConfirmBeforeApproval": "Change the date in Key dates, then confirm it before approval.",
  "aac.confirmEnteredSecDate": "Confirm entered SEC date",
  "aac.foundStages": "We found",
  "aac.inThisBrief": "stages in this brief",
  "aac.separateSourceTask": "Separate source task",
  "aac.replaceStagesQuestion": "Replace tracker stages with source stages?",
  "aac.replaceStagesHelp": "This replaces only the editable tracker stages with the reviewed source-labelled stages. Your approved plan and calendars are unchanged.",
  "aac.keepCurrentTracker": "Keep current tracker",
  "aac.sourceStagesReady": "Source stages are ready in your editable tracker. Save when you are ready.",
  "aac.applyStages": "Apply",
  "aac.missing.secDeadline": "SEC completion / hand-in deadline",
  "aac.missing.secConfirmation": "confirmation of the SEC completion / hand-in deadline",
  "aac.missing.plannedStart": "when AAC work will start",
  "aac.missing.finalDeadline": "final classroom deadline",
  "aac.missing.fifthYearEnd": "Fifth Year teaching end date",
  "aac.missing.sixthYearRestart": "Sixth Year restart date",
  "aac.missing.namedStage": "at least one named stage",
  "aac.addBeforeSuggestions": "Add the following before Elume can suggest stage dates:",
  "aac.suggestedDatesSaveFallback": "Suggested dates are ready in your editable tracker but could not be saved. Please try Save tracker.",
  "aac.trackerSavedSuggestionsAdded": "Tracker saved and suggested dates added. Review or edit the stages and dates at any time; save any changes before opening student progress.",
  "aac.trackerSavedDatesPreserved": "Tracker saved. Your existing teacher-entered stage dates were preserved.",
  "aac.stageDatesCalculationFallback": "Draft saved, but Elume could not calculate stage dates. Review the schedule warnings and teaching dates.",
  "aac.calendarDay": "calendar day",
  "aac.calendarDays": "calendar days",
  "aac.deadlineIs": "This deadline is",
  "aac.afterSecDeadline": "after the SEC deadline.",
  "aac.deadlineLeaves": "This leaves",
  "aac.daysBeforeSecDeadline": "days before the SEC deadline. Allow at least 14 days.",
  "aac.addBothDeadlinesForBuffer": "Add the SEC completion / hand-in deadline and final classroom deadline to check the required buffer.",
  "aac.addSecDeadlineForBuffer": "Add the SEC completion / hand-in deadline to check the required buffer.",
  "aac.addFinalDeadlineForBuffer": "Add the final classroom deadline to check the required buffer.",
  "aac.editableChoicesHelp": "Teacher choices are editable and never silently moved.",
  "aac.secCompletionHandInDeadline": "SEC completion / hand-in deadline",
  "aac.secCompletionHandInHelp": "The confirmed official deadline for students to complete or hand in this AAC. Check the specification or brief before confirming it.",
  "aac.plannedStart": "When will you start?",
  "aac.plannedStartHelp": "The date you plan to begin AAC work with this class.",
  "aac.studentFinishTarget": "When should students aim to finish?",
  "aac.studentFinishTargetHelp": "Your normal classroom finish date, leaving time for students who need to catch up before your final classroom deadline.",
  "aac.finalClassroomDeadline": "Final classroom deadline",
  "aac.finalClassroomDeadlineHelp": "Your latest classroom deadline for outstanding work and catch-up. Leave at least 14 calendar days before the SEC deadline.",
  "aac.changeFinalDeadline": "Change your final classroom deadline",
  "aac.secDeadline": "SEC deadline",
  "aac.latestPermittedClassroomDate": "Latest permitted classroom date",
  "aac.addDatesForCatchUp": "Add normal finish and final classroom dates to see catch-up time.",
  "aac.normalFinishBeforeFinalDeadline": "Normal finish must be on or before the final classroom deadline.",
  "aac.catchUpTime": "Catch-up time",
  "aac.finalDeadlineLeaves": "Your final classroom deadline leaves",
  "aac.beforeSecDeadline": "before the SEC deadline.",
  "aac.otherDates": "Other dates",
  "aac.otherDatesHelp": "Administrative school submission dates are separate from the student SEC completion deadline.",
  "aac.schoolSubmissionDate": "School administrative submission date",
  "aac.schoolSubmissionDateHelp": "Optional school administration date; it does not replace the SEC student completion deadline.",
  "aac.fifthYearTeachingEnds": "Fifth Year teaching ends",
  "aac.sixthYearRestarts": "Sixth Year restarts",
  "aac.sixthYearCalendar": "Sixth Year calendar",
  "aac.calendarProvisional": "Provisional — calendar not yet supplied",
  "aac.teacherReviewedCalendar": "Teacher-reviewed calendar",
  "aac.buffer": "buffer",
  "aac.beforeContinuing": "Before continuing",
  "aac.minimumBufferGuidance": "change the final classroom deadline so it is at least 14 calendar days before the SEC deadline.",
  "aac.teachingTimeHelp": "AAC minutes are a weekly total, not per lesson. Confirm these estimates; Sixth Year can remain provisional.",
  "aac.lessonsPerWeek": "Lessons per week",
  "aac.minutesPerLesson": "Minutes per lesson",
  "aac.beforeSuggestingStageDates": "Before Elume can suggest stage dates",
  "aac.readyForSuggestedDates": "Ready to save this tracker and calculate editable suggested dates. Your existing stage dates will not be changed.",
  "aac.savingTracker": "Saving tracker…",
  "aac.continueSaveTracker": "Continue and save tracker",
  "aac.saveFallback": "Your draft wasn’t saved. Please try again.",
  "aac.trackerSavedUnchanged": "Tracker saved. The approved plan and calendar remain unchanged.",
  "aac.saveBeforeApplyingSuggestions": "Save or refresh your edited draft before applying schedule suggestions.",
  "aac.suggestedDatesReadySave": "Suggested dates are ready in your tracker. Save tracker to keep them.",
  "aac.noSuggestedDates": "There are no current suggested dates to apply.",
  "aac.saveBeforeRecalculating": "Save or refresh your edited draft before recalculating suggested dates.",
  "aac.recalculationNoDates": "Elume could not calculate replacement dates. Review the teaching window and schedule warnings.",
  "aac.recalculationFallback": "Elume could not recalculate suggested dates.",
  "aac.chooseReplacementDate": "Choose at least one stage date to replace, or keep the current draft unchanged.",
  "aac.replacementDatesSelected": "Selected replacement dates are in your tracker. Review them, then choose Save tracker to keep them.",
  "aac.chooseDateBeforeMove": "Apply a suggested date or choose a date before moving this stage by a week.",
  "aac.reviewReplacementDates": "Review replacement dates",
  "aac.replacementDatesHelp": "These dates use your saved teaching window and capacity. Older drafts do not record whether a date was generated or teacher-entered, so nothing is selected by default.",
  "aac.untitledStage": "Untitled stage",
  "aac.noSavedDate": "No saved date",
  "aac.keepCurrentDates": "Keep current dates",
  "aac.applySelectedDates": "Apply selected dates",
  "aac.suggestedPlanHeading": "Here’s your suggested plan",
  "aac.reorderHelp": "Use buttons as well as touch/keyboard controls to reorder.",
  "aac.newStage": "New stage",
  "aac.addStage": "Add stage",
  "aac.stage": "Stage",
  "aac.moveStage": "Move stage",
  "aac.up": "up",
  "aac.down": "down",
  "aac.moveUp": "Move up",
  "aac.moveDown": "Move down",
  "aac.removeStage": "Remove stage",
  "aac.stageTitle": "Stage title",
  "aac.name": "name",
  "aac.completionDate": "Completion date",
  "aac.date": "date",
  "aac.oneWeekEarlier": "1 week earlier",
  "aac.oneWeekLater": "1 week later",
  "aac.minusOneWeek": "−1 week",
  "aac.plusOneWeek": "+1 week",
  "aac.adjustTimeAllocation": "Adjust time allocation",
  "aac.minutes": "minutes",
  "aac.suggested": "suggested",
  "aac.totalTeachingTimeForStage": "Total teaching time for this stage (minutes)",
  "aac.suggestedEstimateHelp": "Suggested from your plan. You can increase or reduce this estimate.",
  "aac.teacherEnteredEstimate": "Teacher-entered estimate.",
  "aac.addTeachingInputsForAllocation": "Add teaching inputs before Elume can allocate time for this stage.",
  "aac.checkpoint": "Checkpoint",
  "aac.removeCheckpoint": "Remove checkpoint",
  "aac.newCheckpoint": "New checkpoint",
  "aac.addCheckpoint": "Add checkpoint",
  "aac.planSummary": "Plan summary",
  "aac.totalAllocatedTeachingTime": "Total allocated teaching time",
  "aac.availableTeachingTime": "Available teaching time",
  "aac.preferredStudentFinish": "Preferred student finish",
  "aac.notSet": "Not set",
  "aac.notConfirmed": "Not confirmed",
  "aac.finalClassroomToSecBuffer": "Final-classroom-to-SEC buffer",
  "aac.provisionalAllocationHelp": "Suggested stage allocations use your available teaching time. Adjust them to suit your class.",
  "aac.staleSuggestionsHelp": "Schedule suggestions are stale after your edits. Save tracker to refresh them.",
  "aac.schedulingNeedsAttention": "Scheduling needs attention",
  "aac.calendarProvisionalHeading": "Calendar information is provisional",
  "aac.editDates": "Edit dates",
  "aac.editTeachingTime": "Edit teaching time",
  "aac.recalculating": "Recalculating…",
  "aac.saving": "Saving…",
  "aac.saveTracker": "Save tracker",
  "aac.replacementDatesPrivateHelp": "Review proposed replacement dates before applying them. Saving keeps this tracker private to teachers; this pilot does not publish calendar events.",
  "aac.recordedPlanningAssumptions": "Recorded planning assumptions",
  "aac.warning.confirmControllingDeadline": "Confirm a controlling completion deadline before approval.",
  "aac.warning.confirmSecDeadline": "Confirm the SEC completion / hand-in deadline before approval.",
  "aac.warning.setCompletionTarget": "Set an internal completion target at least 14 calendar days before the deadline.",
  "aac.warning.bufferNotPreserved": "The internal completion target does not preserve the 14-calendar-day buffer.",
  "aac.warning.useIsoDates": "Use ISO dates for the controlling deadline and completion target.",
  "aac.warning.startBeforeFinish": "Your planned start must be before the normal finish date.",
  "aac.warning.normalFinishBeforeFinal": "Your normal finish date must be on or before the final classroom deadline.",
  "aac.warning.finalBeforeSec": "Your final classroom deadline must be before the SEC completion / hand-in deadline.",
  "aac.warning.validCalendarDates": "Use valid calendar dates for the AAC key dates.",
  "aac.warning.sixthYearProvisional": "Sixth Year availability is provisional until the school calendar is confirmed.",
  "aac.warning.validWeeklyAllocation": "Set a valid weekly AAC allocation before approval.",
  "aac.warning.confirmYearDates": "Confirm Fifth Year end and Sixth Year restart dates before approval.",
  "aac.warning.sixthRestartAfterFifthEnd": "Sixth Year restart must be after Fifth Year teaching ends.",
  "aac.warning.invalidClosureDate": "A reviewed closure has an invalid date.",
  "aac.warning.setStartBeforeSuggestions": "Set when AAC work will start before Elume can suggest stage dates.",
  "aac.warning.moveStartBeforeFinish": "Move the AAC start date on or before the preferred finish date.",
  "aac.warning.provisionalStageTimes": "Some stage timings are editable planning estimates because no duration was supplied.",
  "aac.warning.noCapacityForAllocation": "Elume cannot allocate stage time until there is available teaching capacity in the planned window.",
  "aac.warning.noTeachingTime": "There is no available teaching time in the planned window.",
  "aac.warning.stageDatesInOrder": "Stage dates must remain in order.",
  "aac.warning.exceedsCapacity": "Estimated AAC work exceeds available teaching capacity; adjust it explicitly rather than compressing the plan.",
  "aac.warning.stageNeedsIdAndName": "needs a stable ID and name",
  "aac.warning.stageNeedsPositiveEstimate": "needs a positive estimate",
  "aac.warning.stageInvalidDate": "has an invalid completion date",
  "aac.warning.stageOutsideTeachingTime": "falls outside available AAC teaching time",
  "aac.warning.weeklyMinutesRange": "AAC time must be between 5 and 600 minutes each week",
  "aac.warning.cannotExceedTeachingTime": "AAC time cannot exceed the class teaching time you entered",
  "aac.warning.checkTeachingTimeNumbers": "Check the teaching-time numbers for",
  "aac.trackThisClass": "Track this class",
  "aac.progressSaveFirst": "Save your tracker to continue to student progress.",
  "aac.progressNext": "Next: track each student’s progress through these stages.",
  "aac.reviewStageRemoval": "Review stage removal",
  "aac.reviewStagesWithProgress": "Review stages with recorded progress",
  "aac.removeStagesHelp": "Removing these stages hides them from the active grid. Student check-ins and the removed stage details will remain in read-only history.",
  "aac.keepEditing": "Keep editing",
  "aac.removeReviewedStages": "Remove reviewed stages and retain history",
  "aac.approvalSuccess": "Plan approved. Milestones are now in your teacher class and all-events calendars; students cannot view this plan.",
  "aac.approvalStale": "This review is no longer current. Refresh the saved draft, review it again, then approve.",
  "aac.approvalFallback": "The plan could not be approved. Your saved draft and existing approved plan are unchanged.",
  "aac.reviewedSourceRequirements": "Reviewed source requirements and dates",
  "aac.requirement": "Requirement",
  "aac.noRequirementsReview": "Add requirements manually or generate suggestions from selected sources.",
  "aac.reviewAndSave": "Review and save",
  "aac.draftOnlyReviewHelp": "Save AAC trackers only. AI suggestions, approval and calendar publication are unavailable.",
  "aac.noRecordedAssumptions": "No recorded assumptions yet.",
  "aac.draftOnlyPublicationHelp": "AAC Tracker does not publish stages to any calendar or share them with students.",
  "aac.approvalPublicationHelp": "Approval publishes the saved stage milestones to your teacher class and all-events calendars. It does not share this plan with students.",
  "aac.approvedPlanLive": "Approved plan is live in your calendars.",
  "aac.openClassCalendar": "Open class calendar",
  "aac.saveBeforeApproval": "Save your edits before approval so the reviewed revision and token are current.",
  "aac.refreshBeforeApproval": "Refresh the saved draft before approving again.",
  "aac.resolveWarningsBeforeApproval": "Resolve the scheduling warnings shown above before approval.",
  "aac.approving": "Approving…",
  "aac.approvePlan": "Approve plan and update my calendars",
  "aac.refreshReview": "Refresh review",
  "aac.progress.notStarted": "Not started",
  "aac.progress.inProgress": "In progress",
  "aac.progress.readyForReview": "Ready for teacher review",
  "aac.progress.teacherReviewed": "Teacher reviewed",
  "aac.progress.discardConfirm": "Discard unsaved check-in input? Saved server records will be kept.",
  "aac.progress.loadFallback": "The server grid could not be loaded. Retry without changing saved records.",
  "aac.progress.unsaved": "Unsaved changes",
  "aac.progress.saving": "Saving…",
  "aac.progress.saved": "Saved to server",
  "aac.progress.notSaved": "Not saved",
  "aac.progress.retry": "Please retry",
  "aac.progress.inputRetained": "Your input is retained.",
  "aac.progress.checkInStatus": "check-in status",
  "aac.progress.progress": "progress",
  "aac.progress.saveCheckIn": "Save check-in",
  "aac.progress.save": "Save",
  "aac.progress.ariaLabel": "AAC student progress",
  "aac.progress.heading": "Student progress",
  "aac.progress.privateHelp": "Private teacher check-ins, saved to this class on the server. Dates never mark work complete.",
  "aac.progress.plan": "Plan",
  "aac.progress.reload": "Reload saved grid",
  "aac.progress.loading": "Loading saved progress…",
  "aac.progress.trackerHistory": "Tracker history",
  "aac.progress.current": "Current",
  "aac.progress.history": "History",
  "aac.progress.readOnly": "Historical tracker — read-only. Prior check-ins are preserved.",
  "aac.progress.showArchived": "Show archived students with history",
  "aac.progress.noStages": "Save named tracker stages in the Plan to begin tracking.",
  "aac.progress.student": "Student",
  "aac.progress.classDateToConfirm": "Class date to confirm",
  "aac.progress.teacherReviewedSaved": "teacher reviewed (saved)",
  "aac.progress.archivedHistory": "Archived — history only",
  "aac.progress.checkIn": "check-in",
  "aac.progress.close": "Close",
  "aac.progress.classTarget": "Class target",
  "aac.progress.notSet": "Not set",
  "aac.progress.individualTargetHelp": "Individual target (optional; blank uses class target)",
  "aac.progress.individualTarget": "Individual target",
  "aac.progress.individualTargetAria": "individual target",
  "aac.progress.effectiveTarget": "Effective target",
  "aac.progress.teacherNote": "Teacher note / next action",
  "aac.progress.followUpDate": "Follow-up date (optional)",
  "aac.progress.followUpDateAria": "Follow-up date",
  "aac.progress.removedStages": "Removed stages — retained history",
  "aac.progress.noCheckIn": "No check-in recorded",
  "aac.recalculateSuggestedDates": "Recalculate suggested dates",
  "classAdmin.addAssessment": "Add Assessment",
  "classAdmin.active": "Active",
  "classAdmin.inactive": "Inactive",
  "classAdmin.studentRoster": "Student Roster",
  "classAdmin.noStudents": "No students yet.",
  "classAdmin.testsResults": "Tests & Results",
  "classAdmin.noTests": "No tests yet.",
  "classAdmin.classAverage": "Class Average",
  "classAdmin.openReport": "Open report",
  "classAdmin.createTest": "Create Test",
  "classAdmin.editTest": "Edit Test",
  "classAdmin.editStudentName": "Edit name",
  "classAdmin.editStudentNameHelp": "This updates the class roster name used by Insights, results and future quiz matching.",
  "classAdmin.studentName": "Student name",
  "classAdmin.studentNameRequired": "Enter a student name.",
  "classAdmin.studentNameSaveFailed": "We couldn't save that name just now. Please try again.",
  "classAdmin.studentNameUnavailable": "We couldn't find that student in this class roster.",
  "classAdmin.totalMarks": "Total marks",
  "classAdmin.totalMarksRequired": "Total marks must be a positive number.",
  "classAdmin.totalMarksChangeConfirm": "Changing total marks keeps each raw mark and recalculates percentages. Continue?",
  "classAdmin.updateTotalMarksTitle": "Update total marks?",
  "classAdmin.updateTotalMarksBody": "Change the total from {{oldTotal}} to {{newTotal}}? Students' marks will stay the same, and their percentages will be recalculated.",
  "classAdmin.goBack": "Go back",
  "classAdmin.updateTotal": "Update total",
  "classAdmin.unsavedResultsKept": "Your unsaved result changes are still here.",
  "classAdmin.rawMarksExceedTotal": "One or more unsaved marks are above the new total. Correct them before saving results.",
  "classAdmin.legacyPercentageOnly": "Percentage-only assessment",
  "classAdmin.legacyPercentageOnlyHelp": "This legacy percentage-only assessment keeps its original results. Total marks cannot be added after results are saved.",
  "classAdmin.markOutOf": "Mark (out of {{total}})",
  "classAdmin.percentage": "Percentage",
  "classAdmin.result": "Result (%)",
  "seatingPlan.title": "Seating Plan",
  "seatingPlan.loading": "Loading seating plan...",
  "seatingPlan.backToClass": "Back to class",
  "seatingPlan.exportImage": "Export Image",
  "seatingPlan.print": "Print Seating Plan",
  "seatingPlan.regenerate": "Regenerate",
  "seatingPlan.setupRoster": "Setup & Roster",
  "seatingPlan.rows": "Rows of tables",
  "seatingPlan.tablesPerRow": "Tables per row",
  "seatingPlan.create": "Create seating plan",
  "seatingPlan.studentsInPlan": "Students in plan",
  "tools.randomName": "Random Name Generator",
  "tools.timer": "Timer",
  "tools.timeUp": "Time is up!",
  "tools.minutes": "Minutes",
  "tools.seconds": "Seconds",
  "tools.start": "Start",
  "tools.pause": "Pause",
  "tools.reset": "Reset",
  "tools.done": "Done",
  "tools.pickName": "Pick Name",
  "tools.teamGenerator": "Team Generator",
  "tools.groupSize": "Group size",
  "tools.generateTeams": "Generate Teams",
  "studentHub.studentHub": "Student Hub",
  "studentHub.eyebrow": "STUDENT HUB",
  "studentHub.welcome": "Welcome to Student Hub",
  "studentHub.welcomeName": "Welcome, {{name}}",
  "studentHub.headingHelp": "Your classes, live activities and exam papers are all in one place.",
  "studentHub.savedClasses": "Your classes",
  "studentHub.savedClassesHelp": "Saved on this device for quick access.",
  "studentHub.addFirstClass": "Add your first class",
  "studentHub.addFirstClassHelp": "Enter the class code and PIN from your teacher.",
  "studentHub.joinAnotherClass": "Join another class",
  "studentHub.classFallback": "Class",
  "studentHub.classCode": "Class code",
  "studentHub.removeDevice": "Remove from this device",
  "studentHub.viewAll": "View all classes",
  "studentHub.showLess": "Show less",
  "studentHub.liveActivities": "Live activities",
  "studentHub.live": "LIVE",
  "studentHub.liveQuiz": "Live Quiz",
  "studentHub.quizHelp": "Join your teacher's live quiz with a quiz code.",
  "studentHub.enterQuizCode": "Enter quiz code",
  "studentHub.collabBoard": "Collab Board",
  "studentHub.collabHelp": "Draw and work together with your class.",
  "studentHub.enterBoardCode": "Enter board code",
  "studentHub.examPapers": "Exam Papers",
  "studentHub.examPapersHelp": "Practise with Junior Cycle and Leaving Cert papers.",
  "studentHub.browsePapers": "Browse papers",
  "studentHub.practiceQuizzes": "Practice Quizzes",
  "studentHub.practiceLibrary": "Elume Practice Library",
  "studentHub.practiceLibraryHelp": "Choose a subject and practise at your own pace.",
  "studentHub.openPracticeLibrary": "Open Practice Library",
  "studentHub.teacherQuizzes": "Your teacher’s quizzes",
  "studentHub.teacherQuizzesHelp": "Quizzes your teacher has assigned for you to practise.",
  "studentHub.noTeacherQuizzes": "No quizzes assigned by your teacher yet.",
  "studentHub.loadingTeacherQuizzes": "Loading your teacher’s quizzes…",
  "studentHub.classEvents": "Class events",
  "studentHub.noEvents": "No student-visible events coming up.",
  "studentHub.eventsUnavailable": "Events are unavailable right now.",
  "studentHub.nickname": "Nickname",
  "studentHub.noNickname": "No nickname saved",
  "studentHub.changeName": "Change my name",
  "studentHub.nameWelcome": "What should we call you?",
  "studentHub.nameHelp": "Use your first name or a nickname. We will remember it in this browser.",
  "studentHub.nicknamePlaceholder": "Enter your name or nickname",
  "studentHub.continue": "Continue",
  "studentHub.quizCode": "Quiz code",
  "studentHub.boardCode": "Board code",
  "studentHub.classJoinHelp": "Enter the class code and PIN your teacher gave you.",
  "studentHub.quizJoinHelp": "Enter the code shown by your teacher.",
  "studentHub.collabJoinHelp": "Enter the board code shown in class.",
  "studentHub.classCodePlaceholder": "Enter class code",
  "studentHub.quizCodePlaceholder": "Enter quiz code",
  "studentHub.boardCodePlaceholder": "Enter board code",
  "studentHub.pinPlaceholder": "Enter PIN",
  "studentHub.codeRequired": "Please enter a code.",
  "studentHub.pinRequired": "Please enter the class PIN.",
  "studentHub.enterPin": "Enter the class PIN to continue.",
  "studentHub.joinFailed": "We could not join that class. Check the code and PIN and try again.",
  "studentHub.join": "Join",
  "studentHub.joining": "Joining…",
  "studentHub.editClassName": "Edit class name on this device",
  "calendar.shareWithStudents": "Share with students",
  "calendar.shareWithStudentsHelp": "Students with this class's access link can see this event in Student Hub. Events are private by default.",
  "demo.eyebrow": "Built by a teacher, for teachers",
  "demo.headline": "Plan. Teach. Inspire.",
  "demo.supporting": "Your classroom, all together with Elume.",
  "demo.description": "Bring lessons to life, organise your resources and follow your students' progress. Explore a ready-made class and see how Elume fits your day.",
  "demo.explore": "Explore the demo",
  "demo.continue": "Continue your demo",
  "demo.opening": "Opening your demo...",
  "demo.entrySlow": "Your secure demo is still being prepared. Keep this page open, or try again if it does not complete.",
  "demo.entryTemporary": "We could not open the demo just now. Please try again in a moment, or start your free trial.",
  "demo.tryAgain": "Try again",
  "demo.rateLimited": "Demo spaces are temporarily busy on this network. Try again in {{time}}. You can still start your free trial now.",
  "demo.trial": "Start my free 14-day trial",
  "demo.trialShort": "Start free trial",
  "demo.reassurance": "No signup needed to explore.",
  "demo.watch": "Watch Elume in action",
  "demo.openWorkspace": "Open your demo workspace",
  "demo.info": "This demo uses fictional students and prepared resources. Try the interactive tools; saved content and settings stay fixed. Start your free 14-day trial to create resources and build your own classes.",
  "demo.feature.whiteboard.title": "Interactive Whiteboard",
  "demo.feature.whiteboard.body": "Open sample notes and try the teaching tools.",
  "demo.feature.live.title": "Live Quiz & Collaboration",
  "demo.feature.live.body": "Try the Energy quiz and explore collaboration templates.",
  "demo.feature.insights.title": "Student Insights",
  "demo.feature.insights.body": "Explore assessment history, CAT4 comparisons and sample reports.",
  "demo.feature.planning.title": "Planning & Organisation",
  "demo.feature.planning.body": "See a prepared AAC tracker, seating plan and class resources.",
  "demo.previewLocked": "These classes show how your dashboard could look. Open Demo Class to try the interactive tools.",
  "demo.reportExamples": "Prepared examples using fictional students",
  "demo.reportExamplesHelp": "Real accounts generate comments from results and teacher-selected indicators for teacher review.",
  "demo.reportIndicators": "Fictional teacher-selected indicators",
  "demo.cat4.label": "Assessment tools",
  "demo.cat4.title": "CAT4 Insights",
  "demo.cat4.description": "Explore how CAT4 baseline ability and term results can be viewed together using fictional student data.",
  "demo.cat4.open": "Explore CAT4 demo",
  "demo.cat4.backAdmin": "Back to Class Admin",
  "demo.cat4.backInsights": "Back to Class Insights",
  "practice.demoAssessment.title": "Turn these quizzes into live classroom assessments",
  "practice.demoAssessment.body": "With an Elume teacher account, you can deliver these quizzes live to your class, check understanding and review students’ results. In this demo, choose any quiz and try it yourself.",
  "practice.demoAssessment.cta": "Start your free 14-day trial",
};

const gaeilge: Partial<Record<keyof typeof english, string>> = {
  "schoolResources.demoLabel": "Pacáiste Scoile Iomláine Elume",
  "schoolResources.packageTitle": "Tabhair acmhainní teagaisc comhroinnte chuig do scoil",
  "schoolResources.packageDescription": "Le Pacáiste Scoile Iomláine Elume, is féidir le múinteoirí tástálacha, bileoga oibre agus acmhainní teagaisc eile a roinnt ar fud ranna a scoile.",
  "schoolResources.trialEyebrow": "SAOR-TRIAIL SCOILE Sé SEACHTAIN",
  "schoolResources.trialTitle": "TRIAIL SCOILE SAOR IN AISCE Sé SEACHTAIN",
  "schoolResources.trialDescription": "Tabhair am d’fhoireann teagaisc le Elume a fhiosrú le chéile. Inis dúinn faoi do scoil agus rachaimid i dteagmháil leat chun na chéad chéimeanna eile a shocrú.",
  "schoolResources.trialBenefitOne": "Roinn acmhainní teagaisc úsáideacha ar fud ghrúpaí na scoile.",
  "schoolResources.trialBenefitTwo": "Fiosraigh uirlisí comhoibritheacha do cheachtanna agus do ranganna.",
  "schoolResources.trialBenefitThree": "Faigh réamheolas dírithe ar scoileanna ó fhoireann Elume.",
  "schoolResources.requestTrial": "Iarr triail scoile saor in aisce",
  "schoolResources.contactName": "Ainm an teagmhálaí",
  "schoolResources.optional": "roghnach",
  "schoolResources.publicNavigation": "Nascleanúint trialach scoile",
  "schoolResources.signIn": "Logáil isteach mar mhúinteoir",
  "schoolResources.showcaseAlt": "Léaráid d’acmhainní teagaisc á roinnt ar fud scoile",
  "schoolResources.illustrativeOnly": "Samplaí léiritheacha — ní ranníocaíochtaí múinteoirí is féidir a íoslódáil iad.",
  "schoolResources.enquiryTitle": "Inis dúinn faoi do scoil",
  "schoolResources.enquiryDescription": "Tabhair do shonraí teagmhála dúinn agus freagróimid maidir le triail do scoile a shocrú.",
  "schoolResources.name": "D’ainm",
  "schoolResources.workEmail": "Ríomhphost oibre",
  "schoolResources.schoolName": "Ainm na scoile",
  "schoolResources.role": "Do ról",
  "schoolResources.teacherCount": "Líon measta múinteoirí",
  "schoolResources.message": "Teachtaireacht",
  "schoolResources.website": "Suíomh gréasáin",
  "schoolResources.sendEnquiry": "Iarr do thriail scoile saor in aisce",
  "schoolResources.sending": "Ag seoladh…",
  "schoolResources.privacyNote": "Úsáidfimid na sonraí seo chun freagra a thabhairt ar iarratas trialach do scoile amháin. Ní chuirfear ar liosta margaíochta iad.",
  "schoolResources.privacyLink": "Fógra príobháideachais",
  "schoolResources.enquirySuccess": "Go raibh maith agat! Fuaireamar d’iarratas. Rachaidh Peter i dteagmháil leat chun rochtain trialach do scoile a shocrú.",
  "schoolResources.enquiryDeliveryFailure": "Níor ghlac an tseirbhís ríomhphoist le d’iarratas. Tá do shonraí fós anseo. Bain triail eile as nó seol ríomhphost chuig",
  "schoolResources.enquiryNotAccepted": "Níor ghlacamar leis an iarratas sin. Níor seoladh ríomhphost. Seice\u00e1il an fhoirm agus bain triail eile as, nó seol ríomhphost chuig",
  "schoolResources.enquiryDuplicateConflict": "Úsáideadh an eochair iarratais seo le sonraí eile cheana. Athlódáil an leathanach chun iarratas nua a thosú.",
  "schoolResources.enquiryValidationFailure": "Seice\u00e1il na réimsí aibhsithe agus bain triail eile as.",
  "schoolResources.fieldInvalid": "Seice\u00e1il an luach seo le do thoil.",
  "schoolResources.enquiryFailure": "Níorbh fhéidir linn d’iarratas a sheoladh anois. Tá do shonraí fós anseo. Bain triail eile as nó seol ríomhphost chuig",
  "schoolResources.enquiryRateLimited": "Fan thart ar {{minutes}} nóiméad sula seolfaidh tú iarratas eile. Is féidir leat ríomhphost a sheoladh chuig",
  "demo.restriction.changeHelp": "I do chuntas Elume féin, is féidir leat athruithe a dhéanamh agus iad a shábháil.",
  "demo.participantLimit.title": "T\u00e1 t\u00fa ag triail taispe\u00e1na Elume",
  "demo.participantLimit.inUse": "N\u00ed cheada\u00edonn an taispe\u00e1ntas seo ach rannph\u00e1irt\u00ed amh\u00e1in ag aon am, agus t\u00e1 an \u00e1it sin in \u00fas\u00e1id faoi l\u00e1thair.",
  "demo.participantLimit.collaborationDetail": "Le cuntas m\u00fainteora ioml\u00e1n, is f\u00e9idir leat suas le 12 sheomra comhoibrithe a chruth\u00fa do dhalta\u00ed le hobair ina n-aonar n\u00f3 i bhfoirne, a gcl\u00e1ir a athbhreithni\u00fa agus a gcuid oibre a sh\u00e1bh\u00e1il i N\u00f3ta\u00ed do ranga.",
  "demo.participantLimit.liveQuizDetail": "Le cuntas m\u00fainteora ioml\u00e1n, is f\u00e9idir leat tr\u00e1th na gceist beo a re\u00e1cht\u00e1il do rang ioml\u00e1n, freagra\u00ed a fheice\u00e1il agus tortha\u00ed a athbhreithni\u00fa.",
  "demo.participantLimit.backToHub": "Fill ar Mhol na nDalta\u00ed",
  "demo.participantLimit.startTrial": "Tosaigh triail saor in aisce",
  "demo.participantInfo.heading": "T\u00e1 t\u00fa ag ini\u00fachadh taispe\u00e1na Elume.",
  "demo.participantInfo.collaboration": "Ceada\u00edonn an seisi\u00fan seo rannph\u00e1irt\u00ed amh\u00e1in. Le cuntas m\u00fainteora ioml\u00e1n, is f\u00e9idir le do rang comhoibri\u00fa thar suas le 12 sheomra comhoibrithe.",
  "demo.participantInfo.liveQuiz": "Ceada\u00edonn an seisi\u00fan seo rannph\u00e1irt\u00ed amh\u00e1in. Le cuntas m\u00fainteora ioml\u00e1n, is f\u00e9idir leat tr\u00e1th na gceist beo a re\u00e1cht\u00e1il le do rang ioml\u00e1n.",
  "demo.participantInfo.teacherAction": "M\u00fainteoir? Tosaigh do thriail saor in aisce",
  "demo.participantInfo.studentAction": "Dalta? Tabhair cuairt ar Mhol na nDalta\u00ed",
  "home.languageFeature": "Ar fáil i nGaeilge freisin!",
  "home.languageAction": "Bain triail as Elume as Gaeilge",
  "home.languageSelected": "Tá Gaeilge roghnaithe agat",
  "home.welcome": "Fáilte go Elume",
  "home.platform": "An tArdán Teagaisc Uile-in-Aon",
  "home.eyebrow": "Cruthaithe ag múinteoir, do mhúinteoirí",
  "home.where": "Cá háit ar mhaith leat dul?",
  "home.choices": "Roghnaigh bealach isteach Elume",
  "home.studentEyebrow": "Mol na Mac Léinn",
  "home.studentTitle": "Mol na Mac Léinn",
  "home.studentDetail": "Do rang. Do chuid acmhainní.",
  "home.studentReassurance": "Níl cuntas mac léinn de dhíth.",
  "home.studentAction": "Is mac léinn mé",
  "home.teacherEyebrow": "Múinteoir",
  "home.teacherTitle": "Múinteoir",
  "home.teacherDetail": "Do ranganna agus uirlisí teagaisc.",
  "home.teacherAction": "Logáil isteach múinteora",
  "home.demoEyebrow": "Bain triail as an taispeántas",
  "home.demoTitle": "Bain triail as an taispeántas",
  "home.demoDetail": "Féach taobh istigh de Elume.",
  "home.demoReassurance": "Níl clárú de dhíth.",
  "home.demoAction": "Bain triail as an taispeántas",
  "home.benefit": "Pleanáil ceachtanna, teagasc go hidirghníomhach agus leanúint ar dhul chun cinn in aon spás oibre atá cairdiúil do mhúinteoir.",
  "home.trial": "Tosaigh do thriail saor 14 lá",
  "home.saveTime": "Sábháil Ama",
  "home.saveTimeCopy": "Cruthaigh acmhainní teagaisc i gceann nóiméad.",
  "home.engage": "Ranganna a Spreagadh",
  "home.engageCopy": "Cuir ceachtanna beo le huirlisí idirghníomhacha.",
  "home.organised": "Fan Eagraithe",
  "home.organisedCopy": "Coinnigh do ranganna agus acmhainní le chéile.",
  "home.schools": "Déanta do Scoileanna",
  "home.schoolsCopy": "Deartha do sheomraí ranga meánscoile.",
  "home.trialCopy": "Fiosraigh uirlisí teagaisc Elume le do ranganna féin.",
  "home.trialAction": "Tosaigh do thriail saor 14 lá",
  "home.footer": "Dearadh atá dírithe ar mhúinteoir · Tógtha in Éirinn",
  "demo.eyebrow": "CRUTHAITHE AG MUINTEOIR, DO MHUINTEOIRI",
  "demo.headline": "Pleanail. Muin. Spreag.",
  "demo.supporting": "Do sheomra ranga, le cheile in Elume.",
  "demo.description": "Cuir ceachtanna beo, eagraigh d'acmhainni agus lean dul chun cinn do dhaltai. Fiosraigh rang reidh agus feach conas a oireann Elume do do la.",
  "demo.explore": "Fiosraigh an taispeantas",
  "demo.continue": "Lean le do thaispeántas",
  "demo.opening": "Ag oscailt do thaispeantais...",
  "demo.entrySlow": "Tá do thaispeántas slán á ullmhú fós. Coinnigh an leathanach seo oscailte, nó bain triail eile as mura gcríochnaíonn sé.",
  "demo.entryTemporary": "Níorbh fhéidir linn an taispeántas a oscailt anois. Bain triail eile as i gceann nóiméid, nó tosaigh do thriail saor.",
  "demo.tryAgain": "Bain triail eile as",
  "demo.rateLimited": "Tá na spásanna taispeántais gnóthach ar an líonra seo faoi láthair. Bain triail eile as i gceann {{time}}. Is féidir leat do thriail saor a thosú anois fós.",
  "demo.trial": "Tosaigh mo thriail saor 14 la",
  "demo.trialShort": "Tosaigh triail saor",
  "demo.reassurance": "Níl claru de dhith chun fiosru.",
  "demo.watch": "Feach Elume i mbun oibre",
  "demo.openWorkspace": "Oscail do spás taispeantais",
  "demo.info": "Usáideann an taispeantas seo daltaí ficseanúla agus acmhainní ullmhaithe. Bain triail as na huirlisí idirghníomhacha; fanann an t-ábhar agus na socruithe socraithe. Tosaigh do thriail saor 14 lá chun acmhainní agus ranganna de do chuid féin a chruthú.",
  "demo.feature.whiteboard.title": "Clár Bán Idirghníomhach",
  "demo.feature.whiteboard.body": "Oscail nótaí samplacha agus bain triail as na huirlisí teagaisc.",
  "demo.feature.live.title": "Tráth na gCeist Beo agus Comhoibriú",
  "demo.feature.live.body": "Bain triail as an tráth na gceist Fuinnimh agus teimpléid chomhoibrithe.",
  "demo.feature.insights.title": "Léargais Daltaí",
  "demo.feature.insights.body": "Fiosraigh stair mheasúnaithe, comparáidí CAT4 agus samplaí tuairiscí.",
  "demo.feature.planning.title": "Pleanáil agus Eagrú",
  "demo.feature.planning.body": "Feach ar rianaire AAC, plean suíocháin agus acmhainní ranga ullmhaithe.",
  "demo.previewLocked": "Léiríonn na ranganna seo an chuma a d'fhéadfadh a bheith ar do dheais. Oscail Demo Class chun na huirlisí idirghníomhacha a úsáid.",
  "demo.reportExamples": "Samplaí ullmhaithe le daltaí ficseanúla",
  "demo.reportExamplesHelp": "Gineann fíorchuntais tráchtanna ó thorthaí agus táscairí roghnaithe ag múinteoir le hathbhreithniú.",
  "demo.reportIndicators": "Táscairí ficseanúla roghnaithe ag múinteoir",
  "demo.cat4.label": "Uirlisí measúnaithe",
  "demo.cat4.title": "Léargais CAT4",
  "demo.cat4.description": "Fiosraigh conas is féidir bunábaltacht CAT4 agus torthaí téarma a fheiceáil le chéile ag úsáid sonraí ficseanúla daltaí.",
  "demo.cat4.open": "Fiosraigh taispeántas CAT4",
  "demo.cat4.backAdmin": "Fill ar Riarachán Ranga",
  "demo.cat4.backInsights": "Fill ar Léargais Ranga",
  "practice.demoAssessment.title": "Déan measúnuithe beo ranga de na tráthanna ceiste seo",
  "practice.demoAssessment.body": "Le cuntas múinteora Elume, is féidir leat na tráthanna ceiste seo a chur ar fáil beo do do rang, tuiscint a sheiceáil agus torthaí na ndaltaí a athbhreithniú. Sa taispeántas seo, roghnaigh aon tráth na gceist agus bain triail as tú féin.",
  "practice.demoAssessment.cta": "Tosaigh do thriail saor 14 lá",
  "common.upload": "Uaslódáil",
  "aac.progress.individualTargetAria": "sprioc aonair",
  "aac.progress.followUpDateAria": "Dáta leantach",
  "aac.recalculateSuggestedDates": "Athríomh dátaí molta",
  "aac.trackThisClass": "Rianaigh an rang seo",
  "aac.progressSaveFirst": "Sábháil do rianaire chun leanúint ar aghaidh chuig dul chun cinn daltaí.",
  "aac.progressNext": "Ar aghaidh: rianaigh dul chun cinn gach dalta trí na céimeanna seo.",
  "aac.reviewStageRemoval": "Déan athbhreithniú ar bhaint céime",
  "aac.reviewStagesWithProgress": "Déan athbhreithniú ar chéimeanna a bhfuil dul chun cinn taifeadta acu",
  "aac.removeStagesHelp": "Folaíonn baint na gcéimeanna seo ón eangach ghníomhach iad. Fanfaidh seiceálacha isteach daltaí agus sonraí na gcéimeanna bainte sa stair inléite amháin.",
  "aac.keepEditing": "Lean ort ag eagrú",
  "aac.removeReviewedStages": "Bain na céimeanna athbhreithnithe agus coinnigh an stair",
  "aac.approvalSuccess": "Ceadaíodh an plean. Tá garspriocanna anois i bhféilirí do ranga múinteora agus gach imeachta; ní féidir le daltaí an plean seo a fheiceáil.",
  "aac.approvalStale": "Níl an t-athbhreithniú seo reatha a thuilleadh. Athnuaigh an dréacht sábháilte, déan athbhreithniú air arís, ansin ceadaigh é.",
  "aac.approvalFallback": "Níorbh fhéidir an plean a cheadú. Fanann do dhréacht shábháilte agus do phlean ceadaithe reatha gan athrú.",
  "aac.reviewedSourceRequirements": "Riachtanais agus dátaí foinse athbhreithnithe",
  "aac.requirement": "Riachtanas",
  "aac.noRequirementsReview": "Cuir riachtanais leis de láimh nó cruthaigh moltaí ó fhoinsí roghnaithe.",
  "aac.reviewAndSave": "Déan athbhreithniú agus sábháil",
  "aac.draftOnlyReviewHelp": "Sábháil rianairí AAC amháin. Níl moladh AI, ceadú ná foilsiú féilire ar fáil.",
  "aac.noRecordedAssumptions": "Níl aon toimhde taifeadta ann fós.",
  "aac.draftOnlyPublicationHelp": "Ní fhoilsíonn an Rianaire AAC céimeanna chuig aon fhéilire ná ní roinneann sé iad le daltaí.",
  "aac.approvalPublicationHelp": "Foilsíonn ceadú na garspriocanna céime sábháilte chuig féilirí do ranga múinteora agus gach imeachta. Ní roinneann sé an plean seo le daltaí.",
  "aac.approvedPlanLive": "Tá an plean ceadaithe beo i d’fhéilirí.",
  "aac.openClassCalendar": "Oscail féilire an ranga",
  "aac.saveBeforeApproval": "Sábháil d’eagarthóirí roimh cheadú ionas go mbeidh an t-athbhreithniú agus an comhartha reatha.",
  "aac.refreshBeforeApproval": "Athnuaigh an dréacht sábháilte sula gceadaíonn tú arís.",
  "aac.resolveWarningsBeforeApproval": "Réitigh na rabhaidh sceidealaithe thuas roimh cheadú.",
  "aac.approving": "Á cheadú…",
  "aac.approvePlan": "Ceadaigh an plean agus nuashonraigh mo fhéilirí",
  "aac.refreshReview": "Athnuaigh an t-athbhreithniú",
  "aac.progress.notStarted": "Gan tosú",
  "aac.progress.inProgress": "Ar bun",
  "aac.progress.readyForReview": "Réidh d’athbhreithniú an mhúinteora",
  "aac.progress.teacherReviewed": "Athbhreithnithe ag múinteoir",
  "aac.progress.discardConfirm": "An bhfuil fonn ort ionchur seiceála isteach nach bhfuil sábháilte a chur ar ceal? Coinneofar taifid shábháilte an fhreastalaí.",
  "aac.progress.loadFallback": "Níorbh fhéidir eangach an fhreastalaí a lódáil. Bain triail eile as gan taifid shábháilte a athrú.",
  "aac.progress.unsaved": "Athruithe gan sábháil",
  "aac.progress.saving": "Á shábháil…",
  "aac.progress.saved": "Sábháilte ar an bhfreastalaí",
  "aac.progress.notSaved": "Gan sábháil",
  "aac.progress.retry": "Bain triail eile as",
  "aac.progress.inputRetained": "Coinnítear d’ionchur.",
  "aac.progress.checkInStatus": "stádas seiceála isteach",
  "aac.progress.progress": "dul chun cinn",
  "aac.progress.saveCheckIn": "Sábháil seiceála isteach",
  "aac.progress.save": "Sábháil",
  "aac.progress.ariaLabel": "Dul chun cinn daltaí AAC",
  "aac.progress.heading": "Dul chun cinn daltaí",
  "aac.progress.privateHelp": "Seiceálacha isteach príobháideacha múinteora, sábháilte don rang seo ar an bhfreastalaí. Ní mharcálann dátaí obair mar obair chríochnaithe riamh.",
  "aac.progress.plan": "Plean",
  "aac.progress.reload": "Athlódáil eangach shábháilte",
  "aac.progress.loading": "Dul chun cinn sábháilte á lódáil…",
  "aac.progress.trackerHistory": "Stair rianaire",
  "aac.progress.current": "Reatha",
  "aac.progress.history": "Stair",
  "aac.progress.readOnly": "Rianaire stairiúil — inléite amháin. Coinnítear seiceálacha isteach roimhe seo.",
  "aac.progress.showArchived": "Taispeáin daltaí cartlannaithe a bhfuil stair acu",
  "aac.progress.noStages": "Sábháil céimeanna ainmnithe sa Phlean chun tosú ag rianú.",
  "aac.progress.student": "Dalta",
  "aac.progress.classDateToConfirm": "Dáta ranga le deimhniú",
  "aac.progress.teacherReviewedSaved": "athbhreithnithe ag múinteoir (sábháilte)",
  "aac.progress.archivedHistory": "Cartlannaithe — stair amháin",
  "aac.progress.checkIn": "seiceáil isteach",
  "aac.progress.close": "Dún",
  "aac.progress.classTarget": "Sprioc ranga",
  "aac.progress.notSet": "Gan socrú",
  "aac.progress.individualTargetHelp": "Sprioc aonair (roghnach; úsáidtear sprioc an ranga nuair atá sé bán)",
  "aac.progress.individualTarget": "Sprioc aonair",
  "aac.progress.effectiveTarget": "Sprioc éifeachtach",
  "aac.progress.teacherNote": "Nóta múinteora / an chéad ghníomh eile",
  "aac.progress.followUpDate": "Dáta leantach (roghnach)",
  "aac.progress.removedStages": "Céimeanna bainte — stair coinnithe",
  "aac.progress.noCheckIn": "Gan seiceáil isteach taifeadta",
  "teacherAdmin.billing.label": "Síntiús agus billeáil",
  "teacherAdmin.billing.individual": "Bainistigh do phlean, íocaíochtaí nó cealaigh do shíntiús.",
  "teacherAdmin.billing.schoolManaged": "Is í do scoil a bhainistíonn do rochtain Elume. Ní theastaíonn billeáil phearsanta fad is atá do bhallraíocht scoile gníomhach.",
  "teacherAdmin.billing.schoolAndPersonal": "Is í do scoil a bhainistíonn do rochtain Elume. Is féidir leat do shíntiús pearsanta a bhainistiú ar leithligh fós.",
  "teacherAdmin.billing.manage": "Bainistigh síntiús agus billeáil",
  "teacherAdmin.billing.managePersonal": "Bainistigh billeáil phearsanta",
  "teacherAdmin.billing.viewDetails": "Féach sonraí billeála",
  "language.english": "Béarla",
  "language.gaeilge": "Gaeilge",
  "public.aiTools": "Uirlisí faoi chumhacht IS do mhúinteoirí an lae inniu",
  "public.platform": "An tArdán Múinteoireachta Uile-i-Aon",
  "public.makeTeaching": "Déan an mhúinteoireacht", "public.makeTeachingAccent": "níos cliste, níos tapúla, níos gile.",
  "public.description": "Is ardán múinteoireachta uile-i-aon é Elume do mhúinteoirí iar-bhunscoile. Cruthaigh tráth na gceist, eagraigh acmhainní ranga, ullmhaigh ábhar scrúdaithe, reáchtáil uirlisí beo ranga agus bain úsáid as IS nuair is cabhair é — ar fad ó spás oibre amháin atá dírithe ar mhúinteoirí.",
  "public.saveTime": "Sábháil Am", "public.saveTimeDescription": "Cruthaigh tráth na gceist, acmhainní ranga agus ábhar múinteoireachta i gceann nóiméad.",
  "public.engageClasses": "Meall Ranganna", "public.engageClassesDescription": "Bain úsáid as uirlisí beo ranga agus tráth na gceist a oibríonn go hálainn ar an scáileán.",
  "public.stayOrganised": "Fan Eagraithe", "public.stayOrganisedDescription": "Coinnigh nótaí, tástálacha, comhaid ranga agus spásanna múinteoireachta in aon áit amháin.",
  "public.builtForSchools": "Deartha do Scoileanna", "public.builtForSchoolsDescription": "Gairmiúil, slán agus deartha do sheomraí ranga iar-bhunscoile fíora.",
  "public.trustedPilots": "Iontaofa ag múinteoirí píolótacha", "public.teacherFirst": "Dearadh atá dírithe ar mhúinteoirí", "public.builtInIreland": "Déanta in Éirinn", "public.seeInAction": "Féach ar Elume i mbun oibre",
  "public.mobileDescription": "Uirlisí níos cliste don phleanáil ceachta, tráth na gceist, tuairiscí, cláir bhána agus bainistiú ranga.",
  "login.welcomeBack": "Fáilte ar ais", "login.signIn": "Sínigh isteach", "login.email": "Ríomhphost", "login.password": "Focal faire", "login.forgotPassword": "Dearmad déanta agat ar d'fhocal faire?", "login.resetPassword": "Athshocraigh focal faire", "login.resetGuidance": "Cuir isteach do sheoladh ríomhphoist agus seolfaimid nasc athshocraithe má tá cuntas cláraithe agat.", "login.sendReset": "Seol nasc athshocraithe", "login.createAccount": "Cruthaigh cuntas", "login.defaultGuidance": "Sínigh isteach chun rochtain a fháil ar do ranganna agus ar do chuid uirlisí.", "login.schoolGuidance": "Sínigh isteach i do scoil ar Elume.", "login.teacherLogin": "Logáil isteach múinteora", "login.schoolLoading": "Sonraí síniú isteach do scoile á lódáil…", "login.schoolReady": "Sínigh isteach i {{school}} ar Elume.", "login.schoolUnavailable": "Níl rochtain na scoile ar fáil faoi láthair. Déan teagmháil le do scoil nó fill ar shíniú isteach Elume.", "login.goToElume": "Téigh chuig síniú isteach Elume", "login.schoolUnknown": "Níorbh fhéidir an scoil Elume seo a aimsiú.", "login.schoolBrandingError": "Níorbh fhéidir brandáil na scoile a lódáil faoi láthair. Is féidir leat síniú isteach in Elume fós.", "login.passwordPlaceholder": "Cuir isteach d'fhocal faire", "login.signingIn": "Ag síniú isteach…", "login.secureBilling": "Billeáil shlán", "login.paymentsPoweredBy": "Íocaíochtaí faoi chumhacht", "login.secureAccess": "Rochtain shlán", "login.teacherFirst": "Múinteoirí ar dtús", "login.schoolReadyBadge": "Réidh do scoileanna", "login.studentHub": "Mol na Mac Léinn", "login.studentsStart": "Tosaíonn mic léinn anseo", "login.studentHubHelp": "Ag dul isteach i rang? Oscail Mol na Mac Léinn chun cód agus UAP do ranga a chur isteach.", "login.openStudentHub": "Oscail Mol na Mac Léinn", "login.recovery": "Aisghabháil cuntais", "login.recoveryHelp": "Cuir isteach do sheoladh ríomhphoist agus seolfaimid nasc slán athshocraithe focal faire chugat.", "login.resetSent": "Má tá an seoladh ríomhphoist sin cláraithe, seoladh nasc athshocraithe focal faire.", "login.resetFailure": "Níorbh fhéidir an nasc athshocraithe a sheoladh", "login.footer": "Deartha chun cabhrú le múinteoirí am a shábháil, fanacht eagraithe agus ceachtanna a chur ag taitneamh.",
  "register.title": "Cruthaigh cuntas múinteora", "register.welcome": "Fáilte go Elume", "register.rolePrompt": "An múinteoir nó dalta thú?", "register.teacherChoice": "Is múinteoir mé", "register.teacherChoiceHelp": "Lean ar aghaidh chuig clárú múinteora.", "register.studentChoice": "Is dalta mé", "register.studentChoiceHelp": "Oscail Mol na Mac Léinn chun dul isteach i do rang.", "register.changeChoice": "Athraigh ról", "register.firstName": "Ainm", "register.lastName": "Sloinne", "register.schoolName": "Ainm na scoile", "register.confirmPassword": "Deimhnigh focal faire", "register.passwordHelp": "Bain úsáid as 8 gcarachtar ar a laghad, lena n-áirítear litir mhór, litir bheag agus uimhir.", "register.submit": "Cruthaigh cuntas", "register.loading": "Cuntas á chruthú…", "register.completeFields": "Líon isteach gach réimse, le do thoil.", "register.validEmail": "Cuir isteach seoladh ríomhphoist bailí.", "register.passwordMismatch": "Ní mheaitseálann na focail faire.", "register.success": "Cruthaíodh an cuntas. Deimhnigh do sheoladh ríomhphoist sula síníonn tú isteach.", "register.failure": "Níorbh fhéidir an cuntas a chruthú.", "register.passwordMinimum": "Caithfidh 8 gcarachtar ar a laghad a bheith san fhocal faire.", "register.passwordUppercase": "Caithfidh litir mhór amháin ar a laghad a bheith san fhocal faire.", "register.passwordLowercase": "Caithfidh litir bheag amháin ar a laghad a bheith san fhocal faire.", "register.passwordNumber": "Caithfidh uimhir amháin ar a laghad a bheith san fhocal faire.", "register.freeTrialHero": "Tosaigh le triail saor in aisce 14 lá", "register.heroTitle": "Tóg do spás oibre múinteora", "register.heroAccent": "agus cuir tús go tapa.", "register.heroDescription": "Cruthaigh do chuntas Elume, deimhnigh do sheoladh ríomhphoist, agus sínigh isteach ansin chun do phlean a roghnú. Ní roghnaítear plean ar an leathanach seo. Tosaíonn do thriail saor in aisce 14 lá nuair a chríochnaíonn tú socrú na billeála tar éis an deimhnithe.", "register.trialTitle": "Triail 14 Lá", "register.trialDescription": "Cláraigh ar dtús, deimhnigh do sheoladh ríomhphoist, agus roghnaigh do phlean ansin chun do thriail saor in aisce a thosú.", "register.earlyPricingTitle": "Praghsáil Luath-Uchtaitheora", "register.earlyPricingDescription": "Cláraigh anois ar €6 sa mhí nó €60 sa bhliain le linn ár gcéim luathsheolta.", "register.annualValueTitle": "An Luach Bliantúil Is Fearr", "register.annualValueDescription": "Fanfaidh cuntais bhliantúla a cheannaítear anois gníomhach go deireadh Mheán Fómhair 2027.", "register.builtForSchoolsDescription": "Dírithe ar mhúinteoirí, praiticiúil agus deartha do sheomraí ranga iar-bhunscoile fíora.", "register.earlyRates": "Rátaí luath-uchtaitheora", "register.mobileTrialDescription": "Cruthaigh do chuntas anois, deimhnigh do sheoladh ríomhphoist, agus roghnaigh do phlean chun do thriail saor in aisce 14 lá a thosú.", "register.formDescription": "Cláraigh anois, deimhnigh do sheoladh ríomhphoist, agus sínigh isteach ansin chun do phlean a roghnú.", "register.teacherSignup": "Clárú múinteora", "register.trialBadge": "triail saor in aisce 14 lá", "register.pricingBadge": "praghsáil luath-uchtaitheora", "register.afterVerify": "Tar éis duit do sheoladh ríomhphoist a dheimhniú agus síniú isteach, roghnóidh tú do phlean Elume agus tosóidh do thriail saor in aisce.", "register.pricingLead": "Is iad praghsanna luath-uchtaitheora ná", "register.monthlyPrice": "€6 sa mhí", "register.or": "nó", "register.yearlyPrice": "€60 sa bhliain", "register.launchOffer": "Tairiscint speisialta seolta:", "register.annualActiveLead": "fanfaidh cuntais bhliantúla a cheannaítear anois gníomhach go", "register.annualActiveDate": "deireadh Mheán Fómhair 2027", "register.stepRegister": "Cláraigh ar dtús", "register.stepVerify": "Deimhnigh ríomhphost", "register.stepPlan": "Roghnaigh plean", "register.firstNamePlaceholder": "Ainm", "register.lastNamePlaceholder": "Sloinne", "register.schoolPlaceholder": "Do scoil", "register.passwordPlaceholder": "Cruthaigh focal faire", "register.confirmPasswordPlaceholder": "Deimhnigh focal faire", "register.whatNext": "Cad a tharlaíonn ina dhiaidh sin", "register.nextHelp": "Deimhnigh do sheoladh ríomhphoist, sínigh isteach, roghnaigh do phlean, agus tosaigh ag úsáid Elume.", "register.secureSignup": "Clárú slán", "register.alreadyAccount": "An bhfuil cuntas agat cheana?", "register.footer": "Deartha chun cabhrú le múinteoirí am a shábháil, fanacht eagraithe agus ceachtanna a chur ag taitneamh.",
  "verifyEmail.title": "Deimhnigh do sheoladh ríomhphoist", "verifyEmail.help": "Táimid ag gníomhachtú do chuntas múinteora Elume.", "verifyEmail.back": "Ar ais chuig an síniú isteach", "verifyEmail.verifying": "Do sheoladh ríomhphoist á dheimhniú…", "verifyEmail.missingToken": "Tá comhartha deimhnithe in easnamh.", "verifyEmail.setup": "Deimhníodh an seoladh ríomhphoist. Táimid ag tabhairt chuig socrú Elume thú…", "verifyEmail.success": "Deimhníodh an seoladh ríomhphoist. Is féidir leat síniú isteach in Elume anois.", "verifyEmail.failure": "Níorbh fhéidir an seoladh ríomhphoist a dheimhniú.",
  "resetPassword.recovery": "Aisghabháil cuntais", "resetPassword.title": "Athshocraigh d'fhocal faire", "resetPassword.help": "Roghnaigh focal faire nua do do chuntas Elume. Ní gá duit síniú isteach ar an leathanach seo.", "resetPassword.new": "Focal faire nua", "resetPassword.confirm": "Deimhnigh focal faire", "resetPassword.invalid": "Tá an nasc athshocraithe seo neamhbhailí nó neamhiomlán.", "resetPassword.success": "Athshocraíodh an focal faire.", "resetPassword.failure": "Níorbh fhéidir an focal faire a athshocrú.", "resetPassword.submit": "Socraigh focal faire nua", "resetPassword.loading": "Focal faire á nuashonrú…", "resetPassword.back": "Ar ais chuig an síniú isteach",
  "schoolInvite.label": "Cuireadh scoile", "schoolInvite.title": "Glac le Elume", "schoolInvite.checking": "Cuireadh á sheiceáil…", "schoolInvite.invalid": "Tá an nasc cuireadh seo neamhbhailí nó neamhiomlán.", "schoolInvite.unavailable": "Níl an cuireadh seo ar fáil a thuilleadh.", "schoolInvite.existingPassword": "Focal faire Elume atá agat cheana", "schoolInvite.firstName": "Ainm", "schoolInvite.lastName": "Sloinne", "schoolInvite.confirmPassword": "Deimhnigh focal faire", "schoolInvite.accept": "Glac leis an gcuireadh", "schoolInvite.accepting": "Ag glacadh leis an gcuireadh…", "schoolInvite.back": "Ar ais chuig an síniú isteach", "schoolInvite.failure": "Níorbh fhéidir glacadh leis an gcuireadh.", "schoolInvite.defaultInviter": "Riarthóir do scoile", "schoolInvite.invitedTeacher": "Thug {{inviter}} cuireadh duit dul isteach i {{school}} ar Elume.", "schoolInvite.invitedAdmin": "Thug {{inviter}} cuireadh duit {{school}} a bhainistiú ar Elume.", "schoolInvite.sent": "Cuireadh seolta trí Elume", "schoolInvite.existingAccountHelp": "Tá cuntas Elume ann cheana don seoladh ríomhphoist seo. Cuir isteach an focal faire chun a dheimhniú gur leatsa an cuntas sula {{action}}.", "schoolInvite.joinSchool": "dtéann tú isteach sa scoil seo", "schoolInvite.joinSchoolAdmin": "dtéann tú isteach sa scoil seo mar Riarthóir Scoile", "schoolInvite.existingPasswordRequired": "Cuir isteach d'fhocal faire Elume atá agat cheana chun úinéireacht an chuntais a dheimhniú.", "schoolInvite.setupTeacher": "Socraigh do chuntas múinteora do {{school}}.", "schoolInvite.setupAdmin": "Socraigh do chuntas Riarthóra Scoile do {{school}}.", "schoolInvite.password": "Focal faire", "schoolInvite.successTeacher": "Tá tú anois i {{school}}. Sínigh isteach chun leanúint ar aghaidh.", "schoolInvite.successAdmin": "Tá tú anois i {{school}} mar Riarthóir Scoile. Sínigh isteach chun leanúint ar aghaidh.", "schoolInvite.acceptAdmin": "Glac leis mar Riarthóir Scoile",
  "common.back": "Ar ais",
  "common.close": "Dún",
  "common.cancel": "Cealaigh",
  "common.remove": "Bain",
  "common.save": "Sábháil",
  "common.saving": "Á shábháil…",
  "common.saveChanges": "Sábháil athruithe",
  "common.edit": "Cuir in eagar",
  "common.delete": "Scrios",
  "common.loading": "Á lódáil…",
  "common.open": "Oscail",
  "studentFiles.view": "Amharc",
  "studentFiles.opening": "Á oscailt…",
  "studentFiles.viewNewTab": "Osclaítear i gcluaisín nua é. Ní shábháiltear in Íoslódálacha é, ach úsáideann sé sonraí líonra agus taisce an bhrabhsálaí.",
  "studentFiles.viewAria": "Amharc ar {{filename}} i gcluaisín nua",
  "studentFiles.download": "Íoslódáil",
  "studentFiles.downloadAria": "Íoslódáil {{filename}}",
  "studentFiles.attachment": "Ceangaltán roinnte",
  "studentFiles.openAttachment": "Oscail an ceangaltán",
  "studentFiles.viewingNote": "Ní shábháiltear comhaid in Íoslódálacha nuair a fhéachtar orthu, ach úsáidtear sonraí líonra agus taisce an bhrabhsálaí. Osclaítear Amharc i gcluaisín nua.",
  "studentFiles.previewUnavailable": "Níl réamhamharc ar fáil don chineál comhaid seo. Úsáid Íoslódáil chun cóip a shábháil.",
  "studentFiles.openFailed": "Níorbh fhéidir an comhad seo a oscailt. Seiceáil do cheangal nó do rochtain agus bain triail eile as.",
  "common.refresh": "Athnuaigh",
  "error.sessionChanged": "Tá do sheisiún imithe in éag nó athraithe. Sínigh isteach arís le leanúint ar aghaidh. D’fhéadfadh sé seo tarlú ar chúiseanna slándála nó má shínigh tú isteach ar ghléas eile.",
  "error.chooseCategory": "Roghnaigh catagóir ar dtús",
  "error.enterCategoryName": "Cuir isteach ainm catagóire",
  "error.requestFallback": "Níorbh fhéidir an iarraidh sin a chríochnú. Bain triail eile as.",
  "nav.dashboard": "Deais",
  "nav.admin": "Riarachán",
  "nav.calendar": "Féilire",
  "nav.schoolAdmin": "Riarachán Scoile",
  "nav.platformAdmin": "Riarachán Ardáin",
  "nav.schoolResources": "Acmhainní Scoile",
  "nav.logout": "Logáil amach",
  "dashboard.createClass": "Cruthaigh Rang",
  "dashboard.createResources": "Cruthaigh Acmhainní",
  "dashboard.openClass": "Oscail rang",
  "dashboard.creatingClass": "Á chruthú…",
  "dashboard.dragToArrange": "Tarraing tíleanna chun iad a chur in ord.",
  "dashboard.viewTimetable": "féach ar an amchlár",
  "dashboard.timetable": "Tráthchlár",
  "dashboard.planner": "Pleanálaí Elume",
  "dashboard.tasksWeekView": "Tascanna • Amharc seachtaine",
  "dashboard.welcome": "Fáilte",
  "dashboard.loadingClasses": "Ranganna á lódáil…",
  "dashboard.noClasses": "Níl aon rang ann fós — cliceáil Cruthaigh Rang chun tosú.",
  "dashboard.archivedClasses": "Ranganna Cartlainne",
  "dashboard.manageClass": "Bainistigh rang",
  "dashboard.editClass": "Cuir rang in eagar",
  "dashboard.archiveClass": "Cuir rang sa chartlann",
  "dashboard.install": "Suiteáil Elume",
  "dashboard.maybeLater": "B’fhéidir níos déanaí",
  "planner.welcome": "Fáilte",
  "planner.tasksWeekView": "Tascanna • Amharc seachtaine",
  "createClass.title": "Cruthaigh Rang",
  "createClass.help": "Roghnaigh sraith, bliain, leibhéal agus dath tíle.",
  "createClass.stream": "Sraith",
  "createClass.year": "Bliain",
  "createClass.level": "Leibhéal",
  "createClass.subject": "Ábhar",
  "createClass.className": "Ainm an ranga",
  "createClass.juniorCycle": "An tSraith Shóisearach",
  "createClass.seniorCycle": "An tSraith Shinsearach",
  "createClass.transitionYear": "Idirbhliain (TY)",
  "createClass.sen": "Riachtanais Speisialta Oideachais",
  "createClass.tileColour": "Dath tíle",
  "createClass.customClass": "Rang Saincheaptha",
  "createClass.standardClass": "Úsáid sonraí caighdeánacha ranga",
  "createClass.customLineOne": "An chéad líne",
  "createClass.customLineTwo": "An dara líne",
  "createClass.customLineOnePlaceholder": "m.sh. Cúram Ainmhithe",
  "createClass.customLineTwoPlaceholder": "m.sh. Leibhéal 5",
  "class.backToDashboard": "Ar ais chuig an Deais",
  "class.teacher": "Múinteoir:",
  "class.group": "Grúpa:",
  "class.room": "Seomra:",
  "class.classSettings": "Socruithe ranga",
  "class.liveTeachingTools": "Uirlisí Beomhuinteoireachta",
  "class.liveTeachingHelp": "Téigh chuig do phríomhmhodhanna ceachta go tapa.",
  "class.whiteboard": "Clár Bán",
  "class.whiteboardSubtitle": "Oscail an clár múinteoireachta iomlán",
  "class.collaboration": "Comhoibriú",
  "class.collaborationSubtitle": "Clár beo do ghrúpaí daltaí",
  "class.liveQuiz": "Tráth na gCeist Beo",
  "class.liveQuizSubtitle": "Reáchtáil tráth na gceist nó pobalbhreith",
  "class.collabBoard": "Clár Comhoibrithe",
  "class.whiteboardTeachAnnotate": "Múin agus déan nótaí",
  "class.collabDrawTogether": "Tarraing le chéile",
  "class.liveQuizPoll": "Reáchtáil tráth na gceist nó pobalbhreith",
  "collab.startGuidance": "Cruthaigh seisiún, ansin tabhair cuireadh do do dhaltaí.",
  "collab.prepareBoard": "Ullmhaigh clár",
  "collab.exitPreparation": "Fág an t-ullmhúchán",
  "collab.savePreparedBoard": "Sábháil clár ullmhaithe",
  "collab.saveBoardChanges": "Sábháil na hathruithe",
  "collab.editBoardCopy": "Cuir cóip in eagar",
  "collab.preparationUnsaved": "Fág ullmhúchán an chláir? Caillfear athruithe nár sábháladh.",
  "collab.preparationLabel": "Ullmhúchán sealadach",
  "collab.preparedBoardHeading": "Sábháil an clár ullmhaithe seo",
  "collab.preparedBoardHelp": "Coinnigh an clár seo i Mo Chláir Shábháilte chun é a athoscailt nó a úsáid i seisiún comhoibrithe amach anseo.",
  "collab.snipSelectingArea": "Limistéar á roghnú…",
  "collab.takeAnotherSnip": "Tóg snip eile",
  "collab.clickHereToStart": "Cliceáil anseo le tosú",
  "collab.creatingSession": "Seisiún á chruthú…",
  "collab.decreaseRoomCount": "Laghdaigh líon na seomraí",
  "collab.increaseRoomCount": "Méadaigh líon na seomraí",
  "collab.roomCountRange": "Roghnaigh slánuimhir ó 1 go 12.",
  "collab.roomCountInvalid": "Cuir slánuimhir ó 1 go 12 isteach sula dtosaíonn seomraí briste amach.",
  "collab.leaveSession": "Fág an seisiún",
  "collab.leaving": "Ag fágáil…",
  "collab.leaveFailed": "Níorbh fhéidir linn an seisiún a fhágáil. Bain triail eile as.",
  "collab.leaveUnavailable": "Ní féidir an seisiún seo a fhágáil ón ngléas seo a thuilleadh. Tar isteach arís leis an gcód seisiúin má theastaíonn.",
  "teacherInvite.studentJoin": "Daltaí ag teacht isteach",
  "teacherInvite.collabTitle": "Clár Comhoibrithe",
  "teacherInvite.quizTitle": "Tráth na gCeist Beo",
  "teacherInvite.boardCode": "Cód cláir",
  "teacherInvite.quizCode": "Cód tráth na gceist",
  "teacherInvite.joinLink": "Nasc le teacht isteach",
  "teacherInvite.copyQuizCode": "Cóipeáil cód tráth na gceist",
  "teacherInvite.collabInstruction": "Scan le teacht isteach, nó oscail Mol na nDaltaí, roghnaigh Clár Comhoibrithe agus cuir isteach an cód cláir seo.",
  "teacherInvite.quizInstruction": "Scan le teacht isteach, nó oscail Mol na nDaltaí, roghnaigh Tráth na gCeist Beo agus cuir isteach an cód tráth na gceist seo.",
  "class.classAdmin": "Riarachán Ranga",
  "class.resources": "Acmhainní",
  "class.notes": "Nótaí",
  "class.tests": "Scrúduithe",
  "class.quizzes": "Tráth na gCeist",
  "class.examPapers": "Páipéir Scrúdaithe",
  "class.videos": "Físeáin",
  "whiteboard.savedVideos": "Físeáin ranga shábháilte",
  "whiteboard.handMove": "Lámh / Bog",
  "whiteboard.penShortcut": "Peann",
  "whiteboard.savedVideosHelp": "Físeáin shábháilte sa bhrabhsálaí seo don rang reatha.",
  "whiteboard.videoPickerHelp": "Roghnaigh físeán sábháilte don rang seo nó greamaigh nasc YouTube.",
  "whiteboard.noSavedVideos": "Níl aon fhíseán sábháilte don rang seo sa bhrabhsálaí seo fós.",
  "whiteboard.noClassVideoContext": "Tá físeáin ranga shábháilte ar fáil nuair a osclaítear an Clár Bán ó rang. Is féidir leat nasc YouTube a ghreamú thíos fós.",
  "whiteboard.checkingClassAccess": "Ag seiceáil rochtana ar an rang seo sula dtaispeántar físeáin shábháilte…",
  "whiteboard.classVideoAccessRequired": "Ní féidir físeáin ranga shábháilte a thaispeáint mar níl rochtain ag an gcuntas seo ar an rang. Is féidir leat nasc YouTube a ghreamú thíos fós.",
  "whiteboard.savedVideosLoadError": "Níorbh fhéidir físeáin ranga shábháilte a lódáil ón mbrabhsálaí seo. Bain triail eile as.",
  "whiteboard.unsupportedSavedVideo": "Ní físeán YouTube tacaithe é an nasc sábháilte seo. Nuashonraigh é i bhFíseáin sula n-osclaítear é anseo.",
  "whiteboard.selectVideo": "Roghnaigh",
  "whiteboard.audioTooLarge": "Is féidir le comhaid fuaime a bheith suas le 100 MB. Roghnaigh comhad níos lú agus bain triail eile as.",
  "whiteboard.audioUploadLimit": "Diúltaíodh don uaslódáil fuaime sula bhféadfadh Elume í a phróiseáil. Is féidir le comhaid fuaime a bheith suas le 100 MB; bain triail eile as le comhad níos lú.",
  "whiteboard.audioStorageLimit": "Níorbh fhéidir an comhad fuaime seo a uaslódáil mar tá teorainn stórála Elume sroichte. Scrios roinnt comhad sula mbainfidh tú triail eile as.",
  "whiteboard.audioUploadNetwork": "Níor chríochnaigh an uaslódáil fuaime. Seiceáil do cheangal agus bain triail eile as; d’fhéadfadh go dtógfadh comhaid níos mó níos faide le huaslódáil.",
  "whiteboard.audioFileLimit": "Is féidir le comhaid fuaime a bheith suas le 100 MB. Tá do liúntas cuntais ar leithligh.",
  "whiteboard.audioStorageSummary": "Stóras: {used} úsáidte as {limit}; {available} ar fáil.",
  "whiteboard.uploadingAudio": "Ag uaslódáil fuaime…",
  "class.links": "Naisc",
  "class.quickTools": "Uirlisí Tapa Seomra Ranga",
  "class.quickToolsSubtitle": "Uirlisí tapa don seomra ranga",
  "class.randomName": "Ainm Randamach",
  "class.seatingPlan": "Plean Suíocháin",
  "class.timer": "Coimeádóir ama",
  "class.teamGenerator": "Gineadóir Foirne",
  "class.externalResources": "Acmhainní Seachtracha",
  "class.studentAccess": "Rochtain daltaí",
  "class.studentAccessHelp": "Scan le hamharc inléite amháin a oscailt.",
  "class.copyLink": "Cóipeáil nasc",
  "class.classCode": "Cód ranga",
  "class.newAnnouncement": "Fógra Nua",
  "class.calendarAlerts": "Foláirimh féilire",
  "notes.title": "N\u00f3ta\u00ed",
  "notes.description": "Eagraigh PDFanna, sleamhn\u00e1in, bileoga oibre, fuaim agus comhaid an tseomra ranga.",
  "notes.allCategories": "Gach Catag\u00f3ir",
  "notes.audioLibrary": "Leabharlann Fuaime",
  "notes.upload": "Uasl\u00f3d\u00e1il",
  "notes.search": "Cuardaigh catag\u00f3ir n\u00f3 comhaid...",
  "notes.categories": "Catag\u00f3ir",
  "notes.categoryHelp": "T\u00edleanna topaice ar st\u00edl na deaise le rochtain thapa sa seomra ranga",
  "notes.addCategoryUpload": "+ Cuir Catag\u00f3ir / Uasl\u00f3d\u00e1il Leis",
  "notes.loadingWorkspace": "Sp\u00e1s oibre n\u00f3ta\u00ed \u00e1 l\u00f3d\u00e1il...",
  "notes.noCategories": "N\u00edl aon chatag\u00f3ir ann f\u00f3s",
  "notes.startLibrary": "Tosaigh Leabharlann N\u00f3ta\u00ed",
  "notes.category": "Catag\u00f3ir",
  "notes.addFiles": "Cuir comhaid leis",
  "notes.deleteCategory": "Scrios catag\u00f3ir",
  "notes.loadingFiles": "Comhaid \u00e1 l\u00f3d\u00e1il...",
  "notes.noFiles": "N\u00edl aon chomhad sa chatag\u00f3ir seo f\u00f3s",
  "notes.uploadAudio": "Uasl\u00f3d\u00e1il fuaime",
  "notes.uploadFiles": "Uasl\u00f3d\u00e1il comhad",
  "notes.uploaded": "Uasl\u00f3d\u00e1ilte:",
  "notes.deleteFile": "Scrios comhad",
  "notes.reopenWhiteboard": "Athoscail sa Chl\u00e1r B\u00e1n",
  "notes.uploadNotes": "Uasl\u00f3d\u00e1il n\u00f3ta\u00ed",
  "notes.uploadHelp": "Roghnaigh catag\u00f3ir at\u00e1 ann cheana n\u00f3 cruthaigh ceann nua roimh uasl\u00f3d\u00e1il.",
  "notes.existingCategory": "Catag\u00f3ir at\u00e1 ann cheana",
  "notes.createNewCategory": "Cruthaigh catag\u00f3ir nua",
  "notes.newCategoryName": "Ainm catag\u00f3ire nua",
  "notes.chooseCategory": "Roghnaigh catag\u00f3ir...",
  "notes.audioFiles": "Comhaid fuaime",
  "notes.files": "Comhaid",
  "notes.chooseAudioFiles": "Roghnaigh comhaid fuaime",
  "notes.chooseFiles": "Roghnaigh comhaid",
  "notes.uploading": "Ag uasl\u00f3d\u00e1il...",
  "notes.resourcePreparation": "Ullmh\u00fach\u00e1n acmhainne",
  "notes.prepareResource": "D\u00e9an an acmhainn seo r\u00e9idh do Elume?",
  "notes.uploadOriginal": "Uasl\u00f3d\u00e1il an Bhunleagain",
  "notes.convertAndUpload": "Tiontaigh & Uasl\u00f3d\u00e1il",
  "calendar.title": "F\u00e9ilire",
  "calendar.classTitle": "F\u00e9ilire Ranga",
  "calendar.newEvent": "+ Imeacht Nua",
  "calendar.filterEvents": "Scag imeachta\u00ed",
  "calendar.allEvents": "Gach imeacht",
  "calendar.globalOnly": "Domhanda amh\u00e1in",
  "calendar.selectedClass": "Rang roghnaithe",
  "calendar.monthView": "Amharc M\u00edosa",
  "calendar.previous": "Roimhe seo",
  "calendar.today": "Inniu",
  "calendar.next": "Ar aghaidh",
  "calendar.loading": "Ag l\u00f3d\u00e1il...",
  "calendar.eventTitle": "Teideal an imeachta",
  "calendar.description": "Cur s\u00edos",
  "calendar.date": "D\u00e1ta",
  "calendar.time": "Am",
  "calendar.endTime": "Am cr\u00edche",
  "calendar.allDay": "An l\u00e1 ar fad",
  "calendar.eventType": "Cine\u00e1l imeachta",
  "calendar.createEvent": "Cruthaigh imeacht",
  "calendar.editEvent": "Cuir imeacht in eagar",
  "calendar.saveEvent": "S\u00e1bh\u00e1il imeachta",
  "calendar.deleteEvent": "Scrios imeacht",
  "liveQuiz.title": "Tr\u00e1th na gCeist Beo",
  "liveQuiz.backToClass": "Ar ais chuig an rang",
  "liveQuiz.questions": "Ceisteanna",
  "liveQuiz.session": "Seisi\u00fan",
  "liveQuiz.joined": "Tagtha isteach",
  "liveQuiz.state": "Staid",
  "liveQuiz.setup": "Socr\u00fa tr\u00e1th na gceist",
  "liveQuiz.chooseSavedQuiz": "Roghnaigh tr\u00e1th na gceist s\u00e1bh\u00e1ilte",
  "liveQuiz.classQuizzes": "Tr\u00e1th na gCeist Ranga",
  "liveQuiz.mainCollection": "Pr\u00edomhbhaili\u00fach\u00e1n Tr\u00e1th na gCeist",
  "liveQuiz.quickCustom": "Saincheaptha Tapa",
  "liveQuiz.sessionTitle": "Teideal an tseisi\u00fain",
  "liveQuiz.sessionOptions": "Roghanna seisi\u00fain",
  "liveQuiz.autoPlay": "Uathsheinm",
  "liveQuiz.shuffleQuestions": "Measc ceisteanna",
  "liveQuiz.useTimer": "\u00das\u00e1id amad\u00f3ir",
  "liveQuiz.autoEnd": "Cr\u00edochnaigh go huathoibr\u00edoch nuair a fhreagra\u00edtear iad uile",
  "liveQuiz.secondsPerQuestion": "Soicind in aghaidh na ceiste",
  "liveQuiz.questionsLoaded": "Ceisteanna l\u00f3d\u00e1ilte",
  "liveQuiz.createSession": "Cruthaigh seisi\u00fan beo",
  "liveQuiz.creatingSession": "Seisi\u00fan \u00e1 chruth\u00fa...",
  "liveQuiz.customQuestions": "Ceisteanna saincheaptha",
  "liveQuiz.addQuestion": "+ Cuir ceist leis",
  "liveQuiz.question": "Ceist",
  "liveQuiz.remove": "Bain",
  "liveQuiz.prompt": "Treoir",
  "liveQuiz.correctAnswerOptional": "Roghnach: marc\u00e1il freagra ceart. F\u00e1g folamh \u00e9 do mhodh pobalbhreithe.",
  "liveQuiz.start": "Tosaigh",
  "liveQuiz.nextQuestion": "Ar aghaidh",
  "liveQuiz.endSession": "Cuir deireadh leis an seisi\u00fan",
  "liveQuiz.refreshResults": "Athnuaigh tortha\u00ed",
  "liveQuiz.yourQuizzes": "Do Thr\u00e1th na gCeist",
  "liveQuiz.elumeQuizLibrary": "Leabharlann Tr\u00e1th na gCeist Elume",
  "liveQuiz.elumeLibraryHelp": "Brabhs\u00e1il tr\u00e1thanna ceist Elume de r\u00e9ir leibh\u00e9il, \u00e1bhair agus topaice. Fanann an bunthr\u00e1th ceist gan athr\u00fa nuair a thosa\u00edonn t\u00fa ceann.",
  "liveQuiz.allLevels": "Gach leibh\u00e9al",
  "liveQuiz.level": "Leibh\u00e9al",
  "liveQuiz.subject": "\u00c1bhar",
  "liveQuiz.subjects": "\u00e1bhair",
  "liveQuiz.topic": "Topaic",
  "liveQuiz.topics": "topaic",
  "liveQuiz.quizzes": "tr\u00e1th na gceist",
  "liveQuiz.loadingLibrary": "Leabharlann Tr\u00e1th na gCeist Elume \u00e1 l\u00f3d\u00e1il...",
  "liveQuiz.preview": "R\u00e9amhamharc",
  "liveQuiz.startLiveQuiz": "Tosaigh Tr\u00e1th na gCeist Beo",
  "liveQuiz.correctAnswer": "Freagra ceart",
  "liveQuiz.explanation": "M\u00edni\u00fa",
  "liveQuiz.close": "D\u00fan",
  "quizzes.title": "Tr\u00e1th na gCeist",
  "quizzes.aiBuilder": "T\u00f3g\u00e1la\u00ed Tr\u00e1th na gCeist IS",
  "quizzes.generateFromPdf": "Gin \u00f3 PDF",
  "quizzes.newManual": "+ Tr\u00e1th na gCeist L\u00e1mhdh\u00e9anta Nua",
  "quizzes.openLiveQuiz": "Oscail Tr\u00e1th na gCeist Beo",
  "quizzes.questions": "Ceisteanna",
  "quizzes.empty": "N\u00edl aon tr\u00e1th na gceist ann f\u00f3s",
  "quizzes.createManually": "Cruthaigh de l\u00e1imh",
  "quizzes.library": "Leabharlann tr\u00e1th na gceist",
  "quizzes.preview": "R\u00e9amhamharc",
  "quizzes.editQuiz": "Cuir tr\u00e1th na gceist in eagar",
  "quizzes.newQuiz": "Tr\u00e1th na gCeist Nua",
  "quizzes.quizTitle": "Teideal an tr\u00e1th na gceist",
  "quizzes.category": "Catag\u00f3ir",
  "quizzes.descriptionOptional": "Cur s\u00edos gairid (roghnach)",
  "quizzes.editQuestion": "Cuir ceist in eagar",
  "quizzes.addQuestion": "Cuir ceist leis",
  "quizzes.questionText": "T\u00e9acs na ceiste",
  "quizzes.correctAnswer": "Freagra ceart",
  "schoolResources.title": "Acmhainn\u00ed Scoile",
  "schoolResources.sharedTeachingResources": "Acmhainn\u00ed m\u00fainteoireachta comhroinnte",
  "schoolResources.description": "Acmhainn\u00ed roinnte tr\u00ed do ranna. Crutha\u00edonn \u00fas\u00e1id acmhainne c\u00f3ip nua duit i gc\u00f3na\u00ed.",
  "schoolResources.allDepartments": "Mo ranna uile",
  "schoolResources.allTypes": "Gach cine\u00e1l acmhainne",
  "schoolResources.collaborationBoards": "Cl\u00e1ir Chomhoibrithe",
  "schoolResources.quiz": "Tr\u00e1th na gCeist",
  "schoolResources.collaborationBoard": "Cl\u00e1r Comhoibrithe",
  "schoolResources.sharedBy": "Roinnte ag",
  "schoolResources.sharedByYou": "Roinnte agat",
  "schoolResources.addToMyQuizzes": "Cuir le Mo Thr\u00e1th na gCeist",
  "schoolResources.useForCollaboration": "\u00das\u00e1id le haghaidh Comhoibrithe",
  "schoolResources.empty": "N\u00edl aon acmhainn chomhroinnte i do ranna f\u00f3s.",
  "schoolResources.emptyTitle": "Is mór an tairbhe a bhaineann le roinnt",
  "schoolResources.emptyBody": "Roinn acmhainn úsáideach le do chomhghleacaithe chun bailiúchán do rannóige a chur ar bun.",
  "schoolResources.addQuiz": "Cuir tr\u00e1th na gceist le rang",
  "schoolResources.chooseClass": "Roghnaigh do rang",
  "schoolResources.allGroups": "Mo ghrúpaí uile",
  "schoolResources.moreGroups": "Tuilleadh grúpaí",
  "schoolResources.shareResource": "Roinn acmhainn",
  "schoolResources.findResources": "Aimsigh, roinn agus athúsáid acmhainní ó do chomhghleacaithe.",
  "schoolResources.allResources": "Gach acmhainn",
  "schoolResources.searchInGroup": "Cuardaigh acmhainní {{group}}…",
  "schoolResources.resources": "acmhainní",
  "schoolResources.notes": "Nótaí",
  "schoolResources.worksheets": "Bileoga oibre",
  "schoolResources.tests": "Trialacha",
  "schoolResources.quizzes": "Tráth na gceist",
  "schoolResources.searchResources": "Cuardaigh acmhainní",
  "schoolResources.yearGroup": "Grúpa bliana",
  "schoolResources.allYears": "Gach bliain",
  "schoolResources.noMembership": "Beidh d’acmhainní comhroinnte le feiceáil anseo nuair a chuirfidh riarthóir do scoile le roinn thú.",
  "schoolResources.noMatches": "Níl aon acmhainn ag teacht leis na scagairí seo.",
  "schoolResources.preview": "Réamhamharc",
  "schoolResources.addToClass": "Cuir le mo rang",
  "schoolResources.shareWithColleagues": "Roinn le comhghleacaithe",
  "schoolResources.unshare": "Díroinn",
  "schoolResources.resourceType": "Cineál acmhainne",
  "schoolResources.shareGroup": "Grúpa",
  "schoolResources.chooseGroup": "Roghnaigh grúpa údaraithe",
  "schoolResources.resourceDescription": "Cur síos (roghnach)",
  "schoolResources.resourceTitle": "Teideal",
  "schoolResources.onlyThisFile": "Ní roinntear ach an comhad seo. Ní roinntear obair daltaí ná fillteáin ranga.",
  "schoolResources.studentVisibility": "Beidh an chóip seo ar fáil sa rang roghnaithe. Cinntigh nach bhfuil freagraí múinteora ná scéim mharcála inti sula gcuirtear leis í.",
  "schoolResources.close": "Dún",
  "schoolResources.cancel": "Cealaigh",
  "schoolResources.adding": "Á chur leis…",
  "schoolResources.sharing": "Á roinnt…",
  "schoolResources.existingResource": "Nóta, bileog oibre nó triail atá ann cheana",
  "schoolResources.chooseOwnedResource": "Roghnaigh acmhainn ar leatsa í",
  "schoolResources.class": "Rang",
  "schoolResources.test": "Triail",
  "schoolResources.notesWorksheet": "Nótaí / Bileog oibre",
  "schoolResources.sharedSuccess": "Tá d’acmhainn roinnte leis an ngrúpa seo anois.",
  "schoolResources.unsharedSuccess": "Níl d’acmhainn roinnte a thuilleadh.",
  "schoolResources.quizCopied": "Cuireadh “{{title}}” le tráth na gceist do ranga.",
  "schoolResources.resourceCopied": "Cuireadh “{{title}}” le {{className}}.",
  "schoolResources.breadcrumbHome": "Baile",
  "schoolResources.breadcrumbResources": "Acmhainní comhroinnte",
  "schoolResources.previewTitle": "Réamhamharc: {{title}}",
  "schoolResources.downloadFile": "Íoslódáil {{filename}}",
  "schoolResources.collabPreview": "Réamhamharc ar an gclár comhoibrithe",
  "schoolResources.boardItems": "{{count}} mír ar an gclár",
  "classAdmin.title": "Riarachán Ranga",
  "classAdmin.students": "Daltaí",
  "classAdmin.assessments": "Measúnuithe",
  "classAdmin.results": "Torthaí",
  "classAdmin.insights": "Léargais",
  "classAdmin.addStudent": "Cuir Dalta Leis",
  "classAdmin.rosterHelp": "Cuir céadainmneacha amháin leis. Bain úsáid as nótaí le haghaidh meabhrúcháin thapa don mhúinteoir.",
  "classAdmin.demoRosterLocked": "I do chuntas Elume féin, is féidir leat do dhaltaí a chur leis agus a bhainistiú. Úsáideann an taispeántas seo rang réamhullmhaithe ionas gur féidir leat torthaí agus dul chun cinn a iniúchadh.",
  "classAdmin.demoClassReportRestriction": "Ligeann an taispeántas seo duit Léargais Ranga agus tuairiscí dalta aonair réamhullmhaithe a iniúchadh. I do chuntas Elume féin, is féidir leat tuairisc a oscailt do do rang iomlán freisin.",
  "classAdmin.refreshStudentLink": "Athnuaigh nasc an dalta",
  "classAdmin.copyStudentLink": "Cóipeáil nasc an dalta",
  "classAdmin.studentLinkReady": "Tá nasc rochtana dalta réidh don rang seo.",
  "classAdmin.firstNamePlaceholder": "Céadainm (m.sh. Aoife)",
  "classAdmin.addMultiple": "Cuir daltaí iolracha leis",
  "classAdmin.bulkHelp": "Greamaigh céadainmneacha (ceann in aghaidh na líne nó scartha le camóga).",
  "classAdmin.addStudents": "Cuir daltaí leis",
  "classAdmin.testTitle": "Teideal scrúdaithe",
  "classAdmin.dateOptional": "Dáta (roghnach)",
  "classAdmin.enteredAbsent": "Curtha isteach / as láthair",
  "classAdmin.currentAverage": "Meán reatha",
  "classAdmin.absent": "As láthair",
  "classAdmin.actions": "Gníomhartha",
  "classAdmin.scorePlaceholder": "0-100",
  "classAdmin.yes": "Tá",
  "classAdmin.no": "Níl",
  "classAdmin.clearResult": "Glan toradh",
  "classAdmin.deleteStudent": "Scrios dalta?",
  "classAdmin.deleting": "Ag scriosadh…",
  "classAdmin.deleteStudentAction": "Scrios dalta",
  "classAdmin.performanceOverTime": "Feidhmíocht le himeacht ama",
  "classAdmin.performanceHelp": "Taispeántar scóir in ord croineolaíoch na measúnuithe.",
  "classAdmin.chartLegend": "Eochair cairte",
  "classAdmin.student": "Dalta",
  "classAdmin.chartDescription": "Feidhmíocht dalta agus meán an ranga le himeacht ama",
  "classAdmin.atRisk": "Daltaí i mbaol",
  "classAdmin.noAtRisk": "Níl aon dalta i mbaol marcáilte.",
  "classAdmin.rankings": "Ranguithe",
  "classAdmin.noResults": "Níl aon toradh ann fós.",
  "classAdmin.aiComments": "Tuairimí tuairisce AI",
  "classAdmin.aiCommentsHelp": "Cruthaigh tuairimí tuairisce do dhaltaí aonair ag úsáid torthaí agus táscairí roghnaithe ag an múinteoir.",
  "classAdmin.generating": "Á gcruthú…",
  "classAdmin.generateComment": "Cruthaigh tuairim",
  "classAdmin.commentPlaceholder": "Beidh an tuairim chruthaithe le feiceáil anseo…",
  "classAdmin.noHistory": "Níl aon stair ar fáil fós.",
  "classAdmin.atRiskRule": "Riail: meán < 50 nó caillte ≥ 2",
  "classAdmin.rankingHelp": "An ceann is déanaí i gcomparáid leis an meán foriomlán (±3% = saighead)",
  "classAdmin.name": "Ainm",
  "classAdmin.average": "Meán",
  "classAdmin.latest": "Is déanaí",
  "classAdmin.taken": "Déanta",
  "classAdmin.missed": "Caillte",
  "classAdmin.report": "Tuairisc",
  "classAdmin.openStudentReport": "Oscail tuairisc an dalta",
  "classAdmin.short": "Gearr",
  "classAdmin.medium": "Meánach",
  "classAdmin.long": "Fada",
  "classAdmin.indicators": "Táscairí",
  "classAdmin.finalSignOff": "Críochshíniú",
  "classAdmin.signOffPlaceholder": "m.sh. Bain taitneamh as sos an tsamhraidh.",
  "classAdmin.comment": "Tuairim",
  "classAdmin.copyComment": "Cóipeáil tuairim",
  "ai.inputLabel": "Le cúnamh AI",
  "ai.outputLabel": "Dréacht cruthaithe ag AI",
  "aac.step3": "Céim 3 de 4",
  "aac.step4": "Céim 4 de 4",
  "aac.teachingTime": "Cathain a oibreoidh do rang air seo?",
  "aac.setupNew": "Socraigh do rianaire AAC nua",
  "aac.briefTitle": "Teideal an choimre",
  "aac.chooseSpecification": "Roghnaigh do shonraíocht",
  "aac.step2": "Céim 2 de 4",
  "aac.navStart": "1. Tosaigh",
  "aac.navFound": "2. An méid a fuaireamar",
  "aac.navTime": "3. Am teagaisc",
  "aac.navPlan": "4. Plean molta",
  "aac.sourceFindingsHelp": "Is torthaí ón bhfoinse iad seo duit le hathbhreithniú, ní cinntí oifigiúla iad ná plean cruthaithe ag AI.",
  "aac.keyDates": "Príomhdhátaí",
  "aac.uploadFormats": "PDF nó DOCX amháin, 15 MB ar a mhéad. Teastaíonn leagan inléite mar théacs nó iontráil láimhe do PDFanna scanta/doléisithe.",
  "aac.chooseFile": "Roghnaigh comhad",
  "aac.sourceFile": "Comhad foinse AAC",
  "aac.noSources": "Níl aon fhoinse ann fós. Tá dréachtú láimhe ar fáil fós.",
  "aac.setupManually": "Socraigh de láimh",
  "aac.manualReady": "Tá pleanáil láimhe réidh. Cuir príomhdhátaí, am teagaisc agus céimeanna leis nuair atá tú réidh.",
  "aac.briefUploaded": "Coimre uaslódáilte ✓",
  "aac.reviewExtracted": "Déan athbhreithniú ar chodanna bainte as an bhfoinse",
  "aac.removeSourceQuestion": "Bain an fhoinse seo?",
  "aac.createBeforeSources": "Críochnaigh cruthú an rianaire AAC nua sula gcuireann tú foinsí leis.",
  "aac.removeSourceFallback": "Coinníodh an fhoinse. Bain triail eile as.",
  "aac.documentType": "Cineál cáipéise",
  "aac.earlierDocuments": "Coimeádtar cáipéisí níos luaithe le do rianta sábháilte, ach níl aon cheann roghnaithe ná in úsáid anseo.",
  "aac.specificationBrief": "Sonraíocht / coimre",
  "aac.purpose.fifth_year_calendar": "Féilire scoile na Cúigiú Bliana",
  "aac.purpose.sixth_year_calendar": "Féilire scoile na Séú Bliana",
  "aac.chooseUploadSource": "Roghnaigh foinse le huaslódáil",
  "aac.removeSource": "Bain foinse",
  "aac.briefUploadedReview": "Coimre uaslódáilte ✓. Déan athbhreithniú ar an méid a fuaireamar nuair atá tú réidh.",
  "aac.extractionFailed": "Uaslódáladh an cháipéis ach níorbh fhéidir í a bhaint. Is féidir sonraí a chur isteach de láimh.",
  "aac.uploadFallback": "Bain úsáid as PDF nó DOCX atá inléite mar théacs faoi 15 MB, nó cuir eolas isteach de láimh.",
  "aac.getStarted": "Cuirimis tús leis…",
  "aac.examinationYear": "Bliain scrúdaithe nua",
  "aac.examinationYearOptional": "Bliain scrúdaithe (roghnach)",
  "aac.createTracker": "Cruthaigh rianaire AAC",
  "aac.currentProjectStage": "Céim reatha tionscadail nua",
  "aac.fifthYear": "Cúigiú Bliain",
  "aac.sixthYear": "Séú Bliain",
  "aac.alreadyUnderway": "Ar bun cheana",
  "aac.defaultMinutesHelp": "Is é 30 nóiméad sa tseachtain an réamhshocrú is féidir a chur in eagar. Uaslódáil do choimre chun athbhreithniú a dhéanamh ar a céimeanna agus a príomhdhátaí.",
  "aac.working": "Ag obair…",
  "aac.generateDraft": "Cruthaigh dréacht-mholtaí",
  "aac.step1Help": "Uaslódáil an tsonraíocht nó an choimre do d’ábhar. Cabhróimid leat na dátaí tábhachtacha a aimsiú agus plean is féidir leat a choigeartú a thógáil.",
  "aac.addCalendarsOptional": "Cuir féilirí scoile leis — roghnach",
  "aac.calendarHelp": "Cabhraíonn féilirí le pleanáil timpeall ar dhátaí teagaisc agus ar shosanna. Is féidir leat na sonraí sin a chur isteach tú féin.",
  "aac.continueToReview": "Lean ar aghaidh → Déan athbhreithniú ar an méid a fuaireamar",
  "aac.removeSourceHelp": "ní thacóidh sé le moltaí nua a thuilleadh. Má úsáideann plean ceadaithe é, ní bhaintear ach an fhoinse ghníomhach agus coinnítear an chóip stairiúil.",
  "aac.removing": "Á bhaint…",
  "aac.extraction.label": "Eastóscadh",
  "aac.extraction.pending": "Eastóscadh ar feitheamh",
  "aac.extraction.extracted": "Eastóscaithe",
  "aac.extraction.unreadable": "Níorbh fhéidir é a léamh",
  "aac.extraction.replaced": "Athsholáthraithe",
  "aac.extraction.unknown": "Níl an stádas ar fáil",
  "aac.trackerCreatedAddBrief": "Cruthaíodh an rianaire AAC. Cuir do choimre nua leis nuair atá tú réidh.",
  "aac.trackerCreatedManual": "Cruthaíodh an rianaire AAC. Cuir coimre leis nó socraigh de láimh é.",
  "aac.trackerCreateFallback": "Níorbh fhéidir an rianaire AAC a chruthú.",
  "aac.newBriefReady": "Tá do choimre nua réidh le cur leis.",
  "aac.newDraftFallback": "Coinníodh do rianaire reatha mar níorbh fhéidir rianaire nua a thosú.",
  "aac.sourceUploadedStatus": "Uaslódáladh an fhoinse. Taispeántar a stádas thíos.",
  "aac.startNewBriefPrompt": "Tosaigh arís le coimre nua?",
  "aac.startNewBriefHelp": "Coinneofar do rianaire sábháilte, ach caillfear aon athruithe nár sábháladh.",
  "aac.startNewTracker": "Tosaigh rianaire AAC nua",
  "aac.newBriefHistory": "Tosaigh arís le coimre nua agus stair do rianaire sábháilte á coinneáil.",
  "aac.weeklyMinutes": "Nóiméid AAC sa tseachtain",
  "aac.setupSteps": "Céimeanna socraithe AAC",
  "aac.sourceReviewHeading": "Seo an méid a fuaireamar",
  "aac.noReadableSpecification": "Cuir sonraíocht nó coimre inléite leis i gCéim 1, nó lean ar aghaidh le socrú láimhe.",
  "aac.reviewSourceEvidence": "Déan athbhreithniú ar fhianaise na foinse",
  "aac.multipleDeadlinesStep3": "Fuarthas níos mó ná spriocdháta amháin. Roghnaigh dáta rialaitheach chríochnú na ndaltaí i gCéim 3.",
  "aac.secCompletionHandIn": "Críochnú / cur isteach CSS",
  "aac.useConfirmDate": "Úsáid agus deimhnigh an dáta seo",
  "aac.explicitSourceStages": "Céimeanna sainráite ón gcoimre",
  "aac.reviewApplySourceStages": "Déan athbhreithniú ar chéimeanna na foinse agus cuir i bhfeidhm iad",
  "aac.continueTeachingTime": "Lean ar aghaidh go dtí an t-am teagaisc",
  "aac.deadlineCheckFallback": "Níorbh fhéidir an tsonraíocht bainte a sheiceáil le haghaidh spriocdháta.",
  "aac.deadlineRequired": "Cuir isteach spriocdháta críochnaithe / cur isteach CSS sula ndeimhníonn tú é.",
  "aac.deadlineConfirmed": "Deimhníodh an spriocdháta sa rianaire in eagar seo. Sábháil é roimh cheadú.",
  "aac.documentDeadlineReview": "Athbhreithniú ar spriocdháta na cáipéise",
  "aac.checkDocumentDeadline": "Seiceáil an cháipéis le haghaidh spriocdháta",
  "aac.deadlineReviewLoading": "Tá sonraíochtaí bainte á seiceáil. Ní athraíonn sé seo céimeanna ná ní chruthaíonn sé moltaí.",
  "aac.deadlineStatus.specificationNeeded": "Tá sonraíocht nó coimre AAC inléite de dhíth sular féidir le Elume spriocdháta oifigiúil a sheiceáil. Ní sholáthraíonn féilirí scoile an dáta seo.",
  "aac.deadlineStatus.extractionPending": "Tá do shonraíocht á baint fós. Seiceáil arís nuair a mharcáiltear í mar bhainte.",
  "aac.deadlineStatus.extractionFailed": "Níorbh fhéidir an tsonraíocht a léamh. Uaslódáil cóip inléite mar théacs nó cuir an dáta CSS isteach de láimh.",
  "aac.deadlineStatus.noClearDeadline": "Ní bhfuarthas spriocdháta soiléir críochnaithe / cur isteach daltaí sa tsonraíocht bainte. Cuir isteach an dáta de láimh agus deimhnigh é tar éis an choimre a sheiceáil.",
  "aac.deadlineStatus.conflictingCandidates": "Tá spriocdhátaí féideartha contrártha sa tsonraíocht. Déan athbhreithniú ar na sleachta foinse thíos agus roghnaigh nó cuir isteach an dáta ceart CSS.",
  "aac.deadlineSuggestionConfirm": "Moltar é ó do shonraíocht — deimhnigh le do thoil.",
  "aac.source": "foinse",
  "aac.suggestedSecDate": "Dáta molta críochnaithe / cur isteach CSS",
  "aac.suggestedFrom": "Moltar ó",
  "aac.confirmedDateRetainedPrefix": "Fanann do dháta deimhnithe",
  "aac.confirmedDateRetainedSuffix": "gan athrú mura n-úsáideann tú an moladh seo go sainráite.",
  "aac.useSuggestedDate": "Úsáid dáta molta",
  "aac.confirmDate": "Deimhnigh an dáta",
  "aac.changeDate": "Athraigh dáta",
  "aac.changeDateConfirmBeforeApproval": "Athraigh an dáta i bPríomhdhátaí, ansin deimhnigh é roimh cheadú.",
  "aac.confirmEnteredSecDate": "Deimhnigh dáta CSS curtha isteach",
  "aac.foundStages": "Fuaireamar",
  "aac.inThisBrief": "chéim sa choimre seo",
  "aac.separateSourceTask": "Tasc foinse ar leith",
  "aac.replaceStagesQuestion": "Cuir céimeanna foinse in ionad chéimeanna an rianaire?",
  "aac.replaceStagesHelp": "Ní chuireann sé seo ach na céimeanna in eagar sa rianaire in ionad céimeanna athbhreithnithe lipéadaithe ón bhfoinse. Fanann do phlean ceadaithe agus d’fhéilirí gan athrú.",
  "aac.keepCurrentTracker": "Coinnigh an rianaire reatha",
  "aac.sourceStagesReady": "Tá céimeanna na foinse réidh i do rianaire in eagar. Sábháil iad nuair atá tú réidh.",
  "aac.applyStages": "Cuir i bhfeidhm",
  "aac.missing.secDeadline": "spriocdháta críochnaithe / cur isteach CSS",
  "aac.missing.secConfirmation": "deimhniú ar an spriocdháta críochnaithe / cur isteach CSS",
  "aac.missing.plannedStart": "cathain a thosóidh obair AAC",
  "aac.missing.finalDeadline": "spriocdháta deiridh an tseomra ranga",
  "aac.missing.fifthYearEnd": "dáta deiridh teagaisc na Cúigiú Bliana",
  "aac.missing.sixthYearRestart": "dáta atosaithe na Séú Bliana",
  "aac.missing.namedStage": "céim ainmnithe amháin ar a laghad",
  "aac.addBeforeSuggestions": "Cuir iad seo a leanas leis sular féidir le Elume dátaí céime a mholadh:",
  "aac.suggestedDatesSaveFallback": "Tá dátaí molta réidh i do rianaire in eagar ach níorbh fhéidir iad a shábháil. Bain triail as Sábháil rianaire.",
  "aac.trackerSavedSuggestionsAdded": "Sábháladh an rianaire agus cuireadh dátaí molta leis. Déan athbhreithniú ar na céimeanna agus na dátaí nó cuir in eagar iad am ar bith; sábháil athruithe sula n-osclaíonn tú dul chun cinn daltaí.",
  "aac.trackerSavedDatesPreserved": "Sábháladh an rianaire. Coinníodh do dhátaí céime a chuir an múinteoir isteach cheana.",
  "aac.stageDatesCalculationFallback": "Sábháladh an dréacht, ach níorbh fhéidir le Elume dátaí céime a ríomh. Déan athbhreithniú ar na rabhaidh sceidealaithe agus ar na dátaí teagaisc.",
  "aac.calendarDay": "lá féilire",
  "aac.calendarDays": "lá féilire",
  "aac.deadlineIs": "Tá an spriocdháta seo",
  "aac.afterSecDeadline": "tar éis spriocdháta CSS.",
  "aac.deadlineLeaves": "Fágann sé seo",
  "aac.daysBeforeSecDeadline": "lá roimh spriocdháta CSS. Fág 14 lá ar a laghad.",
  "aac.addBothDeadlinesForBuffer": "Cuir spriocdháta críochnaithe / cur isteach CSS agus spriocdháta deiridh an tseomra ranga leis chun an maolán riachtanach a sheiceáil.",
  "aac.addSecDeadlineForBuffer": "Cuir spriocdháta críochnaithe / cur isteach CSS leis chun an maolán riachtanach a sheiceáil.",
  "aac.addFinalDeadlineForBuffer": "Cuir spriocdháta deiridh an tseomra ranga leis chun an maolán riachtanach a sheiceáil.",
  "aac.editableChoicesHelp": "Is féidir roghanna an mhúinteora a chur in eagar agus ní bhogtar go ciúin iad riamh.",
  "aac.secCompletionHandInDeadline": "Spriocdháta críochnaithe / cur isteach CSS",
  "aac.secCompletionHandInHelp": "An spriocdháta oifigiúil deimhnithe do dhaltaí chun an AAC seo a chríochnú nó a chur isteach. Seiceáil an tsonraíocht nó an choimre sula ndeimhníonn tú é.",
  "aac.plannedStart": "Cathain a thosóidh tú?",
  "aac.plannedStartHelp": "An dáta a bheartaíonn tú obair AAC a thosú leis an rang seo.",
  "aac.studentFinishTarget": "Cathain ba chóir do dhaltaí a bheith ag iarraidh críochnú?",
  "aac.studentFinishTargetHelp": "Do ghnáthdháta críochnaithe sa seomra ranga, ag fágáil ama do dhaltaí a bhfuil gá acu teacht suas roimh spriocdháta deiridh an tseomra ranga.",
  "aac.finalClassroomDeadline": "Spriocdháta deiridh an tseomra ranga",
  "aac.finalClassroomDeadlineHelp": "An spriocdháta seomra ranga is déanaí atá agat d’obair atá fós le déanamh agus le teacht suas. Fág 14 lá féilire ar a laghad roimh spriocdháta CSS.",
  "aac.changeFinalDeadline": "Athraigh spriocdháta deiridh an tseomra ranga",
  "aac.secDeadline": "Spriocdháta CSS",
  "aac.latestPermittedClassroomDate": "An dáta seomra ranga is déanaí atá ceadaithe",
  "aac.addDatesForCatchUp": "Cuir gnáthdháta críochnaithe agus spriocdháta deiridh an tseomra ranga leis chun am le teacht suas a fheiceáil.",
  "aac.normalFinishBeforeFinalDeadline": "Caithfidh an gnáthchríoch a bheith ar an spriocdháta deiridh sa seomra ranga nó roimhe.",
  "aac.catchUpTime": "Am le teacht suas",
  "aac.finalDeadlineLeaves": "Fágann spriocdháta deiridh do sheomra ranga",
  "aac.beforeSecDeadline": "roimh spriocdháta CSS.",
  "aac.otherDates": "Dátaí eile",
  "aac.otherDatesHelp": "Tá dátaí riaracháin scoile le cur isteach ar leithligh ó spriocdháta críochnaithe CSS an dalta.",
  "aac.schoolSubmissionDate": "Dáta cur isteach riaracháin na scoile",
  "aac.schoolSubmissionDateHelp": "Dáta riaracháin scoile roghnach; ní chuireann sé in ionad spriocdháta críochnaithe CSS an dalta.",
  "aac.fifthYearTeachingEnds": "Críochnaíonn teagasc na Cúigiú Bliana",
  "aac.sixthYearRestarts": "Atosaíonn an Séú Bliain",
  "aac.sixthYearCalendar": "Féilire na Séú Bliana",
  "aac.calendarProvisional": "Sealadach — níl an féilire curtha ar fáil fós",
  "aac.teacherReviewedCalendar": "Féilire athbhreithnithe ag múinteoir",
  "aac.buffer": "maolán",
  "aac.beforeContinuing": "Sula leanann tú ar aghaidh",
  "aac.minimumBufferGuidance": "athraigh spriocdháta deiridh an tseomra ranga ionas go mbeidh sé 14 lá féilire ar a laghad roimh spriocdháta CSS.",
  "aac.teachingTimeHelp": "Is iomlán seachtainiúil iad nóiméid AAC, ní in aghaidh an cheachta. Deimhnigh na meastacháin seo; is féidir an Séú Bliain a fhágáil sealadach.",
  "aac.lessonsPerWeek": "Ceachtanna sa tseachtain",
  "aac.minutesPerLesson": "Nóiméid sa cheacht",
  "aac.beforeSuggestingStageDates": "Sular féidir le Elume dátaí céime a mholadh",
  "aac.readyForSuggestedDates": "Tá sé réidh an rianaire seo a shábháil agus dátaí molta in eagar a ríomh. Ní athrófar na dátaí céime atá agat cheana.",
  "aac.savingTracker": "Rianaire á shábháil…",
  "aac.continueSaveTracker": "Lean ar aghaidh agus sábháil an rianaire",
  "aac.saveFallback": "Níor sábháladh do dhréacht. Bain triail eile as.",
  "aac.trackerSavedUnchanged": "Sábháladh an rianaire. Fanann an plean ceadaithe agus an féilire gan athrú.",
  "aac.saveBeforeApplyingSuggestions": "Sábháil nó athnuaigh do dhréacht in eagar sula gcuireann tú moltaí sceidealaithe i bhfeidhm.",
  "aac.suggestedDatesReadySave": "Tá dátaí molta réidh i do rianaire. Sábháil an rianaire chun iad a choinneáil.",
  "aac.noSuggestedDates": "Níl aon dáta molta reatha le cur i bhfeidhm.",
  "aac.saveBeforeRecalculating": "Sábháil nó athnuaigh do dhréacht in eagar sula n-athríomhann tú dátaí molta.",
  "aac.recalculationNoDates": "Níorbh fhéidir le Elume dátaí athsholáthair a ríomh. Déan athbhreithniú ar an bhfuinneog teagaisc agus ar na rabhaidh sceidealaithe.",
  "aac.recalculationFallback": "Níorbh fhéidir le Elume dátaí molta a athríomh.",
  "aac.chooseReplacementDate": "Roghnaigh dáta céime amháin ar a laghad le hathsholáthar, nó coinnigh an dréacht reatha gan athrú.",
  "aac.replacementDatesSelected": "Tá dátaí athsholáthair roghnaithe i do rianaire. Déan athbhreithniú orthu, ansin roghnaigh Sábháil rianaire chun iad a choinneáil.",
  "aac.chooseDateBeforeMove": "Cuir dáta molta i bhfeidhm nó roghnaigh dáta sula mbogann tú an chéim seo seachtain.",
  "aac.reviewReplacementDates": "Déan athbhreithniú ar dhátaí athsholáthair",
  "aac.replacementDatesHelp": "Úsáideann na dátaí seo d’fhuinneog teagaisc agus do chumas sábháilte. Ní thaifeadann dréachtaí níos sine cé acu ar chruthaigh an córas nó an múinteoir dáta, mar sin ní roghnaítear aon cheann de réir réamhshocraithe.",
  "aac.untitledStage": "Céim gan teideal",
  "aac.noSavedDate": "Gan dáta sábháilte",
  "aac.keepCurrentDates": "Coinnigh na dátaí reatha",
  "aac.applySelectedDates": "Cuir na dátaí roghnaithe i bhfeidhm",
  "aac.suggestedPlanHeading": "Seo do phlean molta",
  "aac.reorderHelp": "Bain úsáid as cnaipí chomh maith le rialuithe tadhaill/méarchláir chun athordú.",
  "aac.newStage": "Céim nua",
  "aac.addStage": "Cuir céim leis",
  "aac.stage": "Céim",
  "aac.moveStage": "Bog céim",
  "aac.up": "suas",
  "aac.down": "síos",
  "aac.moveUp": "Bog suas",
  "aac.moveDown": "Bog síos",
  "aac.removeStage": "Bain céim",
  "aac.stageTitle": "Teideal céime",
  "aac.name": "ainm",
  "aac.completionDate": "Dáta críochnaithe",
  "aac.date": "dáta",
  "aac.oneWeekEarlier": "1 seachtain níos luaithe",
  "aac.oneWeekLater": "1 seachtain níos déanaí",
  "aac.minusOneWeek": "−1 seachtain",
  "aac.plusOneWeek": "+1 seachtain",
  "aac.adjustTimeAllocation": "Coigeartaigh leithdháileadh ama",
  "aac.minutes": "nóiméad",
  "aac.suggested": "molta",
  "aac.totalTeachingTimeForStage": "Am teagaisc iomlán don chéim seo (nóiméid)",
  "aac.suggestedEstimateHelp": "Moltar é ó do phlean. Is féidir leat an meastachán seo a mhéadú nó a laghdú.",
  "aac.teacherEnteredEstimate": "Meastachán a chuir an múinteoir isteach.",
  "aac.addTeachingInputsForAllocation": "Cuir ionchuir teagaisc leis sular féidir le Elume am a leithdháileadh don chéim seo.",
  "aac.checkpoint": "Pointe seiceála",
  "aac.removeCheckpoint": "Bain pointe seiceála",
  "aac.newCheckpoint": "Pointe seiceála nua",
  "aac.addCheckpoint": "Cuir pointe seiceála leis",
  "aac.planSummary": "Achoimre an phlean",
  "aac.totalAllocatedTeachingTime": "Am teagaisc leithdháilte iomlán",
  "aac.availableTeachingTime": "Am teagaisc atá ar fáil",
  "aac.preferredStudentFinish": "Críoch roghnaithe daltaí",
  "aac.notSet": "Gan socrú",
  "aac.notConfirmed": "Gan deimhniú",
  "aac.finalClassroomToSecBuffer": "Maolán idir an spriocdháta ranga deiridh agus CSS",
  "aac.provisionalAllocationHelp": "Úsáideann leithdháiltí céime molta an t-am teagaisc atá ar fáil duit. Coigeartaigh iad chun freastal ar do rang.",
  "aac.staleSuggestionsHelp": "Tá moltaí sceidealaithe sean tar éis do chuid athruithe. Sábháil an rianaire chun iad a athnuachan.",
  "aac.schedulingNeedsAttention": "Teastaíonn aird ar an sceidealú",
  "aac.calendarProvisionalHeading": "Tá eolas an fhéilire sealadach",
  "aac.editDates": "Cuir dátaí in eagar",
  "aac.editTeachingTime": "Cuir am teagaisc in eagar",
  "aac.recalculating": "Á athríomh…",
  "aac.saving": "Á shábháil…",
  "aac.saveTracker": "Sábháil rianaire",
  "aac.replacementDatesPrivateHelp": "Déan athbhreithniú ar dhátaí athsholáthair molta sula gcuireann tú i bhfeidhm iad. Coinníonn sábháil an rianaire seo príobháideach do mhúinteoirí; ní fhoilsíonn an píolótach seo imeachtaí féilire.",
  "aac.recordedPlanningAssumptions": "Toimhdí pleanála taifeadta",
  "aac.warning.confirmControllingDeadline": "Deimhnigh spriocdháta rialaitheach críochnaithe roimh cheadú.",
  "aac.warning.confirmSecDeadline": "Deimhnigh an spriocdháta críochnaithe / cur isteach CSS roimh cheadú.",
  "aac.warning.setCompletionTarget": "Socraigh sprioc chríochnaithe inmheánach 14 lá féilire ar a laghad roimh an spriocdháta.",
  "aac.warning.bufferNotPreserved": "Ní chaomhnaíonn an sprioc chríochnaithe inmheánach an maolán 14 lá féilire.",
  "aac.warning.useIsoDates": "Bain úsáid as dátaí ISO don spriocdháta rialaitheach agus don sprioc chríochnaithe.",
  "aac.warning.startBeforeFinish": "Caithfidh do thús beartaithe a bheith roimh an ngnáthdháta críochnaithe.",
  "aac.warning.normalFinishBeforeFinal": "Caithfidh do ghnáthdháta críochnaithe a bheith ar spriocdháta deiridh an tseomra ranga nó roimhe.",
  "aac.warning.finalBeforeSec": "Caithfidh spriocdháta deiridh an tseomra ranga a bheith roimh an spriocdháta críochnaithe / cur isteach CSS.",
  "aac.warning.validCalendarDates": "Bain úsáid as dátaí féilire bailí do phríomhdhátaí AAC.",
  "aac.warning.sixthYearProvisional": "Tá infhaighteacht na Séú Bliana sealadach go dtí go ndeimhnítear féilire na scoile.",
  "aac.warning.validWeeklyAllocation": "Socraigh leithdháileadh seachtainiúil AAC bailí roimh cheadú.",
  "aac.warning.confirmYearDates": "Deimhnigh dáta deiridh na Cúigiú Bliana agus dáta atosaithe na Séú Bliana roimh cheadú.",
  "aac.warning.sixthRestartAfterFifthEnd": "Caithfidh atosú na Séú Bliana a bheith tar éis deireadh teagaisc na Cúigiú Bliana.",
  "aac.warning.invalidClosureDate": "Tá dáta neamhbhailí ag dúnadh athbhreithnithe.",
  "aac.warning.setStartBeforeSuggestions": "Socraigh cathain a thosóidh obair AAC sular féidir le Elume dátaí céime a mholadh.",
  "aac.warning.moveStartBeforeFinish": "Bog dáta tosaithe AAC ar an ngnáthdháta críochnaithe nó roimhe.",
  "aac.warning.provisionalStageTimes": "Is meastacháin phleanála in eagar iad roinnt amaí céime toisc nár soláthraíodh fad.",
  "aac.warning.noCapacityForAllocation": "Ní féidir le Elume am céime a leithdháileadh go dtí go mbeidh cumas teagaisc ar fáil san fhuinneog bheartaithe.",
  "aac.warning.noTeachingTime": "Níl aon am teagaisc ar fáil san fhuinneog bheartaithe.",
  "aac.warning.stageDatesInOrder": "Caithfidh dátaí céime fanacht in ord.",
  "aac.warning.exceedsCapacity": "Sáraíonn obair AAC mheasta an cumas teagaisc atá ar fáil; coigeartaigh í go sainráite seachas an plean a chomhbhrú.",
  "aac.warning.stageNeedsIdAndName": "teastaíonn aitheantas cobhsaí agus ainm uaidh",
  "aac.warning.stageNeedsPositiveEstimate": "teastaíonn meastachán dearfach uaidh",
  "aac.warning.stageInvalidDate": "tá dáta críochnaithe neamhbhailí aige",
  "aac.warning.stageOutsideTeachingTime": "titeann sé lasmuigh den am teagaisc AAC atá ar fáil",
  "aac.warning.weeklyMinutesRange": "caithfidh am AAC a bheith idir 5 agus 600 nóiméad sa tseachtain",
  "aac.warning.cannotExceedTeachingTime": "ní féidir le ham AAC dul thar an am teagaisc ranga a chuir tú isteach",
  "aac.warning.checkTeachingTimeNumbers": "Seiceáil na huimhreacha ama teagaisc do",
  "classAdmin.addAssessment": "Cuir Measúnú Leis",
  "classAdmin.active": "Gníomhach",
  "classAdmin.inactive": "Neamhghníomhach",
  "classAdmin.studentRoster": "Liosta Daltaí",
  "classAdmin.noStudents": "Níl aon dalta ann fós.",
  "classAdmin.testsResults": "Tástálacha & Torthaí",
  "classAdmin.noTests": "Níl aon tástáil ann fós.",
  "classAdmin.classAverage": "Meán an Ranga",
  "classAdmin.openReport": "Oscail tuairisc",
  "classAdmin.createTest": "Cruthaigh Tástáil",
  "classAdmin.editTest": "Cuir Tástáil in Eagar",
  "classAdmin.result": "Toradh (%)",
  "seatingPlan.title": "Plean Suíocháin",
  "seatingPlan.loading": "Plean suíocháin á lódáil...",
  "seatingPlan.backToClass": "Ar ais chuig an rang",
  "seatingPlan.exportImage": "Easpórtáil Íomhá",
  "seatingPlan.print": "Priontáil Plean Suíocháin",
  "seatingPlan.regenerate": "Gin arís",
  "seatingPlan.setupRoster": "Socrú & Liosta",
  "seatingPlan.rows": "Sraitheanna táblaí",
  "seatingPlan.tablesPerRow": "Táblaí in aghaidh na sraithe",
  "seatingPlan.create": "Cruthaigh plean suíocháin",
  "seatingPlan.studentsInPlan": "Daltaí sa phlean",
  "tools.randomName": "Gineadóir Ainm Randamach",
  "tools.timer": "Coimeádóir ama",
  "tools.timeUp": "Tá an t-am istigh!",
  "tools.minutes": "Nóiméid",
  "tools.seconds": "Soicind",
  "tools.start": "Tosaigh",
  "tools.pause": "Cuir ar sos",
  "tools.reset": "Athshocraigh",
  "tools.done": "Críochnaithe",
  "tools.pickName": "Roghnaigh ainm",
  "tools.teamGenerator": "Gineadóir Foirne",
  "tools.groupSize": "Méid grúpa",
  "tools.generateTeams": "Gin foirne",
  "quizzes.backToClass": "Ar ais chuig an rang",
  "quizzes.heroHelp": "Cruthaigh tr\u00e1th na gceist go tapa \u00f3 PDF ranga, cuir in eagar \u00e9 m\u00e1 theasta\u00edonn, agus cuir ar si\u00fal \u00e9 le Tr\u00e1th na gCeist Beo.",
  "quizzes.step": "C\u00e9im",
  "quizzes.pickPdf": "Roghnaigh PDF ranga",
  "quizzes.pickPdfHelp": "\u00das\u00e1id PDF at\u00e1 i N\u00f3ta\u00ed n\u00f3 i bP\u00e1ip\u00e9ir Scr\u00fadaithe cheana.",
  "quizzes.generateWithAi": "Gin le IS",
  "quizzes.generateHelp": "Roghnaigh l\u00edon na gceisteanna, ansin lig do Elume tr\u00e1th na gceist a chruth\u00fa.",
  "quizzes.deliverLive": "Cuir ar si\u00fal go beo \u00e9",
  "quizzes.deliverHelp": "Nuair at\u00e1 s\u00e9 r\u00e9idh, re\u00e1cht\u00e1il \u00e9 ar leathanach Tr\u00e1th na gCeist Beo an ranga.",
  "quizzes.thisClass": "An rang seo",
  "quizzes.fastestStart": "An bealach is tapa le tos\u00fa",
  "quizzes.fastestStartHelp": "Uasl\u00f3d\u00e1il n\u00f3 ath\u00fas\u00e1id PDF ranga, gin tr\u00e1th na gceist anseo, ansin cuir an seisi\u00fan ar bun le Tr\u00e1th na gCeist Beo.",
  "quizzes.teacherNote": "N\u00f3ta don mh\u00fainteoir",
  "quizzes.aiPdfNote": "\u00das\u00e1ideann gini\u00fa IS PDFanna at\u00e1 st\u00f3r\u00e1ilte don rang seo cheana.",
  "quizzes.aiPdfHelp": "Coinn\u00edonn s\u00e9 sin cruth\u00fa tr\u00e1th na gceist slachtmhar agus intuartha.",
  "quizzes.openNotes": "Oscail N\u00f3ta\u00ed",
  "quizzes.openExamPapers": "Oscail P\u00e1ip\u00e9ir Scr\u00fadaithe",
  "quizzes.readyToCreate": "R\u00e9idh le cruth\u00fa",
  "quizzes.emptyHelp": "Tosaigh le Gin \u00f3 PDF don bhealach is tapa. Cruth\u00f3idh Elume tr\u00e1th na gceist is f\u00e9idir leat a fheabhs\u00fa roimh \u00fas\u00e1id.",
  "quizzes.goodSources": "Foins\u00ed maithe",
  "quizzes.goodSourcesList": "N\u00f3ta\u00ed, bileoga athbhreithnithe, sampla\u00ed oibrithe, p\u00e1ip\u00e9ir scr\u00fadaithe",
  "quizzes.goodSourcesHelp": "D\u00e1 soil\u00e9ire an PDF, is ea is fearr an gini\u00fa ceisteanna.",
  "quizzes.afterGeneration": "Tar \u00e9is gini\u00fana",
  "quizzes.afterGenerationTitle": "Cuir in eagar m\u00e1 theasta\u00edonn, ansin bog chuig Tr\u00e1th na gCeist Beo",
  "quizzes.afterGenerationHelp": "Is chun an tr\u00e1th na gceist a th\u00f3g\u00e1il at\u00e1 an leathanach seo.",
  "quizzes.builtForClass": "Tr\u00e1th na gCeist don rang seo",
  "quizzes.categoryLabel": "Catag\u00f3ir",
  "quizzes.noDescription": "N\u00edl cur s\u00edos ann f\u00f3s.",
  "quizzes.created": "Cruthaithe",
  "quizzes.nextStep": "An ch\u00e9ad ch\u00e9im eile",
  "quizzes.deliverViaLiveQuiz": "Cuir ar si\u00fal le Tr\u00e1th na gCeist Beo",
  "quizzes.deliverLiveAction": "Cuir ar si\u00fal go beo",
  "quizzes.editQuestions": "Cuir ceisteanna in eagar",
  "quizzes.mainCollection": "Pr\u00edomhbhaili\u00fach\u00e1n Tr\u00e1th na gCeist",
  "quizzes.saveToMainCollection": "S\u00e1bh\u00e1il sa Phr\u00edomhbhaili\u00fach\u00e1n",
  "quizzes.inMainCollection": "Sa Phr\u00edomhbhaili\u00fach\u00e1n",
  "quizzes.editor": "Eagarth\u00f3ir tr\u00e1th na gceist",
  "quizzes.buildFromPdf": "Cruthaigh tr\u00e1th na gceist \u00f3 PDF ranga at\u00e1 ann cheana",
  "quizzes.generating": "Ag gini\u00fa...",
  "calendar.back": "Ar ais",
  "calendar.routeHelp": "Coinnigh imeachta\u00ed ranga slachtmhar, infheicthe agus furasta a nuashonr\u00fa.",
  "calendar.allHelp": "F\u00e9ach ar gach rud go tapa thar ranganna agus imeachta\u00ed scoile.",
  "calendar.globalHelp": "Imeachta\u00ed scoile amh\u00e1in \u00e1 dtaispe\u00e1int.",
  "calendar.eventsThisMonth": "imeacht an mh\u00ed seo",
  "calendar.aiPlanning": "Plean\u00e1il le c\u00fanamh IS",
  "calendar.view": "Amharc",
  "calendar.quickSwitch": "Athr\u00fa tapa",
  "calendar.class": "Rang",
  "calendar.planner": "Plean\u00e1la\u00ed",
  "calendar.focusedView": "Amharc ranga d\u00edrithe",
  "calendar.focusedViewHelp": "Baineann na himeachta\u00ed anseo go d\u00edreach leis an rang seo.",
  "calendar.aiAssistant": "C\u00fantoir F\u00e9ilire IS",
  "calendar.aiAssistantHelp": "D\u00e9an imeacht ceart as smaoineamh tapa",
  "calendar.generate": "Gin",
  "calendar.aiSuggestionReady": "Moladh IS r\u00e9idh",
  "calendar.aiSuggestionHelp": "D'oscail muid \u00e9 sa mhodal imeachta ionas gur f\u00e9idir leat \u00e9 a athbhreithni\u00fa.",
  "calendar.reopenReview": "Athoscail athbhreithni\u00fa",
  "calendar.tapDay": "Tap\u00e1il l\u00e1 chun rud a chur leis go tapa.",
  "calendar.more": "eile",
  "calendar.reviewAiEvent": "Athbhreithnigh imeacht IS",
  "calendar.general": "Ginear\u00e1lta",
  "calendar.test": "T\u00e1st\u00e1il",
  "calendar.homework": "Obair bhaile",
  "calendar.trip": "Turas",
  "calendar.visibility": "Infheictheacht",
  "calendar.global": "Domhanda",
  "liveQuiz.savedQuizzes": "Tr\u00e1th na gCeist S\u00e1bh\u00e1ilte",
  "liveQuiz.teacherControls": "Rialuithe m\u00fainteora",
  "liveQuiz.runSession": "Re\u00e1cht\u00e1il an tseisi\u00fain go beo",
  "liveQuiz.answered": "Freagartha",
  "liveQuiz.timeLeft": "Am f\u00e1gtha",
  "liveQuiz.joinLink": "Nasc chun p\u00e1irt a ghlacadh",
  "liveQuiz.sessionCode": "C\u00f3d seisi\u00fain",
  "liveQuiz.copyCode": "C\u00f3ipe\u00e1il c\u00f3d",
  "liveQuiz.copyLink": "C\u00f3ipe\u00e1il nasc",
  "liveQuiz.resultsHistory": "Tortha\u00ed agus Stair",
  "liveQuiz.reportsLocal": "S\u00e1bh\u00e1iltear tuairisc\u00ed go h\u00e1iti\u00fail ar an ngl\u00e9as seo.",
  "liveQuiz.topThree": "An tri\u00far is fearr",
  "liveQuiz.leaderboard": "Cl\u00e1r ceannair\u00ed",
  "liveQuiz.noReport": "N\u00edl aon tuairisc roghnaithe",
  "liveQuiz.noReportHelp": "Cuir deireadh le seisi\u00fan chun tuairisc a ghini\u00fa go huathoibr\u00edoch.",
  "liveQuiz.history": "Stair",
  "liveQuiz.anonymous": "Gan ainm",
  "liveQuiz.named": "Ainmnithe",
  "liveQuiz.average": "Me\u00e1n",
  "liveQuiz.noHistory": "N\u00edl aon stair ann f\u00f3s.",
  "liveQuiz.classInsights": "L\u00e9argais ranga",
  "liveQuiz.insightsHelp": "Iarrachta\u00ed tr\u00e1th na gceist beo le d\u00e9ana\u00ed don rang seo, gr\u00fap\u00e1ilte de r\u00e9ir dalta.",
  "liveQuiz.all": "Uile",
  "liveQuiz.counted": "Comh\u00e1irithe",
  "liveQuiz.excluded": "Eisiata",
  "liveQuiz.incomplete": "Neamhioml\u00e1n",
  "liveQuiz.attempts": "Iarrachta\u00ed",
  "liveQuiz.loadingInsights": "L\u00e9argais ranga \u00e1 l\u00f3d\u00e1il...",
  "class.timetable": "Tr\u00e1thchl\u00e1r",
  "class.timetableLoading": "Tr\u00e1thchl\u00e1r \u00e1 l\u00f3d\u00e1il...",
  "class.noTimetable": "N\u00edl tr\u00e1thchl\u00e1r s\u00e1bh\u00e1ilte ann f\u00f3s. Socraigh in Riarach\u00e1n \u00e9 ar dt\u00fas.",
  "class.studentHubHelp": "\u00das\u00e1ideann dalta\u00ed \u00e9 seo leis an PIN ranga in Mol na nDalta\u00ed.",
  "class.noAnnouncements": "N\u00edl aon fh\u00f3gra don rang seo f\u00f3s.",
  "class.loadingAnnouncements": "F\u00f3gra\u00ed \u00e1 l\u00f3d\u00e1il...",
  "class.postBold": "Trom",
  "class.postItalic": "Iod\u00e1lach",
  "class.postColour": "Dath t\u00e9acs",
  "class.postDefaultColour": "Dubh (r\u00e9amhshocr\u00fa)",
  "class.postEmoji": "Cuir emoji isteach",
  "class.postEmojiCheck": "Marc seiceála",
  "class.postEmojiStar": "Réalta",
  "class.postEmojiQuestion": "Comhartha ceiste",
  "class.postEmojiThumbsUp": "Ordóg in airde",
  "class.postEmojiClap": "Bualadh bos",
  "class.postEmojiCelebrate": "Ceiliúradh",
  "class.postEmojiIdea": "Smaoineamh",
  "class.postEmojiPin": "Biorán",
  "class.postEmojiSmile": "Aghaidh gháireach",
  "class.postEmojiHeart": "Croí",
  "class.postColourRed": "Dearg",
  "class.postColourBlue": "Gorm",
  "class.postColourGreen": "Glas",
  "class.postColourPurple": "Corcra",
  "class.postPlaceholder": "Scr\u00edobh f\u00f3gra...",
  "class.noUpcomingEvents": "N\u00edl aon imeacht ag teacht suas.",
  "tools.startTimer": "Tosaigh amad\u00f3ir",
  "tools.randomNameHelp": "Roghna\u00edonn s\u00e9 \u00f3 dhalta\u00ed ghn\u00edomhacha i Riarach\u00e1n Ranga.",
  "tools.selectedStudent": "Dalta roghnaithe",
  "tools.loadingStudents": "Dalta\u00ed \u00e1 l\u00f3d\u00e1il...",
  "tools.noActiveStudents": "N\u00edl aon dalta gn\u00edomhach ann. Cuir dalta\u00ed leis i Riarach\u00e1n Ranga.",
  "tools.picking": "\u00c1 roghn\u00fa...",
  "tools.teamGeneratorHelp": "Crutha\u00edonn s\u00e9 gr\u00fapa\u00ed randamacha tapa \u00f3 dhalta\u00ed ghn\u00edomhacha.",
  "tools.groupsMayVary": "D'fh\u00e9adfadh na gr\u00fapa\u00ed deiridh a bheith beag\u00e1n m\u00edchothrom nuair nach roinneann na huimhreacha go cothrom.",
  "dashboard.manageClassHelp": "Cuir an rang seo sa chartlann nuair nach bhfuil s\u00e9 gn\u00edomhach a thuilleadh, n\u00f3 scrios go buan \u00e9.",
  "dashboard.deletePermanently": "Scrios go buan",
  // Proposed wording below is intentionally awaiting Gaeilge review.
  "login.studentHubEnterCode": "Cuir isteach cód do ranga agus do UAP chun dul isteach i do rang.",
  "collab.joinBoard": "Téigh isteach sa Chlár Comhoibrithe",
  "collab.loading": "Á lódáil…",
  "collab.sessionEnded": "Tá an seisiún thart",
  "collab.sessionEndedHelp": "Chuir do mhúinteoir deireadh leis an seisiún comhoibrithe. Tá tú ag filleadh ar Mhol na Mac Léinn…",
  "collab.returnNow": "Fill anois",
  "collab.waiting": "Ag fanacht…",
  "collab.roomNotAssigned": "Níl do sheomra mionghrúpa sannta ag do mhúinteoir fós.",
  "collab.sessionNotStarted": "Níor thosaigh do mhúinteoir an seisiún mionghrúpa fós.",
  "collab.leave": "Fág",
  "collab.room": "Seomra {{number}}",
  "collab.breakoutRoom": "Seomra mionghrúpa {{number}}",
  "collab.pen": "Peann",
  "collab.highlighter": "Aibhsitheoir",
  "collab.line": "Líne",
  "collab.eraser": "Léirscriosán",
  "collab.stickyNote": "Nóta greamaitheach",
  "collab.undo": "Cealaigh",
  "collab.moveBoard": "Bog an clár",
  "collab.moveBoardOn": "Bog an clár ar siúl",
  "collab.moveBoardGuidance": "Tarraing chun bogadh thart. Múch é chun tarraingt.",
  "collab.colour": "Dath",
  "collab.highlight": "Aibhsigh",
  "collab.stroke": "Stróc",
  "collab.roomMembers": "Baill an tseomra",
  "collab.board": "Clár Comhoibrithe",
  "collab.joinHeading": "TÉIGH ISTEACH I GCLÁR COMHOIBRITHE",
  "collab.joinGuidance": "Bain úsáid as an gcód Comhoibrithe atá ar an gclár, ansin cuir isteach d'ainm chun dul isteach.",
  "collab.code": "CÓD COMHOIBRITHE",
  "collab.yourName": "D’ainm",
  "collab.namePlaceholder": "m.sh. Aoife",
  "videos.title": "Físeáin",
  "videos.intro": "Greamaigh nasc YouTube chun mionsamhail a chur leis. Cliceáil chun imirt.",
  "videos.add": "Cuir físeán leis",
  "videos.empty": "Níl aon fhíseán ann fós. Cliceáil",
  "videos.emptySuffix": "agus greamaigh nasc YouTube.",
  "videos.addYoutube": "Cuir físeán YouTube leis",
  "videos.addGuidance": "Greamaigh nasc (youtube.com, youtu.be, shorts).",
  "videos.youtubeLink": "Nasc YouTube (m.sh. https://www.youtube.com/watch?v=...)",
  "videos.videoTitle": "Teideal (m.sh. Tonnacha Seasta)",
  "videos.category": "Catagóir (m.sh. Tonnacha, Meicnic)",
  "videos.edit": "Cuir físeán in eagar",
  "videos.editGuidance": "Nuashonraigh an teideal, an chatagóir nó an nasc.",
  "links.emptyCategory": "Níl aon acmhainn sa chatagóir seo fós. Cliceáil",
  "links.addToCategory": "Cuir acmhainn leis seo",
  "links.editCategory": "Cuir catagóir in eagar",
  "links.newCategory": "Catagóir nua",
  "links.categoryGuidance": "Cuir cur síos gairid leis ionas go mbeidh a fhios ag múinteoirí cad chuige an chuid seo.",
  "links.categoryName": "Ainm catagóire (m.sh. Insamhaltaí, Athbhreithniú, Obair bhaile)",
  "links.shortDescription": "Cur síos gairid (roghnach)",
  "links.editLink": "Cuir nasc in eagar",
  "links.addLink": "Cuir nasc leis",
  "links.linkGuidance": "Roghnaigh catagóir, greamaigh an nasc, agus cuir nóta gairid le haghaidh múinteoirí.",
  "links.shortTitle": "Teideal gairid (roghnach)",
  "links.pasteLink": "Greamaigh nasc (m.sh. phet.colorado.edu / youtube.com / desmos.com)",
  "links.linkDescription": "Cur síos gairid (cén fáth go bhfuil sé anseo / cad atá le déanamh) (roghnach)",
  "links.add": "Cuir leis",
  "whiteboard.audioLibrary": "Leabharlann fuaime",
  "whiteboard.audioLibraryHelp": "Roghnaigh fuaim ranga le seinm in éineacht le do chlár bán.",
  "whiteboard.uploadAudio": "Uaslódáil fuaim",
  "whiteboard.audioFormats": "MP3, WAV, M4A, AAC nó OGG. Fanann sé i gcatagóir Fuaime na Nótaí don rang seo.",
  "whiteboard.changeFile": "Athraigh comhad",
  "whiteboard.chooseAudio": "Roghnaigh fuaim",
  "whiteboard.saveBoard": "Sábháil an clár bán",
  "whiteboard.saveBoardHelp": "Sábhálfaidh sé seo íomhá agus cruthóidh sé postáil i bhfotha an ranga.",
  "whiteboard.name": "Ainm an chláir bháin",
  "whiteboard.filenameDateHelp": "Beidh dáta an lae inniu san ainm comhaid.",
  "whiteboard.restoreDraft": "Athchóirigh dréacht den chlár bán?",
  "whiteboard.importPdf": "Iompórtáil PDF",
  "whiteboard.importPdfHelp": "Roghnaigh PDF ranga nó scrúdpháipéar comhroinnte, ansin bain úsáid as an amharcóir PDF agus as na huirlisí gearrtha atá ann cheana.",
  "whiteboard.classPdfs": "PDFanna ranga",
  "whiteboard.examLibrary": "Leabharlann scrúdaithe",
  "whiteboard.startSnip": "Tosaigh gearradh",
  "whiteboard.snipToBoard": "Gearr chuig an gclár",
  "whiteboard.dragToSelect": "Tarraing chun roghnú… (cliceáil chun cealú)",
  "whiteboard.snipHelp": "Tarraing dronuilleog chun roghnú, ansin cliceáil",
  "whiteboard.youtubeVideo": "Físeán YouTube",
  "whiteboard.searchAudio": "Cuardaigh comhaid fuaime…",
  "whiteboard.loadingAudio": "Leabharlann fuaime á lódáil…",
  "whiteboard.noAudio": "Níl aon chomhad fuaime ann fós",
  "whiteboard.playYoutube": "Seinn físeán YouTube",
  "whiteboard.openBoard": "Oscail an clár bán",
  "whiteboard.startNew": "Tosaigh clár bán nua",
  "whiteboard.openRecent": "Oscail clár bán le déanaí",
  "whiteboard.loadingRecent": "Cláir bhána le déanaí á lódáil…",
  "whiteboard.noPdfs": "Níl aon PDF ann fós. Uaslódáil roinnt PDFanna Nótaí/Scrúduithe ar dtús.",
  "whiteboard.cycle": "Timthriall",
  "whiteboard.subject": "Ábhar",
  "whiteboard.level": "Leibhéal",
  "whiteboard.allLevels": "Gach leibhéal",
  "whiteboard.noExamPapers": "Níl aon scrúdpháipéar comhroinnte a mheaitseálann na scagairí seo fós.",
  "whiteboard.insertGrid": "Ionsáigh eangach",
  "whiteboard.insertXy": "Ionsáigh plána XY",
  "whiteboard.fullScreen": "Lánscáileán",
  "whiteboard.halfScreen": "Leathscáileán",
  "whiteboard.leaveBoard": "Fág an clár bán?",
  "whiteboard.pdfLoadFailure": "Níorbh fhéidir linn an PDF seo a luchtú anois. Bain triail eile as.",
  "planner.title": "Pleanálaí Múinteora",
  "planner.intro": "Dialann sheachtainiúil agus tascanna do do sheachtain teagaisc. Pleanáil de réir sliotán ama ionas go bhfeicfidh tú gach rang go tapa.",
  "planner.changeSettings": "Athraigh socruithe",
  "planner.tasks": "Tascanna",
  "planner.deleteTaskConfirm": "Scrios an tasc seo? Ní féidir é seo a chealú.",
  "planner.settings": "Socruithe an phleanálaí",
  "planner.visibleSlots": "Athraigh na sliotáin atá le feiceáil",
  "planner.slotsPerDay": "Sliotáin sa lá",
  "planner.saveSettings": "Sábháil socruithe",
  "planner.editNote": "Cuir nóta in eagar",
  "planner.shortTitle": "Teideal gairid",
  "planner.fullNote": "Nóta iomlán",
  "planner.relatesTo": "Baineann le",
  "planner.general": "Ginearálta",
  "planner.personal": "Pearsanta",
  "planner.classClub": "Rang / Club",
  "planner.active": "Gníomhach",
  "planner.archived": "Cartlannaithe",
  "planner.expand": "Leathnaigh",
  "planner.collapse": "Laghdaigh",
  "planner.noTasks": "Níl aon tasc ann fós.",
  "planner.archive": "Cuir sa chartlann",
  "planner.noArchivedTasks": "Níl aon tasc cartlainne ann.",
  "planner.revive": "Athghníomhaigh",
  "planner.openCalendar": "Oscail féilire",
  "planner.noEvents": "Níl aon imeacht ann.",
  "planner.openCalendarFor": "Oscail féilire do {{date}}",
  "planner.noEventsFor": "Níl aon imeacht do {{date}}",
  "planner.noteTitlePlaceholder": "m.sh. Priontáil bileoga oibre / Ullmhaigh tráth na gceist / Glao abhaile…",
  "planner.noteBodyPlaceholder": "Scríobh na sonraí iomlána anseo…",
  "planner.due": "Le déanamh faoi: {{date}}",
  "planner.noDueDate": "Gan dáta dlite",
  "planner.archivedOn": "• Cartlannaithe: {{date}}",
  "classAdmin.notesOptional": "Nótaí (roghnach)",
  "aac.savedPlanComparison": "Comparáid idir pleananna sábháilte",
  "tests.newCategory": "Catag\u00f3ir nua scr\u00fadaithe",
  "tests.categoryTitle": "Teideal na catag\u00f3ire",
  "tests.descriptionOptional": "Cur s\u00edos (roghnach)",
  "tests.create": "Cruthaigh",
  "tests.uploadTest": "Uasl\u00f3d\u00e1il scr\u00fad\u00fa",
  "tests.existingCategory": "Catag\u00f3ir at\u00e1 ann cheana",
  "tests.createNewCategory": "Cruthaigh catag\u00f3ir nua",
  "tests.testTitle": "Teideal an scr\u00fad\u00faithe",
  "tests.category": "Catag\u00f3ir",
  "tests.noCategory": "Gan chatag\u00f3ir",
  "tests.newCategoryTitle": "Teideal catag\u00f3ire nua",
  "tests.newCategoryDescription": "Cur s\u00edos catag\u00f3ire nua (roghnach)",
  "tests.file": "Comhad",
  "tests.fileHelp": "Is fearr PDF. Glactar le Word/PPT freisin.",
  "studentHub.studentHub": "Mol na Mac Léinn",
  "studentHub.eyebrow": "MOL NA MAC LÉINN",
  "studentHub.welcome": "Fáilte go Mol na Mac Léinn",
  "studentHub.welcomeName": "Fáilte, {{name}}",
  "studentHub.headingHelp": "Tá do ranganna, gníomhaíochtaí beo agus scrúdpháipéir in aon áit amháin.",
  "studentHub.savedClasses": "Do ranganna",
  "studentHub.savedClassesHelp": "Sábháiltear iad ar an ngléas seo chun rochtain thapa a fháil.",
  "studentHub.addFirstClass": "Cuir do chéad rang leis",
  "studentHub.addFirstClassHelp": "Cuir isteach an cód ranga agus an PIN ó do mhúinteoir.",
  "studentHub.joinAnotherClass": "Téigh isteach i rang eile",
  "studentHub.classFallback": "Rang",
  "studentHub.classCode": "Cód ranga",
  "studentHub.removeDevice": "Bain den ghléas seo",
  "studentHub.viewAll": "Féach ar gach rang",
  "studentHub.showLess": "Taispeáin níos lú",
  "studentHub.liveActivities": "Gníomhaíochtaí beo",
  "studentHub.live": "BEO",
  "studentHub.liveQuiz": "Tráth na gCeist Beo",
  "studentHub.quizHelp": "Téigh isteach i dtráth na gceist beo do mhúinteora le cód tráth na gceist.",
  "studentHub.enterQuizCode": "Cuir isteach cód tráth na gceist",
  "studentHub.collabBoard": "Clár Comhoibrithe",
  "studentHub.collabHelp": "Tarraing agus oibrigh le chéile le do rang.",
  "studentHub.enterBoardCode": "Cuir isteach cód cláir",
  "studentHub.examPapers": "Scrúdpháipéir",
  "studentHub.examPapersHelp": "Cleachtaigh le páipéir na Sraithe Sóisearaí agus na hArdteistiméireachta.",
  "studentHub.browsePapers": "Brabhsáil páipéir",
  "studentHub.practiceQuizzes": "Tráth na gCeist Cleachtaidh",
  "studentHub.practiceLibrary": "Leabharlann Chleachtaidh Elume",
  "studentHub.practiceLibraryHelp": "Roghnaigh ábhar agus cleachtaigh ar do luas féin.",
  "studentHub.openPracticeLibrary": "Oscail an Leabharlann Chleachtaidh",
  "studentHub.teacherQuizzes": "Tráth na gceist ó do mhúinteoir",
  "studentHub.teacherQuizzesHelp": "Tráthanna ceiste atá sannta ag do mhúinteoir chun cleachtadh a dhéanamh.",
  "studentHub.noTeacherQuizzes": "Níl aon tráth na gceist sannta ag do mhúinteoir fós.",
  "studentHub.loadingTeacherQuizzes": "Ag lódáil thráth na gceist do mhúinteora…",
  "studentHub.classEvents": "Imeachtaí ranga",
  "studentHub.noEvents": "Níl aon imeacht atá le feiceáil ag daltaí le teacht.",
  "studentHub.eventsUnavailable": "Níl imeachtaí ar fáil faoi láthair.",
  "studentHub.nickname": "Leasainm",
  "studentHub.noNickname": "Níl leasainm sábháilte",
  "studentHub.changeName": "Athraigh m’ainm",
  "studentHub.nameWelcome": "Cad ba mhaith leat go nglaofaimis ort?",
  "studentHub.nameHelp": "Úsáid d’ainm nó leasainm. Cuimhneofar air sa bhrabhsálaí seo.",
  "studentHub.nicknamePlaceholder": "Cuir isteach d’ainm nó leasainm",
  "studentHub.continue": "Lean ar aghaidh",
  "studentHub.quizCode": "Cód tráth na gceist",
  "studentHub.boardCode": "Cód cláir",
  "studentHub.classJoinHelp": "Cuir isteach an cód ranga agus an PIN a thug do mhúinteoir duit.",
  "studentHub.quizJoinHelp": "Cuir isteach an cód a thaispeáin do mhúinteoir.",
  "studentHub.collabJoinHelp": "Cuir isteach an cód cláir a thaispeántar sa rang.",
  "studentHub.classCodePlaceholder": "Cuir isteach cód ranga",
  "studentHub.quizCodePlaceholder": "Cuir isteach cód tráth na gceist",
  "studentHub.boardCodePlaceholder": "Cuir isteach cód cláir",
  "studentHub.pinPlaceholder": "Cuir isteach PIN",
  "studentHub.codeRequired": "Cuir isteach cód, le do thoil.",
  "studentHub.pinRequired": "Cuir isteach PIN an ranga, le do thoil.",
  "studentHub.enterPin": "Cuir isteach PIN an ranga chun leanúint ar aghaidh.",
  "studentHub.joinFailed": "Níorbh fhéidir linn dul isteach sa rang sin. Seiceáil an cód agus an PIN agus bain triail eile as.",
  "studentHub.join": "Téigh isteach",
  "studentHub.joining": "Ag dul isteach…",
  "studentHub.editClassName": "Cuir ainm an ranga in eagar ar an ngléas seo",
  "calendar.shareWithStudents": "Comhroinn le daltaí",
  "calendar.shareWithStudentsHelp": "Is féidir le daltaí a bhfuil nasc rochtana an ranga seo acu an t-imeacht seo a fheiceáil i Mol na Mac Léinn. Tá imeachtaí príobháideach de réir réamhshocraithe.",
  "classAdmin.editStudentName": "Cuir ainm in eagar",
  "classAdmin.editStudentNameHelp": "Nuashonraíonn sé seo ainm liosta an ranga a úsáidtear le haghaidh Léargais, torthaí agus meaitseáil tráth na gceist amach anseo.",
  "classAdmin.studentName": "Ainm an dalta",
  "classAdmin.studentNameRequired": "Cuir isteach ainm dalta.",
  "classAdmin.studentNameSaveFailed": "Níorbh fhéidir linn an t-ainm sin a shábháil anois. Bain triail eile as.",
  "classAdmin.studentNameUnavailable": "Níorbh fhéidir linn an dalta sin a aimsiú i liosta an ranga seo.",
  "classAdmin.totalMarks": "Marcanna iomlána",
  "classAdmin.totalMarksRequired": "Caithfidh líon dearfach a bheith sna marcanna iomlána.",
  "classAdmin.totalMarksChangeConfirm": "Coinníonn athrú na marcanna iomlána gach amh-mharc agus athríomhann sé na céatadáin. Lean ar aghaidh?",
  "classAdmin.updateTotalMarksTitle": "Nuashonraigh na marcanna iomlána?",
  "classAdmin.updateTotalMarksBody": "Athraigh an t-iomlán ó {{oldTotal}} go {{newTotal}}? Fanfaidh marcanna na ndaltaí mar atá agus athríomhfar a gcéatadáin.",
  "classAdmin.goBack": "Téigh ar ais",
  "classAdmin.updateTotal": "Nuashonraigh an t-iomlán",
  "classAdmin.unsavedResultsKept": "Tá d’athruithe torthaí nár sábháladh fós anseo.",
  "classAdmin.rawMarksExceedTotal": "Tá marc amháin ar a laghad nár sábháladh níos airde ná an t-iomlán nua. Ceartaigh iad sula sábhálann tú na torthaí.",
  "classAdmin.legacyPercentageOnly": "Measúnú céatadán-amháin",
  "classAdmin.legacyPercentageOnlyHelp": "Coinníonn an measúnú céatadán-amháin seo a thorthaí bunaidh. Ní féidir marcanna iomlána a chur leis tar éis torthaí a shábháil.",
  "classAdmin.markOutOf": "Marc (as {{total}})",
  "classAdmin.percentage": "Céatadán",
  "whiteboard.renameSaved": "Athainmnigh",
  "whiteboard.renameSavedHelp": "Athraíonn sé seo teideal an chláir agus lipéad na Nótaí ranga nasctha. Ní shábhálann sé an clár arís.",
  "whiteboard.renameRequired": "Cuir teideal cláir bháin isteach.",
  "whiteboard.renameTooLong": "Is féidir le suas le {{max}} carachtar a bheith i dteidil cláir bhána.",
  "whiteboard.title": "Teideal cláir bháin",
};

// Keep these late additions ASCII-escaped in source because this checkout is
// edited from Windows shells with mixed legacy code pages. The compiled UI
// still receives the correct Unicode characters rather than replacement glyphs.
Object.assign(english, {
  "whiteboard.pdfLoadFailure": "We couldn\u2019t load this PDF just now. Please try again.",
});
Object.assign(gaeilge, {
  "demo.entrySlow": "T\u00e1 do thaispe\u00e1ntas sl\u00e1n \u00e1 ullmh\u00fa f\u00f3s. Coinnigh an leathanach seo oscailte, n\u00f3 bain triail eile as mura gcr\u00edochn\u00edonn s\u00e9.",
  "demo.entryTemporary": "N\u00edorbh fh\u00e9idir linn an taispe\u00e1ntas a oscailt anois. Bain triail eile as i gceann n\u00f3im\u00e9id, n\u00f3 tosaigh do thriail saor.",
  "whiteboard.pdfLoadFailure": "N\u00edorbh fh\u00e9idir linn an PDF seo a lucht\u00fa anois. Bain triail eile as.",
});

export const translations: Record<UiLanguage, Partial<Record<string, string>>> = {
  en: english,
  ga: gaeilge,
};

export function translate(language: UiLanguage, key: string): string {
  return translations[language][key] ?? translations.en[key] ?? key;
}

export function translationSource(key: string) {
  const englishValue = english[key] ?? key;
  return {
    english: englishValue,
    gaeilge: gaeilge[key] ?? englishValue,
  };
}
